import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import '../styles/ui-theme.css';
import { useSmartNavigate } from '../hooks/useSmartNavigate';
import { useAccountSettings } from '../main/hooks/useAccountSettings';

import type { RootState } from '../store/store';
import api from '../api/axiosConfig';
import { getUserInfo } from '../api/userApi';
import type { UserProfileDto } from '../api/userApi';
import { getPaymentHistory } from '../api/paymentApi';
import type { PaymentDto, SubscriptionStatusDto } from '../types/userTypes';

import AuthModal from './modal/AuthModal';
import AccountSettingsModal from './modal/AccountSettingsModal';
import WithdrawConfirmDialog from './modal/WithdrawConfirmDialog';
import EmailChangeDialog from './modal/EmailChangeDialog';
import BillingPayDialog from './billing/BillingPayDialog';
import BillingCheckoutPanel from './billing/BillingCheckoutPanel';
import AdminUsersGrid from './admin/AdminUsersGrid';
import AdminAnalyticsPanel from './admin/AdminAnalyticsPanel';
import AdminProductValidationPanel from './admin/AdminProductValidationPanel';
import PortfolioThresholdAlertPanel from './alerts/PortfolioThresholdAlertPanel';

import {
  fetchAlertsRequested as fetchAlertsRequestedRedux,
  fetchSubsRequested as fetchSubsRequestedRedux,
} from '../store/slice/alertsSlice';

import {
  fetchSubscriptionStatusRequest,
  fetchSubscriptionStatusSuccess,
  clearMyPageError,
} from '../store/slice/myPageSlice';

import { loginRequest, signupRequest } from '../store/slice/authSlice';
import { trackEvent, trackPageView } from '../shared/analytics/ga4';

import {
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';

const PANEL = '#ffffff';
const CARD = '#f8fafc';
const BORDER = '1px solid #e2e8f0';
const TXT = '#172033';
const SUB = '#596678';
const SUB2 = '#8793a2';
const BLUE = '#0b4f71';
const PAGE_BG = '#f3f6f9';
const BORDER_STRONG = '#cbd5df';
const GOOD = '#2f7d4a';
const RED = '#c94747';
const AMBER = '#a06a0a';
const SOFT_BLUE = '#eef6fb';
const SOFT_WARN = '#fff8e8';
const SOFT_GOOD = '#edf8f1';

/*
 * 구독/결제 UI 로컬 검증 스위치.
 * 결제 구현은 유지하되 localhost/127.0.0.1에서만 노출한다.
 * 운영 도메인에서는 자동으로 false가 되어 실수로 배포해도 결제 UI가 열리지 않는다.
 */
const BILLING_UI_ENABLED =
  typeof window !== 'undefined'
  && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/*
 * Telegram 알림 UI.
 * 장전 브리핑 버튼은 계정 연결 + 오전 8시 브리핑 신청을 한 번에 처리한다.
 * Web Push와 동일한 실시간 상태 알림은 연결 후 별도 opt-in으로 켠다.
 * 종목 알림 / Sector Surge / Close Bet은 기존처럼 사용자가 별도로 활성화한 항목만 발송한다.
 */
const TELEGRAM_UI_ENABLED = true;

const PORTFOLIO_AUTO_DIAGNOSIS_INTERVAL_MS = 5 * 60_000;

function getSeoulMarketClock(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return {
    weekday: value('weekday'),
    hour: Number(value('hour')),
    minute: Number(value('minute')),
  };
}

function isKrxRegularMarketTime(now = new Date()) {
  const { weekday, hour, minute } = getSeoulMarketClock(now);
  if (weekday === 'Sat' || weekday === 'Sun') return false;

  const minutes = hour * 60 + minute;
  return minutes >= 9 * 60 && minutes <= 15 * 60 + 30;
}



function isAxios401(e: any) {
  return e?.response?.status === 401;
}

const AUTH_TOKEN_STORAGE_KEYS = [
  'accessToken',
  'access_token',
  'token',
  'jwt',
  'jwtToken',
  'authToken',
] as const;

function readStoredAccessToken(): string {
  if (typeof window === 'undefined') return '';

  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of AUTH_TOKEN_STORAGE_KEYS) {
      try {
        const raw = storage.getItem(key);
        if (!raw) continue;

        const trimmed = raw.trim();
        if (!trimmed) continue;

        // 일부 프로젝트는 토큰 문자열 대신 JSON 인증 객체를 저장한다.
        if (trimmed.startsWith('{')) {
          try {
            const parsed = JSON.parse(trimmed);
            const nested =
              parsed?.accessToken ??
              parsed?.access_token ??
              parsed?.token ??
              parsed?.jwt ??
              parsed?.jwtToken;

            if (nested) return String(nested).trim();
          } catch {
            // JSON이 아니면 원문 토큰으로 계속 처리한다.
          }
        }

        return trimmed;
      } catch {
        // 스토리지 접근 실패 시 다음 후보를 확인한다.
      }
    }
  }

  return '';
}

function normalizeBearerToken(value: unknown): string {
  const token = String(value ?? '').trim();
  if (!token) return '';
  return token.replace(/^Bearer\s+/i, '').trim();
}

function getSubscriptionDescription(
  subscription: SubscriptionStatusDto | null | undefined,
  isPro: boolean,
  isCancelReserved: boolean,
) {
  if (!isPro) return '무료 플랜입니다. 현재는 계정, 포트폴리오, 시장 모니터와 알림 기능을 이용할 수 있습니다.';
  if (!isCancelReserved) {
    return `프로 플랜이 활성화되어 있습니다.${subscription?.nextBillingDate ? ` 다음 결제일: ${subscription.nextBillingDate}` : ''}`;
  }
  return `해지 예약이 설정되어 있습니다.${subscription?.nextBillingDate ? ` ${subscription.nextBillingDate} 이후 자동 종료됩니다.` : ''}`;
}

function formatAmount(value: any) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '-';
  return `${n.toLocaleString('ko-KR')}원`;
}

function formatDateTime(value: any) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('ko-KR');
}

function useToasts() {
  const [toasts, setToasts] = useState<
    Array<{ id: number; kind: 'ok' | 'warn' | 'error'; title: string; msg?: string; timeout?: number }>
  >([]);

  const addToast = useCallback(
    (payload: Omit<{ id: number; kind: 'ok' | 'warn' | 'error'; title: string; msg?: string; timeout?: number }, 'id'>) => {
      const id = Date.now() + Math.random();
      const timeout = payload.timeout ?? 2800;
      setToasts((prev) => [...prev, { id, ...payload }]);
      window.setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), timeout);
    },
    [],
  );

  return { toasts, addToast };
}

function SectionTitle({
  title,
  sub,
  right,
  isMobile,
}: {
  title: string;
  sub?: string;
  right?: React.ReactNode;
  isMobile: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'flex-start',
        justifyContent: 'space-between',
        gap: 0.8,
        mb: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: TXT, fontSize: isMobile ? 14.5 : 15.5, fontWeight: 900, letterSpacing: '-.015em' }}>
          {title}
        </Typography>
        {sub ? (
          <Typography sx={{ color: SUB, fontSize: isMobile ? 11.2 : 11.7, mt: 0.28, lineHeight: 1.55, wordBreak: 'keep-all' }}>
            {sub}
          </Typography>
        ) : null}
      </Box>
      {right ? <Box sx={{ flex: '0 0 auto' }}>{right}</Box> : null}
    </Box>
  );
}

function MetricCard({
  title,
  value,
  desc,
  actionLabel,
  onClick,
  tone = 'default',
}: {
  title: string;
  value: string;
  desc: string;
  actionLabel: string;
  onClick: () => void;
  tone?: 'default' | 'ok' | 'warn';
}) {
  const accent = tone === 'ok' ? GOOD : tone === 'warn' ? AMBER : BLUE;
  const bg = tone === 'ok' ? SOFT_GOOD : tone === 'warn' ? SOFT_WARN : '#fff';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.05,
        borderRadius: 2.2,
        background: bg,
        border: BORDER,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Stack spacing={0.55} sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.8}>
          <Typography sx={{ color: SUB, fontSize: 10.8, fontWeight: 850 }}>{title}</Typography>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: accent, flex: '0 0 auto' }} />
        </Stack>
        <Typography sx={{ color: TXT, fontSize: 17, fontWeight: 900, letterSpacing: '-.02em' }}>{value}</Typography>
        <Typography sx={{ color: SUB, fontSize: 11.3, lineHeight: 1.5, flex: 1, wordBreak: 'keep-all' }}>{desc}</Typography>
        <Button
          variant="text"
          onClick={onClick}
          sx={{
            alignSelf: 'flex-start',
            minHeight: 30,
            p: 0,
            mt: 0.15,
            color: BLUE,
            fontSize: 11.3,
            fontWeight: 900,
            textTransform: 'none',
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {actionLabel} →
        </Button>
      </Stack>
    </Paper>
  );
}

function SocialProviderStatusCard({
  label,
  connected,
  accent,
}: {
  label: string;
  connected: boolean;
  accent: string;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        borderRadius: 2,
        background: connected ? '#fff' : CARD,
        border: connected ? `1px solid ${accent}55` : BORDER,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                bgcolor: connected ? accent : '#cbd5e1',
                boxShadow: connected ? `0 0 0 3px ${accent}18` : 'none',
                flex: '0 0 auto',
              }}
            />
            <Typography sx={{ color: TXT, fontSize: 12.5, fontWeight: 950 }}>
              {label}
            </Typography>
          </Stack>
          <Typography sx={{ color: SUB, fontSize: 11.2, mt: 0.45, lineHeight: 1.5 }}>
            {connected ? '이 계정으로 로그인할 수 있습니다.' : '아직 연결되지 않았습니다.'}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={connected ? '연결됨' : '미연결'}
          sx={{
            height: 22,
            flex: '0 0 auto',
            bgcolor: connected ? `${accent}14` : '#eef2f7',
            border: connected ? `1px solid ${accent}38` : BORDER,
            color: connected ? accent : SUB,
            fontWeight: 900,
            fontSize: 10.5,
          }}
        />
      </Box>
    </Paper>
  );
}

type SectorSurgeAlertStatus = {
  id?: number | null;
  userId?: number | null;
  channel?: string | null;
  endpointMasked?: string | null;
  active?: boolean | null;
  minSectorChangePct?: number | null;
  topStockCount?: number | null;
  cooldownMinutes?: number | null;
  lastSentSectorCode?: string | null;
  lastSentAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type SectorSurgeAlertPreview = {
  available?: boolean | null;
  reasonCode?: string | null;
  reasonMessage?: string | null;
  sectorCode?: string | null;
  sectorName?: string | null;
  sectorChangePct?: number | null;
  sectorTradingValue?: number | null;
  representativeEtfTicker?: string | null;
  representativeEtfName?: string | null;
  detectedAt?: string | null;
  topStocks?: Array<Record<string, any>> | null;
  risingSectorCode?: string | null;
  risingSectorName?: string | null;
  risingSectorChangePct?: number | null;
  risingSectorTradingValue?: number | null;
  risingRepresentativeEtfTicker?: string | null;
  risingRepresentativeEtfName?: string | null;
  topRisingStocks?: Array<Record<string, any>> | null;
  fallingSectorCode?: string | null;
  fallingSectorName?: string | null;
  fallingSectorChangePct?: number | null;
  fallingSectorTradingValue?: number | null;
  fallingRepresentativeEtfTicker?: string | null;
  fallingRepresentativeEtfName?: string | null;
  topFallingStocks?: Array<Record<string, any>> | null;
  telegramMessage?: string | null;
};

function formatPct(value: any) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function formatCompactKrw(value: any) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return '-';
  if (n >= 1000000000000) return `${(n / 1000000000000).toFixed(1)}조`;
  if (n >= 100000000) return `${Math.round(n / 100000000).toLocaleString('ko-KR')}억`;
  if (n >= 10000) return `${Math.round(n / 10000).toLocaleString('ko-KR')}만`;
  return n.toLocaleString('ko-KR');
}

function pickStockName(row: Record<string, any>) {
  return row.corpName ?? row.corp_name ?? row.name ?? row.ticker ?? '-';
}

function pickStockTicker(row: Record<string, any>) {
  return row.ticker ?? row.stockCode ?? row.stock_code ?? '';
}

function pickStockChangeRate(row: Record<string, any>) {
  return row.changeRate ?? row.change_rate ?? row.pctChange ?? row.pct_change ?? null;
}

function pickStockTradingValue(row: Record<string, any>) {
  return row.tradingValue ?? row.trading_value ?? row.cumTradingValue ?? row.cum_trading_value ?? null;
}


type CloseBetAlertStatus = {
  id?: number | null;
  userId?: number | null;
  name?: string | null;
  active?: boolean | null;
  telegramLinked?: boolean | null;
  endpointMasked?: string | null;
  strategy?: string | null;
  topStockCount?: number | null;
  lastRunAt?: string | null;
  lastSentAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type CloseBetAlertPreview = {
  available?: boolean | null;
  reasonCode?: string | null;
  reasonMessage?: string | null;
  signalDate?: string | null;
  strategy?: string | null;
  selectionLevel?: string | null;
  candidateCount?: number | null;
  candidates?: Array<Record<string, any>> | null;
  telegramMessage?: string | null;
  createdAt?: string | null;
};


type TelegramConnectionState = {
  linked: boolean;
  pending: boolean;
  morningBriefActive: boolean;
  realtimeAlertsActive: boolean;
  endpointMasked?: string | null;
  deepLink?: string | null;
  expiresAt?: string | null;
  message?: string | null;
};












function SectorSurgeAlertCard({
  status,
  preview,
  loading,
  action,
  isMobile,
  onRefresh,
  onSubscribe,
  onUnsubscribe,
  onPreview,
}: {
  status: SectorSurgeAlertStatus | null;
  preview: SectorSurgeAlertPreview | null;
  loading: boolean;
  action: string | null;
  isMobile: boolean;
  onRefresh: () => void;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
  onPreview: () => void;
}) {
  const active = Boolean(status?.active);
  const telegramLinked = Boolean(status?.endpointMasked);
  const available = Boolean(preview?.available);
  const risingStocks = Array.isArray(preview?.topRisingStocks)
    ? preview?.topRisingStocks ?? []
    : Array.isArray(preview?.topStocks)
      ? preview?.topStocks ?? []
      : [];
  const fallingStocks = Array.isArray(preview?.topFallingStocks) ? preview?.topFallingStocks ?? [] : [];

  const risingName = preview?.risingSectorName || preview?.sectorName || preview?.risingSectorCode || preview?.sectorCode || '';
  const risingPct = preview?.risingSectorChangePct ?? preview?.sectorChangePct;
  const risingTradingValue = preview?.risingSectorTradingValue ?? preview?.sectorTradingValue;

  const fallingName = preview?.fallingSectorName || preview?.fallingSectorCode || '';
  const fallingPct = preview?.fallingSectorChangePct;
  const fallingTradingValue = preview?.fallingSectorTradingValue;

  const renderStockRows = (rows: Array<Record<string, any>>, direction: 'up' | 'down') => {
    if (!rows.length) {
      return <Typography sx={{ color: SUB, fontSize: 12, lineHeight: 1.6 }}>실시간 연결 종목 데이터가 아직 없습니다.</Typography>;
    }
    return (
      <Box sx={{ display: 'grid', gap: 0.45 }}>
        {rows.slice(0, 3).map((row, idx) => {
          const pct = Number(pickStockChangeRate(row) ?? 0);
          return (
            <Box key={`${direction}-${pickStockTicker(row)}-${idx}`} sx={{ display: 'grid', gridTemplateColumns: '24px 1fr auto auto', gap: 0.6, alignItems: 'center', fontSize: 12 }}>
              <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 900 }}>{idx + 1}</Typography>
              <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 900 }} noWrap>{pickStockName(row)}{pickStockTicker(row) ? ` (${pickStockTicker(row)})` : ''}</Typography>
              <Typography sx={{ color: pct >= 0 ? RED : GOOD, fontSize: 12, fontWeight: 950 }}>{formatPct(pct)}</Typography>
              <Typography sx={{ color: SUB, fontSize: 11.5, fontWeight: 800 }}>{formatCompactKrw(pickStockTradingValue(row))}</Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
      <SectionTitle
        title="시장 급변 알림"
        sub="시장 흐름이 크게 움직일 때 상승·하락 업종과 주요 종목을 텔레그램으로 알려드립니다."
        isMobile={isMobile}
        right={<Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={onRefresh} disabled={loading} sx={{ borderRadius: 2, color: TXT, borderColor: BORDER_STRONG, fontWeight: 900, width: isMobile ? '100%' : 'auto' }}>{action === 'load' ? '확인 중…' : '새로고침'}</Button>}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '0.85fr 1.15fr' }, gap: 1 }}>
        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.2, background: active ? '#ddf6df' : CARD, border: active ? '1px solid #b9e8bf' : BORDER }}>
          <Stack spacing={0.75}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ color: TXT, fontSize: 13.5, fontWeight: 950 }}>신청 상태</Typography>
              <Chip size="small" label={active ? '활성' : '비활성'} sx={{ height: 22, bgcolor: active ? '#ddf6df' : '#eef2f7', border: active ? '1px solid #b9e8bf' : BORDER, color: TXT, fontWeight: 900 }} />
            </Box>
            <Typography sx={{ color: SUB, fontSize: 12, lineHeight: 1.65 }}>
              텔레그램: {status?.endpointMasked || '-'}
              <br />변동 기준: ±{status?.minSectorChangePct != null ? `${status.minSectorChangePct}%` : '기본값'} · 표시 종목: {status?.topStockCount ?? 3}개
              <br />재알림 간격: {status?.cooldownMinutes ?? 30}분 · 최근 발송: {status?.lastSentAt ? formatDateTime(status.lastSentAt) : '-'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={onSubscribe} disabled={loading || !telegramLinked} sx={{ borderRadius: 2, minHeight: 36, textTransform: 'none', fontWeight: 900, bgcolor: BLUE, color: '#fff', '&:hover': { bgcolor: BLUE } }}>
                {action === 'subscribe' ? '저장 중…' : active ? '설정 갱신' : '알림 신청'}
              </Button>
              {active ? (
                <Button variant="outlined" color="error" onClick={onUnsubscribe} disabled={loading} sx={{ borderRadius: 2, minHeight: 36, textTransform: 'none', fontWeight: 900 }}>
                  {action === 'unsubscribe' ? '중단 중…' : '알림 중단'}
                </Button>
              ) : null}
              <Button variant="outlined" onClick={onPreview} disabled={loading} sx={{ borderRadius: 2, minHeight: 36, textTransform: 'none', color: TXT, borderColor: BORDER_STRONG, fontWeight: 900 }}>
                {action === 'preview' ? '조회 중…' : '현재 흐름 보기'}
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.2, background: CARD, border: BORDER }}>
          <Stack spacing={0.9}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={{ color: TXT, fontSize: 13.5, fontWeight: 950 }}>현재 등락 미리보기</Typography>
              <Chip size="small" label={available ? '발송 가능' : '대기'} sx={{ height: 22, bgcolor: available ? '#ddf6df' : '#fff1cc', border: available ? '1px solid #b9e8bf' : '1px solid #f5d177', color: TXT, fontWeight: 900 }} />
            </Box>

            {preview ? (
              <>
                {!available && preview.reasonMessage ? <Typography sx={{ color: SUB, fontSize: 12, lineHeight: 1.6 }}>사유: {preview.reasonMessage}</Typography> : null}

                <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, background: '#fff' }}>
                  <Typography sx={{ color: TXT, fontSize: 12.5, fontWeight: 950, lineHeight: 1.55 }}>
                    📈 상승 섹터: {risingName || '미확인'} {risingPct != null ? formatPct(risingPct) : ''}
                  </Typography>
                  <Typography sx={{ color: SUB, fontSize: 11.7, lineHeight: 1.6 }}>
                    업종 거래대금: {formatCompactKrw(risingTradingValue)}
                  </Typography>
                  {renderStockRows(risingStocks, 'up')}
                </Paper>

                <Paper elevation={0} sx={{ p: 1, borderRadius: 2, border: BORDER, background: '#fff' }}>
                  <Typography sx={{ color: TXT, fontSize: 12.5, fontWeight: 950, lineHeight: 1.55 }}>
                    📉 하락 섹터: {fallingName || '미확인'} {fallingPct != null ? formatPct(fallingPct) : ''}
                  </Typography>
                  <Typography sx={{ color: SUB, fontSize: 11.7, lineHeight: 1.6 }}>
                    업종 거래대금: {formatCompactKrw(fallingTradingValue)}
                  </Typography>
                  {renderStockRows(fallingStocks, 'down')}
                </Paper>

                <Typography sx={{ color: SUB2, fontSize: 11.5, lineHeight: 1.6 }}>기준시각: {preview.detectedAt ? formatDateTime(preview.detectedAt) : '-'}</Typography>
              </>
            ) : (
              <Typography sx={{ color: SUB, fontSize: 12, lineHeight: 1.6 }}>미리보기를 실행하면 현재 상승/하락 섹터와 각 섹터의 TOP 3 종목을 확인할 수 있습니다.</Typography>
            )}
          </Stack>
        </Paper>
      </Box>
    </Paper>
  );
}

function MonitoringPrincipleCard({
  title,
  value,
  desc,
  chips,
}: {
  title: string;
  value: string;
  desc: string;
  chips?: string[];
}) {
  return (
    <Paper elevation={0} sx={{ p: 1.05, borderRadius: 2.2, border: BORDER, background: CARD }}>
      <Stack spacing={0.75}>
        <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 950 }}>{title}</Typography>
        <Typography sx={{ color: TXT, fontSize: 15, fontWeight: 950 }}>{value}</Typography>
        <Typography sx={{ color: SUB, fontSize: 12, lineHeight: 1.6 }}>{desc}</Typography>
        {chips?.length ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {chips.map((chip) => (
              <Chip key={chip} size="small" label={chip} sx={{ height: 22, bgcolor: '#fff', border: BORDER, color: TXT, fontWeight: 850 }} />
            ))}
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function MarketMonitoringSettingsPanel({
  status,
  preview,
  loading,
  action,
  isMobile,
  onRefresh,
  onSubscribe,
  onUnsubscribe,
  onPreview,
  onOpenRealtime,
}: {
  status: SectorSurgeAlertStatus | null;
  preview: SectorSurgeAlertPreview | null;
  loading: boolean;
  action: string | null;
  isMobile: boolean;
  onRefresh: () => void;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
  onPreview: () => void;
  onOpenRealtime: () => void;
}) {
  const active = Boolean(status?.active);
  const telegramLinked = Boolean(status?.endpointMasked);
  const risingName = preview?.risingSectorName || preview?.sectorName || preview?.risingSectorCode || preview?.sectorCode || '-';
  const fallingName = preview?.fallingSectorName || preview?.fallingSectorCode || '-';

  return (
    <Box sx={{ display: 'grid', gap: 1.1 }}>
      <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
        <SectionTitle
          title="시장 모니터"
          sub="시장을 계속 보고 있지 않아도 중요한 상승·하락 흐름을 자동으로 확인하고 알림을 받을 수 있습니다."
          isMobile={isMobile}
          right={<Button variant="outlined" onClick={onOpenRealtime} sx={{ borderRadius: 2, color: TXT, borderColor: BORDER_STRONG, fontWeight: 900, width: isMobile ? '100%' : 'auto' }}>실시간 종목 보기</Button>}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,minmax(0,1fr))' }, gap: 0.8 }}>
          <MonitoringPrincipleCard
            title="감시 범위"
            value="시장 전체 자동 감시"
            desc="종목을 하나씩 등록하지 않아도 상승·하락 업종을 자동으로 확인합니다."
            chips={['상승 흐름', '하락 흐름', '주요 종목']}
          />
          <MonitoringPrincipleCard
            title="알림 조건"
            value={active ? '텔레그램 발송 활성' : '알림 조건 저장 대기'}
            desc="변동 기준과 표시 종목 수, 재알림 간격을 한 번 설정하면 자동으로 적용합니다."
            chips={[`기준 ±${status?.minSectorChangePct ?? 1}%`, `TOP ${status?.topStockCount ?? 3}`, `${status?.cooldownMinutes ?? 30}분 쿨다운`]}
          />
          <MonitoringPrincipleCard
            title="알림 수신"
            value={telegramLinked ? '텔레그램 연결됨' : '텔레그램 연결 필요'}
            desc="연결된 텔레그램으로 시장 알림을 받을 수 있습니다."
            chips={[telegramLinked ? '수신 가능' : '연결 필요', active ? '알림 켜짐' : '알림 꺼짐']}
          />
        </Box>
      </Paper>

      <SectorSurgeAlertCard
        status={status}
        preview={preview}
        loading={loading}
        action={action}
        isMobile={isMobile}
        onRefresh={onRefresh}
        onSubscribe={onSubscribe}
        onUnsubscribe={onUnsubscribe}
        onPreview={onPreview}
      />

      <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
        <SectionTitle title="현재 감시 상태" sub="지금 감시 중인 상승·하락 흐름을 간단히 확인합니다." isMobile={isMobile} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.8 }}>
          <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
            <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 950 }}>상승 흐름</Typography>
            <Typography sx={{ color: TXT, fontSize: 13.2, fontWeight: 950, mt: 0.35 }}>{risingName}</Typography>
            <Typography sx={{ color: SUB, fontSize: 12, mt: 0.35, lineHeight: 1.6 }}>상승률이 가장 높은 섹터와 연결 종목 TOP 3를 전송합니다.</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
            <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 950 }}>하락 흐름</Typography>
            <Typography sx={{ color: TXT, fontSize: 13.2, fontWeight: 950, mt: 0.35 }}>{fallingName}</Typography>
            <Typography sx={{ color: SUB, fontSize: 12, mt: 0.35, lineHeight: 1.6 }}>하락률이 가장 낮은 섹터와 연결 종목 TOP 3를 함께 전송합니다.</Typography>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}


type PortfolioRiskMode = 'conservative' | 'balanced' | 'aggressive';

type PortfolioHoldingItem = {
  ticker: string;
  tickerName: string;
  quantity?: number | null;
  avgPrice?: number | null;
  rawLine?: string;
};

type PortfolioStockCandidate = {
  ticker: string;
  tickerName: string;
  market?: string | null;
  sector?: string | null;
  closePrice?: number | null;
};

type PortfolioLatestClose = {
  ticker: string;
  found: boolean;
  tradeDate?: string | null;
  closePrice?: number | null;
  source?: string | null;
  message?: string | null;
};

type PortfolioValidationIssue = {
  ticker?: string | null;
  field?: string | null;
  severity?: 'ERROR' | 'WARN' | string | null;
  code?: string | null;
  message?: string | null;
};

type PortfolioStrategyHoldingAction = {
  ticker?: string | null;
  tickerName?: string | null;
  sectorCode?: string | null;
  sectorName?: string | null;
  actionCode?: string | null;
  actionLabel?: string | null;
  actionReason?: string | null;
  dataStatus?: string | null;
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | string | null;
  macroSignal?: string | null;
  etfSignal?: string | null;
};

type PortfolioStrategyPreview = {
  diagnosisStatus?: string | null;
  recommendedRiskMode?: PortfolioRiskMode | null;
  effectiveRiskMode?: PortfolioRiskMode | null;
  macroRegime?: string | null;
  macroLeadScore?: number | null;
  etfSectorBias?: string | null;
  portfolioRiskScore?: number | null;
  confidence?: number | null;
  cashGuide?: string | null;
  actionSummary?: string | null;
  generatedAt?: string | null;
  source?: string | null;
  topSectors?: string[] | null;
  weakSectors?: string[] | null;
  holdingActions?: PortfolioStrategyHoldingAction[] | null;
  validationIssues?: PortfolioValidationIssue[] | null;
};

function normalizePortfolioRiskMode(value: any): PortfolioRiskMode | null {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === 'conservative' || v === 'defensive' || v === 'safe') return 'conservative';
  if (v === 'aggressive' || v === 'attack' || v === 'growth') return 'aggressive';
  if (v === 'balanced' || v === 'balance' || v === 'neutral') return 'balanced';
  return null;
}

function modeToApiValue(mode: PortfolioRiskMode) {
  if (mode === 'conservative') return 'CONSERVATIVE';
  if (mode === 'aggressive') return 'AGGRESSIVE';
  return 'BALANCED';
}

function toNumberOrNull(value: any): number | null {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function digitsOnly(value: unknown): string {
  return String(value ?? '').replace(/[^0-9]/g, '');
}


function parsePositiveIntegerInput(value: unknown): number | null {
  const digits = digitsOnly(value);
  if (!digits) return null;
  const parsed = Number(digits);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
}

function toStringArray(value: any): string[] {
  if (Array.isArray(value)) return value.map((x) => String(x ?? '').trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(/[|,/]/g).map((x) => x.trim()).filter(Boolean);
  return [];
}

function normalizePortfolioStrategyPreview(payload: any, fallbackMode: PortfolioRiskMode): PortfolioStrategyPreview {
  const src = payload?.data ?? payload?.result ?? payload ?? {};
  const recommendedRiskMode = normalizePortfolioRiskMode(
    src.recommendedRiskMode ?? src.recommended_risk_mode ?? src.recommendedMode ?? src.recommended_mode ?? src.targetRiskMode ?? src.target_risk_mode,
  ) ?? fallbackMode;
  const effectiveRiskMode = normalizePortfolioRiskMode(src.effectiveRiskMode ?? src.effective_risk_mode) ?? recommendedRiskMode;

  const holdingRows = Array.isArray(src.holdingActions)
    ? src.holdingActions
    : Array.isArray(src.holding_actions)
      ? src.holding_actions
      : Array.isArray(src.items)
        ? src.items
        : [];
  const validationRows = Array.isArray(src.validationIssues)
    ? src.validationIssues
    : Array.isArray(src.validation_issues)
      ? src.validation_issues
      : [];

  return {
    diagnosisStatus: src.diagnosisStatus ?? src.diagnosis_status ?? null,
    recommendedRiskMode,
    effectiveRiskMode,
    macroRegime: src.macroRegime ?? src.macro_regime ?? src.regime ?? src.marketRegime ?? src.market_regime ?? null,
    macroLeadScore: toNumberOrNull(src.macroLeadScore ?? src.macro_lead_score ?? src.macroScore ?? src.macro_score),
    etfSectorBias: src.etfSectorBias ?? src.etf_sector_bias ?? src.sectorBias ?? src.sector_bias ?? null,
    portfolioRiskScore: toNumberOrNull(src.portfolioRiskScore ?? src.portfolio_risk_score ?? src.riskScore ?? src.risk_score),
    confidence: toNumberOrNull(src.confidence ?? src.confidenceScore ?? src.confidence_score),
    cashGuide: src.cashGuide ?? src.cash_guide ?? src.recommendedCashGuide ?? src.recommended_cash_guide ?? null,
    actionSummary: src.actionSummary ?? src.action_summary ?? src.summary ?? src.message ?? null,
    generatedAt: src.generatedAt ?? src.generated_at ?? src.updatedAt ?? src.updated_at ?? null,
    source: src.source ?? src.sourceType ?? src.source_type ?? 'API',
    topSectors: toStringArray(src.topSectors ?? src.top_sectors ?? src.favoredSectors ?? src.favored_sectors),
    weakSectors: toStringArray(src.weakSectors ?? src.weak_sectors ?? src.cautionSectors ?? src.caution_sectors),
    holdingActions: holdingRows.slice(0, 12).map((row: any) => ({
      ticker: row?.ticker ?? row?.stockCode ?? row?.stock_code ?? null,
      tickerName: row?.tickerName ?? row?.ticker_name ?? row?.stockName ?? row?.stock_name ?? row?.corpName ?? row?.corp_name ?? null,
      sectorCode: row?.sectorCode ?? row?.sector_code ?? null,
      sectorName: row?.sectorName ?? row?.sector_name ?? row?.industryName ?? row?.industry_name ?? null,
      actionCode: row?.actionCode ?? row?.action_code ?? null,
      actionLabel: row?.actionLabel ?? row?.action_label ?? row?.action ?? row?.recommendation ?? null,
      actionReason: row?.actionReason ?? row?.action_reason ?? row?.reason ?? row?.message ?? null,
      dataStatus: row?.dataStatus ?? row?.data_status ?? null,
      riskLevel: row?.riskLevel ?? row?.risk_level ?? null,
      macroSignal: row?.macroSignal ?? row?.macro_signal ?? null,
      etfSignal: row?.etfSignal ?? row?.etf_signal ?? null,
    })),
    validationIssues: validationRows.slice(0, 80).map((row: any) => ({
      ticker: row?.ticker ?? row?.stockCode ?? row?.stock_code ?? null,
      field: row?.field ?? null,
      severity: row?.severity ?? row?.level ?? null,
      code: row?.code ?? row?.reasonCode ?? row?.reason_code ?? null,
      message: row?.message ?? row?.reason ?? null,
    })),
  };
}

function portfolioTickerKey(value: unknown): string {
  const digits = digitsOnly(value);
  if (!digits) return '';
  return digits.length >= 6 ? digits.slice(-6) : digits.padStart(6, '0');
}

function isPortfolioValidationError(issue: PortfolioValidationIssue): boolean {
  return String(issue.severity ?? '').trim().toUpperCase() === 'ERROR';
}

function isPortfolioDataInvalidAction(item: PortfolioStrategyHoldingAction): boolean {
  const actionCode = String(item.actionCode ?? '').trim().toUpperCase();
  const dataStatus = String(item.dataStatus ?? '').trim().toUpperCase();
  const label = String(item.actionLabel ?? '');
  return actionCode === 'DATA_INVALID' || dataStatus === 'INVALID' || /데이터 오류|진단 불가/.test(label);
}

function portfolioValidationLabel(issue: PortfolioValidationIssue): string {
  const code = String(issue.code ?? '').trim().toUpperCase();
  switch (code) {
    case 'QUANTITY_INVALID':
      return '보유 수량 입력 필요';
    case 'REALTIME_MISSING':
      return '실시간 시세 없음';
    case 'PRICE_INVALID':
      return '현재가 확인 필요';
    case 'CHANGE_RATE_OUTLIER':
      return '등락률 데이터 확인';
    case 'MACRO_MISSING':
      return '시장 데이터 확인';
    case 'SECTOR_SIGNAL_MISSING':
      return '섹터 데이터 확인';
    case 'HOLDINGS_EMPTY':
      return '보유 종목 확인';
    default:
      return String(issue.message ?? '').trim() || '필수 데이터 확인';
  }
}

function portfolioValidationGuide(issue: PortfolioValidationIssue): string {
  const code = String(issue.code ?? '').trim().toUpperCase();
  switch (code) {
    case 'QUANTITY_INVALID':
      return '보유 수량을 1주 이상 입력하고 저장한 뒤 다시 진단하세요.';
    case 'REALTIME_MISSING':
      return '해당 종목의 실시간 시세가 수집된 뒤 다시 진단하세요.';
    case 'PRICE_INVALID':
      return '현재가 데이터가 정상 수집됐는지 확인한 뒤 다시 진단하세요.';
    case 'CHANGE_RATE_OUTLIER':
      return '실시간 가격·등락률 필드가 정상인지 확인한 뒤 다시 진단하세요.';
    case 'MACRO_MISSING':
      return '시장 선행 데이터가 갱신된 뒤 다시 진단하세요.';
    case 'SECTOR_SIGNAL_MISSING':
      return '최신 섹터 신호가 갱신된 뒤 다시 진단하세요.';
    default:
      return '필수 값을 확인한 뒤 다시 진단하세요.';
  }
}

function portfolioActionValidationMeta(
  item: PortfolioStrategyHoldingAction,
  validationIssues: PortfolioValidationIssue[],
) {
  if (!isPortfolioDataInvalidAction(item)) return null;

  const ticker = portfolioTickerKey(item.ticker);
  const matched = validationIssues.filter((issue) => {
    if (!isPortfolioValidationError(issue)) return false;
    const issueTicker = portfolioTickerKey(issue.ticker);
    return Boolean(ticker && issueTicker && ticker === issueTicker);
  });

  if (!matched.length) {
    return {
      label: '필수 데이터 확인 필요',
      details: ['보유 수량 또는 실시간 시세'],
      guide: '보유 수량과 현재가 데이터를 확인한 뒤 다시 진단하세요.',
    };
  }

  const details = Array.from(new Set(matched.map(portfolioValidationLabel).filter(Boolean)));
  const guides = Array.from(new Set(matched.map(portfolioValidationGuide).filter(Boolean)));
  return {
    label: details.length === 1 ? details[0] : '필수 데이터 확인 필요',
    details,
    guide: guides[0] ?? '필수 값을 확인한 뒤 다시 진단하세요.',
  };
}

function portfolioActionReason(
  item: PortfolioStrategyHoldingAction,
  validationIssues: PortfolioValidationIssue[],
): string {
  const raw = String(item.actionReason ?? '').trim();
  if (!isPortfolioDataInvalidAction(item)) {
    return raw || '시장·섹터 진단 결과를 확인한 뒤 대응합니다.';
  }

  const base = raw
    .replace(/필수 데이터 오류가 있어 투자 판단을 생성하지 않습니다\.?/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const meta = portfolioActionValidationMeta(item, validationIssues);
  const parts: string[] = [];
  if (base) parts.push(base);
  if (meta?.details?.length) parts.push(`확인 필요: ${meta.details.join(' · ')}.`);
  if (meta?.guide) parts.push(`조치: ${meta.guide}`);
  return parts.join(' ') || '필수 데이터를 확인한 뒤 다시 진단하세요.';
}

function buildLocalStrategyPreview(holdings: PortfolioHoldingItem[], preferredMode: PortfolioRiskMode): PortfolioStrategyPreview {
  const cfg = getPortfolioRiskModeConfig(preferredMode);
  return {
    diagnosisStatus: 'UNAVAILABLE',
    recommendedRiskMode: preferredMode,
    effectiveRiskMode: preferredMode,
    macroRegime: '백엔드 진단 대기',
    macroLeadScore: null,
    etfSectorBias: '자동 진단 연결 대기',
    portfolioRiskScore: null,
    confidence: null,
    cashGuide: cfg.cashGuide,
    actionSummary: '자동 진단 결과를 불러오지 못했습니다. 연결이 복구되면 시장과 업종 흐름을 반영한 운용 제안을 다시 계산합니다.',
    generatedAt: new Date().toISOString(),
    source: 'LOCAL_FALLBACK',
    topSectors: [],
    weakSectors: [],
    validationIssues: [],
    holdingActions: holdings.slice(0, 8).map((h) => ({
      ticker: h.ticker,
      tickerName: h.tickerName,
      actionLabel: `${cfg.label} 기준 알림 대기`,
      actionReason: '시장과 업종 진단이 연결되면 종목별 대응을 자동으로 계산합니다.',
      riskLevel: 'MEDIUM',
    })),
  };
}

function parsePortfolioHoldings(input: string): PortfolioHoldingItem[] {
  const seen = new Set<string>();

  return String(input ?? '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const normalized = line.replace(/[,\t|]+/g, ' ').replace(/\s+/g, ' ').trim();
      const parts = normalized.split(' ');
      const ticker = String(parts[0] ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      const tickerName = String(parts[1] ?? '').trim() || ticker;
      const quantity = Number(String(parts[2] ?? '').replace(/,/g, ''));
      const avgPrice = Number(String(parts[3] ?? '').replace(/,/g, ''));

      return {
        ticker,
        tickerName,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : null,
        avgPrice: Number.isFinite(avgPrice) && avgPrice > 0 ? avgPrice : null,
        rawLine: line,
      };
    })
    .filter((row) => {
      if (!row.ticker || seen.has(row.ticker)) return false;
      seen.add(row.ticker);
      return true;
    });
}


function normalizeTickerCode(value: any) {
  const raw = String(value ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (/^\d{1,6}$/.test(raw)) return raw.padStart(6, '0');
  return raw;
}

function normalizePortfolioStockCandidate(row: any): PortfolioStockCandidate | null {
  const ticker = normalizeTickerCode(
    row?.ticker ?? row?.stockCode ?? row?.stock_code ?? row?.code ?? row?.symbol ?? row?.shortCode ?? row?.short_code,
  );
  const tickerName = String(
    row?.tickerName ?? row?.ticker_name ?? row?.stockName ?? row?.stock_name ?? row?.corpName ?? row?.corp_name ?? row?.name ?? row?.korName ?? row?.kor_name ?? '',
  ).trim();

  if (!ticker || !tickerName) return null;

  const closePriceRaw = row?.closePrice ?? row?.close_price ?? row?.currentPrice ?? row?.current_price ?? row?.price ?? row?.lastPrice ?? row?.last_price;
  const closePrice = Number(String(closePriceRaw ?? '').replace(/,/g, ''));

  return {
    ticker,
    tickerName,
    market: row?.market ?? row?.marketType ?? row?.market_type ?? row?.exchange ?? null,
    sector: row?.sector ?? row?.sectorName ?? row?.sector_name ?? row?.industryName ?? row?.industry_name ?? null,
    closePrice: Number.isFinite(closePrice) && closePrice > 0 ? closePrice : null,
  };
}

function unwrapStockSearchRows(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.stocks)) return payload.stocks;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
}

async function searchPortfolioStockCandidates(keyword: string): Promise<PortfolioStockCandidate[]> {
  const q = String(keyword ?? '').trim();
  if (!q) return [];

  const endpoints = [
    '/portfolio/stocks/search',
    '/stocks/search',
    '/market/stocks/search',
    '/api/stocks/search',
  ];

  let lastError: any = null;
  for (const url of endpoints) {
    try {
      const res = await api.get(url, { params: { keyword: q, q, limit: 12 } });
      const seen = new Set<string>();
      return unwrapStockSearchRows(res?.data)
        .map(normalizePortfolioStockCandidate)
        .filter((row): row is PortfolioStockCandidate => Boolean(row?.ticker && row?.tickerName))
        .filter((row) => {
          if (seen.has(row.ticker)) return false;
          seen.add(row.ticker);
          return true;
        })
        .slice(0, 12);
    } catch (e: any) {
      lastError = e;
      const status = e?.response?.status;
      if (status === 401) throw e;
      if (status && status !== 404 && status !== 405) break;
    }
  }

  if (lastError) throw lastError;
  return [];
}

async function fetchPortfolioLatestClose(ticker: string): Promise<PortfolioLatestClose> {
  const normalizedTicker = normalizeTickerCode(ticker);
  if (!normalizedTicker) {
    return {
      ticker: '',
      found: false,
      tradeDate: null,
      closePrice: null,
      message: '종목코드를 확인할 수 없습니다.',
    };
  }

  const res = await api.get('/portfolio/stocks/latest-close', {
    params: { ticker: normalizedTicker },
  });
  const src = res?.data?.data ?? res?.data?.result ?? res?.data ?? {};
  const rawPrice = src?.closePrice ?? src?.close_price;
  const closePrice = Number(String(rawPrice ?? '').replace(/,/g, ''));

  return {
    ticker: normalizeTickerCode(src?.ticker ?? normalizedTicker),
    found: Boolean(src?.found) && Number.isFinite(closePrice) && closePrice > 0,
    tradeDate: src?.tradeDate ?? src?.trade_date ?? null,
    closePrice: Number.isFinite(closePrice) && closePrice > 0 ? closePrice : null,
    source: src?.source ?? null,
    message: src?.message ?? null,
  };
}

function holdingToInputLine(item: PortfolioHoldingItem) {
  const parts = [item.ticker, item.tickerName];
  const quantity = Number(item.quantity ?? 0);
  const avgPrice = Number(item.avgPrice ?? 0);
  const hasQuantity = Number.isFinite(quantity) && quantity > 0;
  const hasAvgPrice = Number.isFinite(avgPrice) && avgPrice > 0;

  // portfolioText는 공백 위치를 기준으로 3번째=수량, 4번째=평단으로 다시 파싱된다.
  // 수량 없이 평단만 존재할 때 3번째 자리에 평단을 바로 넣으면 평단이 수량으로 오인된다.
  // 따라서 평단만 존재하는 경우 수량 자리를 0으로 명시해 필드 위치를 보존한다.
  if (hasQuantity) {
    parts.push(String(quantity));
  } else if (hasAvgPrice) {
    parts.push('0');
  }

  if (hasAvgPrice) parts.push(String(avgPrice));
  return parts.join(' ');
}

function unwrapPortfolioHoldingsPayload(payload: any): {
  holdings: PortfolioHoldingItem[];
  preferredRiskMode: PortfolioRiskMode | null;
  autoManageEnabled: boolean | null;
} {
  const src = payload?.data ?? payload?.result ?? payload ?? {};
  const rows = Array.isArray(src?.holdings)
    ? src.holdings
    : Array.isArray(src?.items)
      ? src.items
      : Array.isArray(src?.portfolioHoldings)
        ? src.portfolioHoldings
        : Array.isArray(src)
          ? src
          : [];

  const seen = new Set<string>();
  const holdings = rows
    .map((row: any): PortfolioHoldingItem | null => {
      const ticker = normalizeTickerCode(
        row?.ticker ?? row?.stockCode ?? row?.stock_code ?? row?.code ?? row?.symbol,
      );
      if (!ticker || seen.has(ticker)) return null;
      seen.add(ticker);

      const tickerName = String(
        row?.tickerName ??
          row?.ticker_name ??
          row?.stockName ??
          row?.stock_name ??
          row?.corpName ??
          row?.corp_name ??
          row?.name ??
          ticker,
      ).trim();

      return {
        ticker,
        tickerName: tickerName || ticker,
        quantity: toNumberOrNull(
          row?.quantity ?? row?.holdingQuantity ?? row?.holding_quantity,
        ),
        avgPrice: toNumberOrNull(
          row?.avgPrice ?? row?.avg_price ?? row?.averagePrice ?? row?.average_price,
        ),
      };
    })
    .filter((row: PortfolioHoldingItem | null): row is PortfolioHoldingItem => Boolean(row));

  const autoManageValue = src?.autoManageEnabled ?? src?.auto_manage_enabled;

  return {
    holdings,
    preferredRiskMode: normalizePortfolioRiskMode(
      src?.preferredRiskMode ??
        src?.preferred_risk_mode ??
        src?.riskMode ??
        src?.risk_mode,
    ),
    autoManageEnabled:
      typeof autoManageValue === 'boolean'
        ? autoManageValue
        : autoManageValue === 1 || autoManageValue === '1' || autoManageValue === 'true'
          ? true
          : autoManageValue === 0 || autoManageValue === '0' || autoManageValue === 'false'
            ? false
            : null,
  };
}

function PortfolioStockSearchBox({
  holdings,
  onAddHolding,
}: {
  holdings: PortfolioHoldingItem[];
  onAddHolding: (item: PortfolioHoldingItem) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const [candidates, setCandidates] = useState<PortfolioStockCandidate[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchMessage, setSearchMessage] = useState('종목명 일부나 종목코드를 입력한 뒤 검색하세요. 예: 삼성, 하이닉스, 005930');

  const holdingTickers = useMemo(() => new Set(holdings.map((h) => h.ticker)), [holdings]);

  const handleSearch = useCallback(async () => {
    const q = keyword.trim();
    if (!q) {
      setCandidates([]);
      setSearchMessage('검색어를 입력하세요. 종목명 일부 또는 종목코드로 검색할 수 있습니다.');
      return;
    }

    setLoadingSearch(true);
    setSearchMessage('검색 중입니다...');
    try {
      const rows = await searchPortfolioStockCandidates(q);
      setCandidates(rows);
      setSearchMessage(rows.length ? `${rows.length}개 후보를 찾았습니다. 추가할 종목을 선택하세요.` : '일치하는 종목이 없습니다. 종목명 또는 종목코드를 다시 확인하세요.');
    } catch (e: any) {
      setCandidates([]);
      setSearchMessage(
        e?.response?.status === 401
          ? '로그인 세션 확인이 필요합니다.'
          : '종목 검색 API가 아직 연결되지 않았습니다. 백엔드에 /portfolio/stocks/search 연결 후 사용할 수 있습니다.',
      );
    } finally {
      setLoadingSearch(false);
    }
  }, [keyword]);

  const handleAdd = useCallback(
    (candidate: PortfolioStockCandidate) => {
      onAddHolding({
        ticker: candidate.ticker,
        tickerName: candidate.tickerName,
        quantity: null,
        avgPrice: null,
      });
      setKeyword('');
      setCandidates([]);
      setSearchMessage(
        `${candidate.tickerName} (${candidate.ticker})를 추가했습니다. 진단에는 보유 수량이 필수입니다. 아래 목록에서 수정 체크 후 수량을 입력하고, 손익률 계산이 필요하면 평단도 입력하세요.`,
      );
    },
    [onAddHolding],
  );

  return (
    <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: '#fff', border: BORDER }}>
      <Stack spacing={0.75}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: TXT, fontSize: 13, fontWeight: 950 }}>종목명 검색 추가</Typography>
            <Typography sx={{ color: SUB2, fontSize: 11.5, mt: 0.25, lineHeight: 1.55 }}>종목코드를 몰라도 종목명 일부로 검색해서 바로 추가합니다.</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) auto' }, gap: 0.6, alignItems: 'center' }}>
          <Box
            component="input"
            value={keyword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSearch();
              }
            }}
            placeholder="예: 삼성, 하이닉스, NAVER, 005930"
            sx={{
              width: '100%',
              minHeight: 38,
              px: 1,
              borderRadius: 2,
              border: BORDER,
              outline: 'none',
              color: TXT,
              background: '#fff',
              fontSize: 13,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              '&:focus': { borderColor: BLUE },
            }}
          />
          <Button
            variant="contained"
            onClick={() => void handleSearch()}
            disabled={loadingSearch}
            sx={{
              borderRadius: 2,
              minHeight: 38,
              minWidth: { xs: '100%', md: 96 },
              bgcolor: BLUE,
              color: '#fff',
              fontWeight: 950,
              textTransform: 'none',
              '&:hover': { bgcolor: BLUE },
            }}
          >
            {loadingSearch ? '검색 중…' : '검색'}
          </Button>
        </Box>

        <Typography sx={{ color: searchMessage.includes('연결') || searchMessage.includes('없습니다') || searchMessage.includes('필요') ? AMBER : SUB2, fontSize: 11.7, lineHeight: 1.55 }}>{searchMessage}</Typography>

        {candidates.length ? (
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            {candidates.map((candidate) => {
              const alreadyAdded = holdingTickers.has(candidate.ticker);
              return (
                <Paper key={candidate.ticker} elevation={0} sx={{ p: 0.85, borderRadius: 1.8, background: alreadyAdded ? '#eef2f7' : CARD, border: BORDER }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr auto' }, gap: 0.7, alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: TXT, fontSize: 12.8, fontWeight: 950 }} noWrap>{candidate.tickerName} ({candidate.ticker})</Typography>
                      <Typography sx={{ color: SUB, fontSize: 11.5, mt: 0.2 }} noWrap>
                        {candidate.market || '시장 미확인'} · {candidate.sector || '섹터 미확인'}{candidate.closePrice ? ` · 현재가 ${candidate.closePrice.toLocaleString('ko-KR')}원` : ''}
                      </Typography>
                    </Box>
                    <Button
                      variant={alreadyAdded ? 'outlined' : 'contained'}
                      onClick={() => handleAdd(candidate)}
                      disabled={alreadyAdded}
                      sx={{ borderRadius: 2, minHeight: 32, textTransform: 'none', fontWeight: 900, bgcolor: alreadyAdded ? '#fff' : BLUE, color: alreadyAdded ? SUB : '#fff', borderColor: BORDER_STRONG, '&:hover': { bgcolor: alreadyAdded ? '#fff' : BLUE } }}
                    >
                      {alreadyAdded ? '추가됨' : '추가'}
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}

function getPortfolioRiskModeConfig(mode: PortfolioRiskMode) {
  if (mode === 'conservative') {
    return {
      label: '보수적',
      subtitle: '하락 방어와 현금 비중 관리 중심',
      cashGuide: '현금 25~40%',
      upTrigger: '+4% 이상 또는 섹터 우호 강함',
      downTrigger: '-2.5% 이하 또는 macro/ETF 섹터 주의',
      rebalanceGuide: '하락 알림 우선, 급등 추격 제한',
      chips: ['방어 우선', '하락 알림 강화', '현금 비중 확보'],
    };
  }

  if (mode === 'aggressive') {
    return {
      label: '공격적',
      subtitle: '강한 섹터 모멘텀과 상승 추세 추종 중심',
      cashGuide: '현금 0~15%',
      upTrigger: '+6% 이상 또는 macro/ETF 섹터 상위',
      downTrigger: '-5% 이하 또는 섹터 급락',
      rebalanceGuide: '상승 알림 우선, 강세 섹터 비중 확대',
      chips: ['모멘텀 추종', '상승 알림 강화', '섹터 집중'],
    };
  }

  return {
    label: '균형형',
    subtitle: '상승 기회와 하락 방어를 동시에 반영',
    cashGuide: '현금 15~25%',
    upTrigger: '+5% 이상 또는 섹터 우호 후보',
    downTrigger: '-3.5% 이하 또는 섹터 주의 후보',
    rebalanceGuide: '상승/하락 알림 균형, 섹터 쏠림 점검',
    chips: ['균형 운용', '상승·하락 동시 감시', '분산 관리'],
  };
}

function PortfolioHoldingsGrid({
  holdings,
  deletingTicker,
  saving,
  onChangeHolding,
  onSavePortfolio,
  onDeleteHolding,
}: {
  holdings: PortfolioHoldingItem[];
  deletingTicker?: string | null;
  saving?: boolean;
  onChangeHolding: (
    ticker: string,
    patch: Pick<PortfolioHoldingItem, 'quantity' | 'avgPrice'>,
  ) => void;
  onSavePortfolio: () => Promise<void>;
  onDeleteHolding: (ticker: string) => void;
}) {
  const [editableTickers, setEditableTickers] = useState<Record<string, boolean>>({});
  const [latestCloseByTicker, setLatestCloseByTicker] = useState<Record<string, PortfolioLatestClose>>({});
  const [latestCloseLoading, setLatestCloseLoading] = useState<Record<string, boolean>>({});
  const [latestCloseError, setLatestCloseError] = useState<Record<string, string>>({});
  const holdingsRef = useRef(holdings);

  useEffect(() => {
    holdingsRef.current = holdings;
  }, [holdings]);

  const toggleEditable = useCallback(async (item: PortfolioHoldingItem, checked: boolean) => {
    const ticker = item.ticker;
    setEditableTickers((current) => ({
      ...current,
      [ticker]: checked,
    }));

    if (!checked) return;

    const currentAvgPrice = Number(item.avgPrice ?? 0);
    if (currentAvgPrice > 0) return;

    setLatestCloseLoading((current) => ({ ...current, [ticker]: true }));
    setLatestCloseError((current) => ({ ...current, [ticker]: '' }));

    try {
      const latest = await fetchPortfolioLatestClose(ticker);
      setLatestCloseByTicker((current) => ({ ...current, [ticker]: latest }));

      if (!latest.found || !latest.closePrice) {
        setLatestCloseError((current) => ({
          ...current,
          [ticker]: latest.message || '저장된 최근 종가를 찾지 못했습니다.',
        }));
        return;
      }

      const latestHolding = holdingsRef.current.find((holding) => holding.ticker === ticker);
      const latestAvgPrice = Number(latestHolding?.avgPrice ?? 0);
      if (latestAvgPrice <= 0) {
        const latestQuantity = Number(latestHolding?.quantity ?? item.quantity ?? 0);
        const closePrice = Number(latest.closePrice);

        // V2에서 수량이 비어 있고 평단만 최근 종가로 자동 입력될 때,
        // portfolioText 직렬화가 평단을 3번째 토큰(수량)으로 밀어 넣는 문제가 있었다.
        // 현재 수량이 방금 조회한 최근 종가와 정확히 같고 평단이 비어 있다면
        // 그 잘못 이동된 값을 수량에서 제거한 뒤 평단으로 복구한다.
        const legacyShiftedAvgPrice =
          Number.isFinite(latestQuantity) &&
          latestQuantity > 0 &&
          Number.isFinite(closePrice) &&
          closePrice > 0 &&
          Math.abs(latestQuantity - closePrice) < 0.000001;

        onChangeHolding(ticker, {
          quantity: legacyShiftedAvgPrice
            ? null
            : latestHolding?.quantity ?? item.quantity ?? null,
          avgPrice: closePrice,
        });
      }
    } catch (e: any) {
      const status = e?.response?.status;
      setLatestCloseError((current) => ({
        ...current,
        [ticker]: status === 401
          ? '로그인 세션을 확인한 뒤 다시 시도하세요.'
          : '최근 종가를 불러오지 못했습니다. 평단을 직접 입력하세요.',
      }));
    } finally {
      setLatestCloseLoading((current) => ({ ...current, [ticker]: false }));
    }
  }, [onChangeHolding]);

  if (!holdings.length) {
    return (
      <Paper elevation={0} sx={{ p: 1.05, borderRadius: 2, background: '#fff', border: BORDER }}>
        <Typography sx={{ color: SUB, fontSize: 12.5, lineHeight: 1.6 }}>
          아직 등록된 보유 종목이 없습니다. 위 검색창에서 종목을 추가하면 이 영역에 그리드로 표시됩니다.
        </Typography>
      </Paper>
    );
  }

  const inputSx = {
    width: '100%',
    minWidth: 0,
    height: 34,
    px: 0.8,
    borderRadius: 1.6,
    border: '1px solid #c9d6e2',
    outline: 'none',
    color: TXT,
    background: '#fff',
    fontSize: 12,
    fontWeight: 850,
    fontFamily: 'inherit',
    textAlign: 'right',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: BLUE,
      boxShadow: '0 0 0 3px rgba(15,59,95,.08)',
    },
  } as const;

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2.2, background: '#fff', border: BORDER }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'grid' },
          gridTemplateColumns: '46px 1fr 1.25fr .9fr 1fr 1fr 104px',
          gap: 0.7,
          px: 1,
          py: 0.75,
          bgcolor: '#f8fafc',
          borderBottom: BORDER,
          alignItems: 'center',
        }}
      >
        {['수정', '종목코드', '종목명', '수량', '평단', '평가원금', '관리'].map((label) => (
          <Typography key={label} sx={{ color: SUB2, fontSize: 11, fontWeight: 950, textAlign: label === '수정' ? 'center' : 'left' }}>
            {label}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: 'grid' }}>
        {holdings.map((item, idx) => {
          const quantity = Number(item.quantity ?? 0);
          const avgPrice = Number(item.avgPrice ?? 0);
          const principal = quantity > 0 && avgPrice > 0 ? quantity * avgPrice : null;
          const deleting = deletingTicker === item.ticker;
          const editable = Boolean(editableTickers[item.ticker]);

          return (
            <Box
              key={`${item.ticker}-${idx}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '36px minmax(0,1fr) auto',
                  md: '46px 1fr 1.25fr .9fr 1fr 1fr 104px',
                },
                gap: { xs: 0.6, md: 0.7 },
                px: 1,
                py: 0.85,
                alignItems: 'center',
                borderBottom: idx === holdings.length - 1 ? 'none' : BORDER,
                bgcolor: editable ? '#f8fbfe' : '#fff',
              }}
            >
              <Box
                component="label"
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <Box
                  component="input"
                  type="checkbox"
                  checked={editable}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    void toggleEditable(item, event.target.checked);
                  }}
                  aria-label={`${item.tickerName || item.ticker} 수량과 평단 수정`}
                  sx={{
                    width: 18,
                    height: 18,
                    accentColor: BLUE,
                    cursor: 'pointer',
                  }}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ display: { xs: 'block', md: 'none' }, color: TXT, fontSize: 13, fontWeight: 950 }} noWrap>
                  {item.tickerName || item.ticker}
                </Typography>
                <Typography sx={{ color: { xs: SUB2, md: TXT }, fontSize: { xs: 11.5, md: 12.5 }, fontWeight: { xs: 850, md: 950 } }} noWrap>
                  {item.ticker}
                </Typography>
              </Box>

              <Typography sx={{ display: { xs: 'none', md: 'block' }, color: TXT, fontSize: 12.8, fontWeight: 950 }} noWrap>
                {item.tickerName || item.ticker}
              </Typography>

              {editable ? (
                <Box
                  component="input"
                  inputMode="numeric"
                  value={quantity > 0 ? quantity.toLocaleString('ko-KR') : ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onChangeHolding(item.ticker, {
                      quantity: parsePositiveIntegerInput(event.target.value),
                      avgPrice: item.avgPrice ?? null,
                    })
                  }
                  placeholder="수량"
                  aria-label={`${item.tickerName || item.ticker} 수량`}
                  sx={inputSx}
                />
              ) : (
                <Typography sx={{ display: { xs: 'none', md: 'block' }, color: SUB, fontSize: 12.3, fontWeight: 850 }}>
                  {quantity > 0 ? quantity.toLocaleString('ko-KR') : '-'}
                </Typography>
              )}

              {editable ? (
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    component="input"
                    inputMode="numeric"
                    value={avgPrice > 0 ? avgPrice.toLocaleString('ko-KR') : ''}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      onChangeHolding(item.ticker, {
                        quantity: item.quantity ?? null,
                        avgPrice: parsePositiveIntegerInput(event.target.value),
                      })
                    }
                    placeholder="평균단가"
                    aria-label={`${item.tickerName || item.ticker} 평균단가`}
                    sx={inputSx}
                  />

                  {latestCloseLoading[item.ticker] ? (
                    <Typography sx={{ color: SUB2, fontSize: 10.4, mt: 0.35, lineHeight: 1.35 }}>
                      최근 거래일 종가 조회 중…
                    </Typography>
                  ) : latestCloseError[item.ticker] ? (
                    <Typography sx={{ color: AMBER, fontSize: 10.4, mt: 0.35, lineHeight: 1.35 }}>
                      {latestCloseError[item.ticker]}
                    </Typography>
                  ) : latestCloseByTicker[item.ticker]?.found && latestCloseByTicker[item.ticker]?.closePrice ? (
                    <Typography sx={{ color: SUB2, fontSize: 10.4, mt: 0.35, lineHeight: 1.35 }}>
                      최근 종가 {latestCloseByTicker[item.ticker]?.tradeDate || '최근 거래일'} · {Number(latestCloseByTicker[item.ticker]?.closePrice).toLocaleString('ko-KR')}원 참고값 자동입력
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                <Typography sx={{ display: { xs: 'none', md: 'block' }, color: SUB, fontSize: 12.3, fontWeight: 850 }}>
                  {avgPrice > 0 ? `${avgPrice.toLocaleString('ko-KR')}원` : '-'}
                </Typography>
              )}

              <Typography
                sx={{
                  display: { xs: editable ? 'block' : 'none', md: 'block' },
                  gridColumn: { xs: editable ? '2 / 3' : 'auto', md: 'auto' },
                  color: TXT,
                  fontSize: 12.3,
                  fontWeight: 900,
                }}
              >
                {principal != null ? formatAmount(principal) : '-'}
              </Typography>

              {!editable && (
                <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 0.15, gridColumn: '2 / 3' }}>
                  <Typography sx={{ color: SUB, fontSize: 11.5, lineHeight: 1.45 }}>
                    수량 {quantity > 0 ? quantity.toLocaleString('ko-KR') : '-'} · 평단 {avgPrice > 0 ? `${avgPrice.toLocaleString('ko-KR')}원` : '-'}
                  </Typography>
                  <Typography sx={{ color: TXT, fontSize: 11.7, fontWeight: 900, lineHeight: 1.45 }}>
                    평가원금 {principal != null ? formatAmount(principal) : '-'}
                  </Typography>
                </Box>
              )}

              <Box
                sx={{
                  justifySelf: 'stretch',
                  gridRow: { xs: editable ? '1 / span 3' : '1 / span 2', md: 'auto' },
                  gridColumn: { xs: '3 / 4', md: 'auto' },
                  display: 'grid',
                  gap: 0.45,
                }}
              >
                {editable && (
                  <Button
                    variant="contained"
                    onClick={async () => {
                      await onSavePortfolio();
                      await toggleEditable(item, false);
                    }}
                    disabled={Boolean(saving) || deleting}
                    sx={{
                      borderRadius: 1.8,
                      minHeight: 30,
                      px: 1,
                      textTransform: 'none',
                      bgcolor: BLUE,
                      color: '#fff',
                      fontWeight: 950,
                      '&:hover': { bgcolor: '#0b304f' },
                    }}
                  >
                    {saving ? '저장 중…' : '저장'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => onDeleteHolding(item.ticker)}
                  disabled={deleting || Boolean(saving)}
                  sx={{
                    borderRadius: 1.8,
                    minHeight: 30,
                    px: 1,
                    textTransform: 'none',
                    fontWeight: 950,
                  }}
                >
                  {deleting ? '삭제 중…' : '삭제'}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function PortfolioAutomationPanel({
  holdings,
  riskMode,
  effectiveRiskMode,
  autoManageEnabled,
  strategyPreview,
  action,
  deletingTicker,
  isMobile,
  onChangeRiskMode,
  onToggleAutoManage,
  onAddHolding,
  onChangeHolding,
  onDeleteHolding,
  onSavePortfolio,
  onRefreshStrategy,
  onApplyRecommendedMode,
  onOpenRealtime,
  onOpenMarketSettings,
}: {
  holdings: PortfolioHoldingItem[];
  riskMode: PortfolioRiskMode;
  effectiveRiskMode: PortfolioRiskMode;
  autoManageEnabled: boolean;
  strategyPreview: PortfolioStrategyPreview | null;
  action: string | null;
  deletingTicker?: string | null;
  isMobile: boolean;
  onChangeRiskMode: (value: PortfolioRiskMode) => void;
  onToggleAutoManage: () => void;
  onAddHolding: (item: PortfolioHoldingItem) => void;
  onChangeHolding: (
    ticker: string,
    patch: Pick<PortfolioHoldingItem, 'quantity' | 'avgPrice'>,
  ) => void;
  onDeleteHolding: (ticker: string) => void;
  onSavePortfolio: () => Promise<void>;
  onRefreshStrategy: () => void;
  onApplyRecommendedMode: () => void;
  onOpenRealtime: () => void;
  onOpenMarketSettings: () => void;
}) {
  const cfg = getPortfolioRiskModeConfig(effectiveRiskMode);
  const selectedCfg = getPortfolioRiskModeConfig(riskMode);
  const recommendedMode = strategyPreview?.recommendedRiskMode ?? null;
  const recommendedCfg = recommendedMode ? getPortfolioRiskModeConfig(recommendedMode) : null;
  const holdingActions = strategyPreview?.holdingActions ?? [];
  const validationIssues = strategyPreview?.validationIssues ?? [];
  const blockingValidationIssues = validationIssues.filter(isPortfolioValidationError);
  const dataInvalidActionCount = holdingActions.filter(isPortfolioDataInvalidAction).length;
  const diagnosing = action === 'strategy' || action === 'strategy-auto';
  const riskModes: PortfolioRiskMode[] = ['conservative', 'balanced', 'aggressive'];

  const getActionTone = (item: PortfolioStrategyHoldingAction) => {
    const text = `${item.actionLabel ?? ''} ${item.actionReason ?? ''}`.toLowerCase();
    const risk = String(item.riskLevel ?? '').toUpperCase();
    const validationMeta = portfolioActionValidationMeta(item, validationIssues);

    if (isPortfolioDataInvalidAction(item)) {
      return {
        label: validationMeta?.label || '필수 데이터 확인 필요',
        bg: 'var(--pi-watch-soft)',
        border: '1px solid rgba(199,129,24,.26)',
        color: 'var(--pi-watch)',
        accent: 'var(--pi-watch)',
      };
    }

    if (risk === 'HIGH' || /주의|축소|정리|매도|위험|하락/.test(text)) {
      return {
        label: item.actionLabel || '비중 축소 검토',
        bg: 'var(--pi-watch-soft)',
        border: '1px solid rgba(199,129,24,.26)',
        color: 'var(--pi-watch)',
        accent: 'var(--pi-watch)',
      };
    }

    if (risk === 'LOW' || /강화|추가|매수|우호|상승/.test(text)) {
      return {
        label: item.actionLabel || '보유 유지·추가 검토',
        bg: 'var(--pi-positive-soft)',
        border: '1px solid rgba(22,138,91,.24)',
        color: 'var(--pi-positive)',
        accent: 'var(--pi-positive)',
      };
    }

    return {
      label: item.actionLabel || '보유 점검',
      bg: 'var(--pi-neutral-soft)',
      border: '1px solid var(--pi-border-quiet)',
      color: 'var(--pi-text-secondary)',
      accent: 'var(--pi-neutral)',
    };
  };

  const priorityActions = [...holdingActions].sort((a, b) => {
    const rank = (item: PortfolioStrategyHoldingAction) => {
      const risk = String(item.riskLevel ?? '').toUpperCase();
      const phrase = `${item.actionLabel ?? ''} ${item.actionReason ?? ''}`;
      if (risk === 'HIGH' || /축소|정리|매도|위험|주의/.test(phrase)) return 0;
      if (risk === 'LOW' || /추가|매수|강화|우호/.test(phrase)) return 1;
      return 2;
    };
    return rank(a) - rank(b);
  });

  const premiumPanelSx = {
    borderRadius: 2,
    border: '1px solid var(--pi-border-quiet)',
    background: 'var(--pi-surface)',
    boxShadow: 'none',
  };

  return (
    <Box sx={{ display: 'grid', gap: 1.25 }}>
      <Paper
        elevation={0}
        sx={{
          ...premiumPanelSx,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 1.2, md: 1.7 },
            py: { xs: 1.2, md: 1.45 },
            background: 'var(--pi-surface)',
            color: 'var(--pi-text)',
            borderLeft: '4px solid var(--pi-blue)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: 1.1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: 10, md: 10.5 },
                  fontWeight: 900,
                  color: 'var(--pi-blue)',
                  letterSpacing: '.12em',
                }}
              >
                PORTFOLIO INTELLIGENCE
              </Typography>
              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: { xs: 19, md: 22 },
                  fontWeight: 950,
                  letterSpacing: '-.035em',
                }}
              >
                오늘 내 종목
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  color: 'var(--pi-text-secondary)',
                  fontSize: { xs: 11, md: 12 },
                  lineHeight: 1.55,
                }}
              >
                복잡한 보유 데이터를 압축해 지금 먼저 봐야 할 판단과 근거를 보여줍니다.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 0.55,
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'stretch' : 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                onClick={onOpenRealtime}
                sx={{
                  minHeight: 38,
                  px: 1.25,
                  borderRadius: 2,
                  borderColor: 'var(--pi-border-strong)',
                  color: 'var(--pi-text)',
                  fontWeight: 900,
                  textTransform: 'none',
                  width: isMobile ? '100%' : 'auto',
                  '&:hover': {
                    borderColor: 'var(--pi-blue)',
                    bgcolor: 'rgba(41,104,255,.06)',
                  },
                }}
              >
                실시간 종목
              </Button>

              <Button
                variant="outlined"
                onClick={onOpenMarketSettings}
                sx={{
                  minHeight: 38,
                  px: 1.25,
                  borderRadius: 2,
                  borderColor: 'var(--pi-border-strong)',
                  color: 'var(--pi-text)',
                  fontWeight: 900,
                  textTransform: 'none',
                  width: isMobile ? '100%' : 'auto',
                  '&:hover': {
                    borderColor: 'var(--pi-blue)',
                    bgcolor: 'rgba(41,104,255,.06)',
                  },
                }}
              >
                알림 상세 설정
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2,minmax(0,1fr))',
                lg: 'repeat(4,minmax(0,1fr))',
              },
              gap: 0.7,
            }}
          >
            {[
              {
                label: '보유 자산',
                value: `${holdings.length}종목`,
                sub: '현재 등록 기준',
              },
              {
                label: '운용 프로필',
                value: cfg.label,
                sub:
                  selectedCfg.label === cfg.label
                    ? '사용자 설정 적용'
                    : `${selectedCfg.label} → 자동 조정`,
              },
              {
                label: '자동 진단',
                value: autoManageEnabled ? '사용 중' : '직접 실행',
                sub: autoManageEnabled ? '장중 5분 · 화면 활성 시' : '버튼 실행 방식',
              },
              {
                label: '변화 추적',
                value: holdings.length > 0 ? '패널에서 즉시' : '대상 없음',
                sub: holdings.length > 0 ? '상세 기준은 알림 탭' : '보유 종목 등록 필요',
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  minWidth: 0,
                  p: 0.9,
                  borderRadius: 2,
                  border: '1px solid var(--pi-border-quiet)',
                  bgcolor: 'var(--pi-surface-subtle)',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--pi-text-muted)',
                    fontSize: 9.8,
                    fontWeight: 900,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    mt: 0.2,
                    color: 'var(--pi-text)',
                    fontSize: 16,
                    fontWeight: 950,
                    letterSpacing: '-.025em',
                  }}
                >
                  {item.value}
                </Typography>
                <Typography
                  noWrap
                  sx={{
                    mt: 0.15,
                    color: 'var(--pi-text-secondary)',
                    fontSize: 10.4,
                  }}
                >
                  {item.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 1.05, md: 1.35 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: '1.2fr .8fr' },
            gap: 1,
            bgcolor: 'var(--pi-surface-subtle)',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, md: 1.15 },
              borderRadius: 1.5,
              bgcolor: 'var(--pi-surface)',
              border: '1px solid var(--pi-border-quiet)',
              minWidth: 0,
            }}
          >
            <Stack spacing={0.9}>
              <Box>
                <Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>
                  보유 종목 관리
                </Typography>
                <Typography
                  sx={{
                    color: SUB2,
                    fontSize: 11.5,
                    lineHeight: 1.55,
                    mt: 0.2,
                  }}
                >
                  종목명으로 검색해 추가한 뒤 수량과 평균단가를 관리합니다. 종목별 변화 추적은 패널에서 바로 켜고, 상승·하락 기준과 수신 채널은 알림 탭에서 세부 조정합니다.
                </Typography>
              </Box>

              <PortfolioStockSearchBox holdings={holdings} onAddHolding={onAddHolding} />

              <PortfolioHoldingsGrid
                holdings={holdings}
                deletingTicker={deletingTicker}
                saving={action === 'save'}
                onChangeHolding={onChangeHolding}
                onSavePortfolio={onSavePortfolio}
                onDeleteHolding={onDeleteHolding}
              />

            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1, md: 1.15 },
              borderRadius: 1.5,
              bgcolor: 'var(--pi-surface)',
              border: '1px solid var(--pi-border-quiet)',
              minWidth: 0,
            }}
          >
            <Stack spacing={0.9}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 0.8,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: TXT, fontSize: 14, fontWeight: 950 }}>
                    전략 운용 콘솔
                  </Typography>
                  <Typography
                    sx={{
                      color: SUB2,
                      fontSize: 11.5,
                      lineHeight: 1.55,
                      mt: 0.2,
                    }}
                  >
                    위험 성향을 선택하고 시장 진단 결과를 반영합니다.
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={autoManageEnabled ? '자동 진단 활성' : '수동 진단'}
                  sx={{
                    height: 24,
                    bgcolor: autoManageEnabled ? 'var(--pi-positive-soft)' : 'var(--pi-neutral-soft)',
                    border: autoManageEnabled
                      ? '1px solid #b9ddc0'
                      : '1px solid #d8e1e9',
                    color: autoManageEnabled ? 'var(--pi-positive)' : 'var(--pi-text-secondary)',
                    fontWeight: 950,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
                  gap: 0.5,
                  p: 0.35,
                  borderRadius: 2,
                  bgcolor: 'var(--pi-surface-subtle)',
                }}
              >
                {riskModes.map((mode) => {
                  const modeCfg = getPortfolioRiskModeConfig(mode);
                  const active = riskMode === mode;

                  return (
                    <Button
                      key={mode}
                      variant="text"
                      onClick={() => onChangeRiskMode(mode)}
                      sx={{
                        minHeight: 38,
                        px: 0.5,
                        borderRadius: 1.6,
                        textTransform: 'none',
                        fontWeight: 950,
                        bgcolor: active ? '#fff' : 'transparent',
                        color: active ? 'var(--pi-blue)' : 'var(--pi-text-secondary)',
                        boxShadow: active
                          ? '0 3px 10px rgba(22,46,68,.09)'
                          : 'none',
                        '&:hover': {
                          bgcolor: active ? '#fff' : 'rgba(255,255,255,.55)',
                        },
                      }}
                    >
                      {modeCfg.label}
                    </Button>
                  );
                })}
              </Box>

              <Box
                sx={{
                  p: 1,
                  borderRadius: 2.2,
                  border: autoManageEnabled
                    ? '1px solid #b7dec0'
                    : '1px solid #dce5ed',
                  background: autoManageEnabled
                    ? 'linear-gradient(145deg,#f2fbf4 0%,#eaf7ed 100%)'
                    : '#f7f9fb',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 0.8,
                    alignItems: 'start',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: SUB2,
                        fontSize: 10.5,
                        fontWeight: 900,
                      }}
                    >
                      현재 적용 전략
                    </Typography>
                    <Typography
                      sx={{
                        color: TXT,
                        mt: 0.1,
                        fontSize: 19,
                        fontWeight: 950,
                        letterSpacing: '-.035em',
                      }}
                    >
                      {cfg.label}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      sx={{
                        color: SUB2,
                        fontSize: 10.5,
                        fontWeight: 900,
                      }}
                    >
                      시스템 권고
                    </Typography>
                    <Typography
                      sx={{
                        color: recommendedCfg ? BLUE : SUB2,
                        mt: 0.15,
                        fontSize: 13,
                        fontWeight: 950,
                      }}
                    >
                      {recommendedCfg?.label ?? '진단 전'}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    mt: 0.65,
                    color: '#53687c',
                    fontSize: 11.6,
                    lineHeight: 1.65,
                    wordBreak: 'keep-all',
                  }}
                >
                  {strategyPreview?.actionSummary ||
                    '보유 종목을 등록하고 진단하면 현재 시장에 맞는 운용 성향과 종목별 우선 대응을 표시합니다.'}
                </Typography>

                <Box
                  sx={{
                    mt: 0.75,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
                    gap: 0.55,
                  }}
                >
                  <Box
                    sx={{
                      p: 0.65,
                      borderRadius: 1.6,
                      bgcolor: 'rgba(255,255,255,.82)',
                      border: '1px solid rgba(194,211,200,.8)',
                    }}
                  >
                    <Typography
                      sx={{ color: SUB2, fontSize: 9.8, fontWeight: 900 }}
                    >
                      권장 현금
                    </Typography>
                    <Typography
                      sx={{ color: TXT, mt: 0.15, fontSize: 12.3, fontWeight: 950 }}
                    >
                      {strategyPreview?.cashGuide || cfg.cashGuide}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      p: 0.65,
                      borderRadius: 1.6,
                      bgcolor: 'rgba(255,255,255,.82)',
                      border: '1px solid rgba(194,211,200,.8)',
                    }}
                  >
                    <Typography
                      sx={{ color: SUB2, fontSize: 9.8, fontWeight: 900 }}
                    >
                      운용 원칙
                    </Typography>
                    <Typography
                      sx={{ color: TXT, mt: 0.15, fontSize: 12.3, fontWeight: 950 }}
                    >
                      {cfg.rebalanceGuide}
                    </Typography>
                  </Box>
                </Box>

                {(strategyPreview?.topSectors?.length ||
                  strategyPreview?.weakSectors?.length) ? (
                  <Box
                    sx={{
                      mt: 0.7,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.4,
                    }}
                  >
                    {(strategyPreview?.topSectors ?? []).slice(0, 4).map((sector) => (
                      <Chip
                        key={`top-${sector}`}
                        size="small"
                        label={`강세 · ${sector}`}
                        sx={{
                          height: 22,
                          bgcolor: '#e4f4e7',
                          color: '#267238',
                          border: '1px solid #b9ddc0',
                          fontWeight: 900,
                        }}
                      />
                    ))}
                    {(strategyPreview?.weakSectors ?? []).slice(0, 4).map((sector) => (
                      <Chip
                        key={`weak-${sector}`}
                        size="small"
                        label={`주의 · ${sector}`}
                        sx={{
                          height: 22,
                          bgcolor: '#fff4dc',
                          color: 'var(--pi-watch)',
                          border: '1px solid #ecd08f',
                          fontWeight: 900,
                        }}
                      />
                    ))}
                  </Box>
                ) : null}
              </Box>

              <Box
                sx={{
                  p: 0.8,
                  borderRadius: 1.9,
                  bgcolor: autoManageEnabled ? '#eef7f0' : '#f5f7f9',
                  border: autoManageEnabled
                    ? '1px solid #c9e2ce'
                    : '1px solid #dce4eb',
                }}
              >
                <Typography
                  sx={{
                    color: autoManageEnabled ? '#286b37' : '#53677a',
                    fontSize: 10.8,
                    fontWeight: 950,
                  }}
                >
                  {autoManageEnabled
                    ? '자동 진단: 장중 09:00~15:30, 페이지가 보이는 동안 5분마다 실행'
                    : '수동 진단: 아래 “지금 진단” 버튼을 누를 때만 실행'}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.22,
                    color: SUB2,
                    fontSize: 10.2,
                    lineHeight: 1.45,
                  }}
                >
                  자동 진단을 켜면 장중 최초 진입 시 즉시 한 번 실행하고, 이후 5분 간격으로 갱신합니다.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 0.55,
                }}
              >
                <Button
                  variant={autoManageEnabled ? 'contained' : 'outlined'}
                  onClick={onToggleAutoManage}
                  sx={{
                    borderRadius: 2,
                    minHeight: 39,
                    textTransform: 'none',
                    bgcolor: autoManageEnabled ? BLUE : '#fff',
                    color: autoManageEnabled ? '#fff' : TXT,
                    borderColor: autoManageEnabled ? BLUE : BORDER_STRONG,
                    fontWeight: 950,
                    boxShadow: autoManageEnabled
                      ? '0 5px 14px rgba(15,59,95,.16)'
                      : 'none',
                    '&:hover': {
                      bgcolor: autoManageEnabled ? '#0b304f' : '#f8fafc',
                    },
                  }}
                >
                  {autoManageEnabled ? '자동 진단 사용 중' : '자동 진단 켜기'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={onRefreshStrategy}
                  disabled={diagnosing || holdings.length === 0}
                  sx={{
                    borderRadius: 2,
                    minHeight: 39,
                    textTransform: 'none',
                    color: BLUE,
                    borderColor: '#afc2d2',
                    bgcolor: '#fff',
                    fontWeight: 950,
                    '&:hover': {
                      borderColor: BLUE,
                      bgcolor: '#f4f8fb',
                    },
                  }}
                >
                  {diagnosing ? '진단 실행 중…' : '지금 진단'}
                </Button>
              </Box>

              {recommendedMode && recommendedMode !== riskMode ? (
                <Button
                  variant="text"
                  onClick={onApplyRecommendedMode}
                  sx={{
                    alignSelf: 'flex-end',
                    minHeight: 30,
                    px: 0.5,
                    color: BLUE,
                    fontWeight: 950,
                    textTransform: 'none',
                  }}
                >
                  권고 전략 ‘{recommendedCfg?.label}’ 적용
                </Button>
              ) : null}
            </Stack>
          </Paper>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...premiumPanelSx,
          p: { xs: 1.1, md: 1.4 },
        }}
      >
        <SectionTitle
          title="우선 대응"
          sub="위험·수급·시장 근거가 강하게 갈리는 종목부터 보여줍니다."
          isMobile={isMobile}
          right={
            strategyPreview?.generatedAt ? (
              <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
                <Typography
                  sx={{ color: SUB2, fontSize: 10.2, fontWeight: 900 }}
                >
                  최근 진단
                </Typography>
                <Typography sx={{ color: SUB, fontSize: 11.2, mt: 0.15 }}>
                  {formatDateTime(strategyPreview.generatedAt)}
                </Typography>
              </Box>
            ) : undefined
          }
        />

        {dataInvalidActionCount > 0 ? (
          <Box
            sx={{
              mb: 0.8,
              px: 1.05,
              py: 0.8,
              borderRadius: 2,
              bgcolor: '#fff8eb',
              border: '1px solid #ead09a',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: { xs: 0.25, md: 0.8 },
            }}
          >
            <Typography sx={{ color: '#7a5200', fontSize: 11.5, fontWeight: 950, flex: '0 0 auto' }}>
              진단 보류 {dataInvalidActionCount}건
            </Typography>
            <Typography sx={{ color: '#806327', fontSize: 10.9, lineHeight: 1.55 }}>
              {blockingValidationIssues.length
                ? '수량·실시간 시세 등 필수 항목을 확인하면 다시 진단할 수 있습니다. 각 종목의 확인 필요 항목과 조치를 확인하세요.'
                : '서버가 필수 데이터 부족으로 판단을 보류했습니다. 보유 수량과 실시간 시세를 확인한 뒤 다시 진단하세요.'}
            </Typography>
          </Box>
        ) : null}

        {!holdings.length ? (
          <Box
            sx={{
              p: 1.4,
              borderRadius: 2.2,
              bgcolor: '#f5f8fb',
              border: '1px dashed #c9d5df',
              textAlign: 'center',
            }}
          >
            <Typography sx={{ color: TXT, fontSize: 12.5, fontWeight: 900 }}>
              분석할 보유 종목이 없습니다.
            </Typography>
            <Typography
              sx={{ color: SUB2, fontSize: 11.3, mt: 0.35, lineHeight: 1.55 }}
            >
              보유 종목을 등록하면 종목별 위험도와 대응 우선순위를 표시합니다.
            </Typography>
          </Box>
        ) : priorityActions.length ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              sx={{
                minWidth: isMobile ? 0 : 680,
                display: 'grid',
                gap: 0.55,
              }}
            >
              {!isMobile ? (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '180px 150px minmax(320px,1fr)',
                    gap: 1,
                    px: 1,
                    py: 0.65,
                    color: SUB2,
                    fontSize: 10.3,
                    fontWeight: 950,
                    borderBottom: '1px solid #dfe7ee',
                  }}
                >
                  <span>종목</span>
                  <span>판단</span>
                  <span>이유</span>
                </Box>
              ) : null}

              {priorityActions.slice(0, 12).map((item, idx) => {
                const tone = getActionTone(item);

                return (
                  <Box
                    key={`${item.ticker ?? idx}-${idx}`}
                    sx={{
                      position: 'relative',
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: '180px 150px minmax(320px,1fr)',
                      },
                      gap: { xs: 0.55, md: 1 },
                      alignItems: 'start',
                      px: { xs: 0.9, md: 1 },
                      py: { xs: 0.85, md: 0.9 },
                      borderRadius: 0,
                      bgcolor: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--pi-border-quiet)',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        bgcolor: tone.accent,
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 0, pl: 0.25 }}>
                      <Typography
                        sx={{
                          color: TXT,
                          fontSize: 12.8,
                          fontWeight: 950,
                          letterSpacing: '-.015em',
                        }}
                      >
                        {item.tickerName || item.ticker || '보유 종목'}
                      </Typography>
                      <Typography
                        sx={{
                          color: SUB2,
                          mt: 0.15,
                          fontSize: 10.5,
                          fontWeight: 800,
                        }}
                      >
                        {item.ticker || '-'}
                        {item.sectorName ? ` · ${item.sectorName}` : ''}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={tone.label}
                      sx={{
                        justifySelf: { xs: 'start', md: 'stretch' },
                        height: 'auto',
                        minHeight: 24,
                        bgcolor: tone.bg,
                        border: tone.border,
                        color: tone.color,
                        fontWeight: 950,
                        '& .MuiChip-label': {
                          display: 'block',
                          px: 0.8,
                          py: 0.3,
                          whiteSpace: 'normal',
                          lineHeight: 1.25,
                          textAlign: 'center',
                        },
                      }}
                    />

                    <Typography
                      sx={{
                        color: '#43576a',
                        fontSize: 11.6,
                        lineHeight: 1.65,
                        fontWeight: 650,
                        whiteSpace: 'normal',
                        wordBreak: 'keep-all',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {portfolioActionReason(item, validationIssues)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2.2,
              bgcolor: '#fff8e8',
              border: '1px solid #ecd397',
            }}
          >
            <Typography sx={{ color: '#795000', fontSize: 12, fontWeight: 900 }}>
              아직 종목별 진단 결과가 없습니다.
            </Typography>
            <Typography
              sx={{ color: '#8c6a27', fontSize: 11.2, mt: 0.3, lineHeight: 1.55 }}
            >
              ‘지금 진단’을 실행하거나 자동 진단을 활성화하면 결과가 표시됩니다.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}


type SupportFeedbackSummary = {
  days?: number;
  totalCount?: number;
  quickCount?: number;
  legacyCount?: number;
  quickGuestCount?: number;
  quickMemberCount?: number;
  mainCount?: number;
  panelCount?: number;
  mainManualCount?: number;
  mainExitCount?: number;
  panelManualCount?: number;
  panelCloseCount?: number;
  gotItCount?: number;
  unclearCount?: number;
  notFoundCount?: number;
  reasonJudgmentUnclearCount?: number;
  reasonEvidenceWeakCount?: number;
  reasonDataInsufficientCount?: number;
  reasonTooMuchInfoCount?: number;
  reasonMissingFeatureCount?: number;
  reasonTrustLowCount?: number;
  reasonOtherCount?: number;
  reasonMissingCount?: number;
  legacyHelpfulCount?: number;
  legacyNeutralCount?: number;
  legacyNeedsWorkCount?: number;
};

type SupportBugReportRow = {
  id: number;
  userId?: number | null;
  category?: string | null;
  title?: string | null;
  description?: string | null;
  pagePath?: string | null;
  ticker?: string | null;
  status?: string | null;
  adminNote?: string | null;
  hasScreenshot?: boolean;
  screenshotName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  browserInfo?: string | null;
  frontendVersion?: string | null;
  portfolioContext?: string | null;
  feedbackSource?: string | null;
  feedbackTrigger?: string | null;
  feedbackAnswer?: string | null;
  feedbackReason?: string | null;
  feedbackDwellSeconds?: number | null;
  feedbackContext?: string | null;
  feedbackComment?: string | null;
};

const SUPPORT_CATEGORY_OPTIONS = [
  { value: 'SCREEN', label: '화면 오류' },
  { value: 'DATA', label: '데이터 오류' },
  { value: 'LOGIN', label: '로그인' },
  { value: 'PORTFOLIO', label: '보유종목/포트폴리오' },
  { value: 'ALERT', label: '알림' },
  { value: 'FEEDBACK', label: '사용자 피드백' },
  { value: 'OTHER', label: '기타' },
] as const;

function supportCategoryLabel(category: unknown) {
  const value = String(category ?? '').toUpperCase();
  return SUPPORT_CATEGORY_OPTIONS.find((item) => item.value === value)?.label ?? (value || '기타');
}

function supportStatusLabel(status: unknown) {
  const value = String(status ?? '').toUpperCase();
  if (value === 'IN_PROGRESS') return '확인 중';
  if (value === 'RESOLVED') return '처리 완료';
  if (value === 'REJECTED') return '종료';
  return '접수';
}

function supportStatusSx(status: unknown) {
  const value = String(status ?? '').toUpperCase();
  if (value === 'RESOLVED') return { color: GOOD, bgcolor: SOFT_GOOD, border: '1px solid #b9ddc4' };
  if (value === 'IN_PROGRESS') return { color: BLUE, bgcolor: SOFT_BLUE, border: '1px solid #c9dfeb' };
  if (value === 'REJECTED') return { color: SUB, bgcolor: '#f1f5f9', border: BORDER };
  return { color: AMBER, bgcolor: SOFT_WARN, border: '1px solid #ead9a8' };
}

function formatSupportDate(value: unknown) {
  if (!value) return '-';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getOrCreateBugReportAnonSessionId() {
  if (typeof window === 'undefined') return '';
  const key = 'policyIntel:bugReportAnonSession:v1';
  try {
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const generated = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(key, generated);
    return generated;
  } catch {
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

function supportSummaryNumber(summary: SupportFeedbackSummary | null, key: keyof SupportFeedbackSummary) {
  const value = Number(summary?.[key] ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function supportSummaryPct(count: number, denominator: number) {
  if (denominator <= 0) return '—';
  return `${((count / denominator) * 100).toFixed(1)}%`;
}


type SupportFeedbackView = {
  quick: boolean;
  source: string;
  trigger: string;
  answer: string;
  reason: string;
  dwellSeconds: number;
  context: Record<string, unknown>;
  comment: string;
};

function supportFeedbackLine(description: unknown, marker: string) {
  const text = String(description ?? '');
  const line = text.split(/\r?\n/).find((row) => row.trim().startsWith(marker));
  return line ? line.trim().slice(marker.length).trim() : '';
}

function supportFeedbackTail(description: unknown, marker: string) {
  const text = String(description ?? '');
  const index = text.indexOf(marker);
  return index >= 0 ? text.slice(index + marker.length).trim() : '';
}

function supportFeedbackContext(value: unknown): Record<string, unknown> {
  const raw = String(value ?? '').trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : {};
  } catch {
    return {};
  }
}

function supportFeedbackView(report: SupportBugReportRow): SupportFeedbackView {
  const source = String(report.feedbackSource ?? supportFeedbackLine(report.description, '[피드백 위치]')).trim().toUpperCase();
  const trigger = String(report.feedbackTrigger ?? supportFeedbackLine(report.description, '[트리거]')).trim().toUpperCase();
  const answer = String(report.feedbackAnswer ?? supportFeedbackLine(report.description, '[답변]')).trim().toUpperCase();
  const reason = String(report.feedbackReason ?? supportFeedbackLine(report.description, '[이유]')).trim().toUpperCase();
  const dwellRaw = report.feedbackDwellSeconds ?? supportFeedbackLine(report.description, '[체류 시간]').replace(/[^0-9]/g, '');
  const dwellSeconds = Number(dwellRaw ?? 0);
  const contextRaw = report.feedbackContext ?? supportFeedbackLine(report.description, '[컨텍스트]');
  const comment = String(report.feedbackComment ?? supportFeedbackTail(report.description, '[사용자 의견]')).trim();
  return {
    quick: ['GOT_IT', 'UNCLEAR', 'NOT_FOUND'].includes(answer),
    source,
    trigger,
    answer,
    reason,
    dwellSeconds: Number.isFinite(dwellSeconds) ? dwellSeconds : 0,
    context: supportFeedbackContext(contextRaw),
    comment,
  };
}

function supportFeedbackSourceLabel(value: unknown) {
  const code = String(value ?? '').toUpperCase();
  if (code === 'MAIN') return '메인';
  if (code === 'PANEL') return '패널';
  return '위치 미확인';
}

function supportFeedbackTriggerLabel(value: unknown) {
  const code = String(value ?? '').toUpperCase();
  if (code === 'MANUAL') return '직접 피드백';
  if (code === 'MAIN_EXIT_INTENT') return '메인 이탈 직전';
  if (code === 'PANEL_CLOSE') return '패널 닫기 직전';
  return '트리거 미확인';
}

function supportFeedbackAnswerLabel(value: unknown) {
  const code = String(value ?? '').toUpperCase();
  if (code === 'GOT_IT') return '답을 얻음';
  if (code === 'UNCLEAR') return '애매했음';
  if (code === 'NOT_FOUND') return '답을 못 얻음';
  return '응답 미확인';
}

function supportFeedbackReasonLabel(value: unknown) {
  const code = String(value ?? '').toUpperCase();
  if (code === 'JUDGMENT_UNCLEAR') return '판단이 이해 안 됨';
  if (code === 'EVIDENCE_WEAK') return '근거가 부족함';
  if (code === 'DATA_INSUFFICIENT') return '데이터가 부족함';
  if (code === 'TOO_MUCH_INFO') return '정보가 너무 많음';
  if (code === 'MISSING_FEATURE') return '원하는 기능이 없음';
  if (code === 'TRUST_LOW') return '신뢰하기 어려움';
  if (code === 'OTHER') return '기타';
  return '';
}

function supportFeedbackAnswerSx(value: unknown) {
  const code = String(value ?? '').toUpperCase();
  if (code === 'GOT_IT') return { color: GOOD, bgcolor: SOFT_GOOD, border: '1px solid #b9ddc4' };
  if (code === 'UNCLEAR') return { color: AMBER, bgcolor: SOFT_WARN, border: '1px solid #ead9a8' };
  if (code === 'NOT_FOUND') return { color: RED, bgcolor: '#fff1f1', border: '1px solid #efcaca' };
  return { color: SUB, bgcolor: '#f1f5f9', border: BORDER };
}

function supportFeedbackTitle(report: SupportBugReportRow) {
  const feedback = supportFeedbackView(report);
  if (!feedback.quick) return report.title || '기존 사용자 피드백';
  return `${supportFeedbackSourceLabel(feedback.source)} · ${supportFeedbackTriggerLabel(feedback.trigger)}`;
}

function SupportFeedbackBody({ report }: { report: SupportBugReportRow }) {
  const feedback = supportFeedbackView(report);
  if (!feedback.quick) {
    return (
      <Typography sx={{ color: SUB, fontSize: 11, mt: 0.55, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {report.description}
      </Typography>
    );
  }

  const contextChips: string[] = [];
  if (feedback.context.searchedStock === true) contextChips.push('종목 검색 후');
  if (feedback.context.searchedStock === false) contextChips.push('검색 전');
  if (feedback.context.financialReady === true) contextChips.push('재무 데이터 준비');
  if (feedback.context.financialReady === false) contextChips.push('재무 데이터 미준비');
  if (feedback.context.valueState) contextChips.push(`판단 ${String(feedback.context.valueState)}`);
  if (feedback.context.issueStatus) contextChips.push(`이슈 ${String(feedback.context.issueStatus)}`);

  return (
    <Box sx={{ mt: 0.65, display: 'grid', gap: 0.65 }}>
      <Box sx={{ display: 'flex', gap: 0.45, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip size="small" label={supportFeedbackAnswerLabel(feedback.answer)} sx={{ height: 23, fontWeight: 950, fontSize: 10.1, ...supportFeedbackAnswerSx(feedback.answer) }} />
        {feedback.reason ? (
          <Chip size="small" label={supportFeedbackReasonLabel(feedback.reason)} sx={{ height: 23, fontWeight: 900, fontSize: 10, bgcolor: '#fff', border: BORDER, color: TXT }} />
        ) : feedback.answer !== 'GOT_IT' ? (
          <Chip size="small" label="이유 미선택 · 과거 데이터" sx={{ height: 23, fontWeight: 900, fontSize: 10, bgcolor: '#fff5f5', border: '1px solid #efcaca', color: RED }} />
        ) : null}
        {report.ticker ? <Chip size="small" label={`종목 ${report.ticker}`} sx={{ height: 23, fontWeight: 850, fontSize: 9.8, bgcolor: '#f8fafc', border: BORDER }} /> : null}
      </Box>

      <Typography sx={{ color: SUB2, fontSize: 10.4, lineHeight: 1.5 }}>
        {report.userId != null ? `로그인 사용자 #${report.userId}` : '비로그인 사용자'}
        {feedback.dwellSeconds > 0 ? ` · 체류 ${feedback.dwellSeconds}초` : ''}
        {report.pagePath ? ` · ${report.pagePath}` : ''}
      </Typography>

      {contextChips.length ? (
        <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap' }}>
          {contextChips.map((label) => (
            <Chip key={label} size="small" label={label} sx={{ height: 21, fontSize: 9.4, bgcolor: '#f4f7fa', border: BORDER, color: SUB, fontWeight: 800 }} />
          ))}
        </Box>
      ) : null}

      {feedback.comment ? (
        <Box sx={{ p: 0.8, borderRadius: 1.6, bgcolor: '#fff', border: BORDER }}>
          <Typography sx={{ color: SUB2, fontSize: 9.8, fontWeight: 900 }}>사용자 의견</Typography>
          <Typography sx={{ mt: 0.25, color: TXT, fontSize: 11.2, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {feedback.comment}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

function SupportReportPanel({
  isMobile,
  isAdmin,
  holdings,
  addToast,
}: {
  isMobile: boolean;
  isAdmin: boolean;
  holdings: PortfolioHoldingItem[];
  addToast: (toast: { kind: 'ok' | 'warn' | 'error'; title: string; msg?: string }) => void;
}) {
  const [category, setCategory] = useState('DATA');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeTechnicalInfo, setIncludeTechnicalInfo] = useState(true);
  const [includePortfolioInfo, setIncludePortfolioInfo] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mineLoading, setMineLoading] = useState(false);
  const [myReports, setMyReports] = useState<SupportBugReportRow[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminReports, setAdminReports] = useState<SupportBugReportRow[]>([]);
  const [feedbackSummary, setFeedbackSummary] = useState<SupportFeedbackSummary | null>(null);

  const loadMine = useCallback(async () => {
    setMineLoading(true);
    try {
      const res = await api.get('/support/bug-reports/my', { params: { limit: 30 } });
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.items) ? res.data.items : [];
      setMyReports(rows);
    } catch (e: any) {
      if (!isAxios401(e)) {
        console.error('[support bug reports mine load failed]', e);
      }
    } finally {
      setMineLoading(false);
    }
  }, []);

  const loadAdmin = useCallback(async () => {
    if (!isAdmin) return;
    setAdminLoading(true);
    try {
      const [reportsRes, summaryRes] = await Promise.all([
        api.get('/admin/support/bug-reports', { params: { limit: 100 } }),
        api.get('/admin/support/feedback-summary', { params: { days: 30 } }),
      ]);
      const rows = Array.isArray(reportsRes?.data)
        ? reportsRes.data
        : Array.isArray(reportsRes?.data?.items)
          ? reportsRes.data.items
          : [];
      setAdminReports(rows);
      setFeedbackSummary((summaryRes?.data ?? null) as SupportFeedbackSummary | null);
    } catch (e) {
      console.error('[support bug reports admin load failed]', e);
    } finally {
      setAdminLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadMine();
    if (isAdmin) void loadAdmin();
  }, [isAdmin, loadAdmin, loadMine]);

  const submit = useCallback(async () => {
    const safeTitle = title.trim();
    const safeDescription = description.trim();
    if (!safeTitle) {
      addToast({ kind: 'error', title: '제목을 입력해주세요.' });
      return;
    }
    if (!safeDescription) {
      addToast({ kind: 'error', title: '상세 내용을 입력해주세요.' });
      return;
    }
    if (safeTitle.length > 150 || safeDescription.length > 5000) {
      addToast({ kind: 'error', title: '입력 길이를 확인해주세요.', msg: '제목 150자, 상세 내용 5000자까지 입력할 수 있습니다.' });
      return;
    }
    if (screenshot && screenshot.size > 5 * 1024 * 1024) {
      addToast({ kind: 'error', title: '스크린샷이 너무 큽니다.', msg: '5MB 이하 이미지 파일만 첨부해주세요.' });
      return;
    }

    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('category', category);
      form.append('title', safeTitle);
      form.append('description', safeDescription);
      form.append('pagePath', typeof window === 'undefined' ? '' : `${window.location.pathname}${window.location.search}${window.location.hash}`);
      form.append('ticker', new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get('ticker') ?? '');
      form.append('anonSessionId', getOrCreateBugReportAnonSessionId());
      form.append('includeTechnicalInfo', String(includeTechnicalInfo));
      form.append('includePortfolioInfo', String(includePortfolioInfo));

      if (includeTechnicalInfo && typeof window !== 'undefined') {
        const technical = {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        form.append('browserInfo', JSON.stringify(technical));
        form.append('frontendVersion', String((window as any).__POLICY_INTEL_VERSION__ ?? ''));
      }

      if (includePortfolioInfo) {
        form.append(
          'portfolioContext',
          JSON.stringify(
            holdings.map((item) => ({
              ticker: item.ticker,
              tickerName: item.tickerName,
              quantity: item.quantity,
              avgPrice: item.avgPrice,
            })),
          ),
        );
      }
      if (screenshot) form.append('screenshot', screenshot);

      await api.post('/support/bug-reports', form, {
        // 공통 Axios 인스턴스는 application/json을 기본 Content-Type으로 강제한다.
        // FormData 전송 시에는 이 값을 제거해야 브라우저/Axios가 multipart boundary를 포함한
        // 올바른 Content-Type을 자동 생성한다.
        headers: {
          'Content-Type': null,
        },
      });
      setTitle('');
      setDescription('');
      setScreenshot(null);
      setIncludePortfolioInfo(false);
      addToast({ kind: 'ok', title: '오류 제보가 접수되었습니다.', msg: '처리 상태는 이 화면의 내 제보에서 확인할 수 있습니다.' });
      await loadMine();
      if (isAdmin) await loadAdmin();
    } catch (e: any) {
      addToast({
        kind: 'error',
        title: '오류 제보를 보내지 못했습니다.',
        msg: e?.response?.data?.message ?? e?.response?.data?.detail ?? '잠시 후 다시 시도해주세요.',
      });
    } finally {
      setSubmitting(false);
    }
  }, [addToast, category, description, holdings, includePortfolioInfo, includeTechnicalInfo, isAdmin, loadAdmin, loadMine, screenshot, title]);

  const openAdminScreenshot = useCallback(async (report: SupportBugReportRow) => {
    try {
      const res = await api.get(`/admin/support/bug-reports/${report.id}/screenshot`, { responseType: 'blob' });
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      addToast({ kind: 'error', title: '스크린샷을 열지 못했습니다.', msg: e?.response?.data?.message ?? '첨부 파일 상태를 확인해주세요.' });
    }
  }, [addToast]);

  const updateAdminStatus = useCallback(async (id: number, status: string) => {
    try {
      await api.patch(`/admin/support/bug-reports/${id}`, { status });
      addToast({ kind: 'ok', title: '처리 상태를 변경했습니다.' });
      await Promise.all([loadAdmin(), loadMine()]);
    } catch (e: any) {
      addToast({ kind: 'error', title: '상태 변경에 실패했습니다.', msg: e?.response?.data?.message ?? '잠시 후 다시 시도해주세요.' });
    }
  }, [addToast, loadAdmin, loadMine]);

  return (
    <Box sx={{ display: 'grid', gap: 1.2 }}>
      <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
        <SectionTitle
          title="오류 제보"
          sub="화면·데이터·로그인·보유종목·알림에서 이상한 점을 발견하면 바로 알려주세요."
          isMobile={isMobile}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px 1fr' }, gap: 0.9 }}>
          <TextField
            select
            size="small"
            label="유형"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          >
            {SUPPORT_CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 150 }}
            placeholder="예: 삼성전자 평단이 수량 칸에 표시됩니다."
            fullWidth
          />
        </Box>

        <TextField
          multiline
          minRows={5}
          label="상세 내용"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 5000 }}
          placeholder="어떤 화면에서 무엇을 했을 때 어떤 값이 잘못 보였는지 적어주세요. 재현 순서를 적으면 확인이 빨라집니다."
          fullWidth
          sx={{ mt: 0.9 }}
        />

        <Box sx={{ mt: 0.9, p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
          <FormControlLabel
            control={<Checkbox checked={includeTechnicalInfo} onChange={(e) => setIncludeTechnicalInfo(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: 11.6, color: TXT, fontWeight: 850 }}>현재 화면의 기술정보 함께 보내기</Typography>}
          />
          <Typography sx={{ pl: 4, mt: -0.3, color: SUB, fontSize: 10.8, lineHeight: 1.55 }}>
            페이지 경로, 브라우저 종류, 화면 크기, 시간대만 포함합니다. 이메일·평단·수량은 자동 첨부하지 않습니다.
          </Typography>

          <FormControlLabel
            control={<Checkbox checked={includePortfolioInfo} onChange={(e) => setIncludePortfolioInfo(e.target.checked)} size="small" />}
            label={<Typography sx={{ fontSize: 11.6, color: TXT, fontWeight: 850 }}>현재 보유종목 정보를 오류 확인에 포함</Typography>}
          />
          <Typography sx={{ pl: 4, mt: -0.3, color: SUB, fontSize: 10.8, lineHeight: 1.55 }}>
            선택한 경우에만 종목코드·수량·평균단가가 함께 전달됩니다. 포트폴리오 오류 확인이 필요할 때만 체크하세요.
          </Typography>
        </Box>

        <Box sx={{ mt: 0.9, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, gap: 0.8 }}>
          <Button component="label" variant="outlined" sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none', color: TXT, borderColor: BORDER_STRONG }}>
            스크린샷 선택
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={(e) => setScreenshot(e.target.files?.[0] ?? null)}
            />
          </Button>
          <Typography sx={{ color: screenshot ? TXT : SUB2, fontSize: 11.1, flex: 1 }}>
            {screenshot ? `${screenshot.name} · ${(screenshot.size / 1024 / 1024).toFixed(2)}MB` : '선택사항 · 이미지 5MB 이하'}
          </Typography>
          {screenshot ? (
            <Button size="small" onClick={() => setScreenshot(null)} sx={{ color: SUB, fontWeight: 850, textTransform: 'none' }}>첨부 취소</Button>
          ) : null}
          <Button
            variant="contained"
            startIcon={<BugReportRoundedIcon />}
            disabled={submitting}
            onClick={() => void submit()}
            sx={{ minHeight: 40, px: 1.7, borderRadius: 2, bgcolor: BLUE, fontWeight: 950, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#083e5a', boxShadow: 'none' } }}
          >
            {submitting ? '접수 중...' : '제보 보내기'}
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
        <SectionTitle title="내가 보낸 제보" sub="최근 접수한 오류와 처리 상태를 확인합니다." isMobile={isMobile} />
        {mineLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
        {!myReports.length && !mineLoading ? (
          <Typography sx={{ color: SUB, fontSize: 11.5, py: 1 }}>아직 접수한 오류 제보가 없습니다.</Typography>
        ) : (
          <Stack spacing={0.7}>
            {myReports.map((report) => (
              <Paper key={report.id} elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.8 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip size="small" label={supportCategoryLabel(report.category)} sx={{ height: 20, fontSize: 9, fontWeight: 900, bgcolor: report.category === 'FEEDBACK' ? '#eef4ff' : '#f8fafc', color: report.category === 'FEEDBACK' ? '#245da8' : SUB }} />
                      <Typography sx={{ color: TXT, fontSize: 12.1, fontWeight: 950, wordBreak: 'break-word' }}>{report.title}</Typography>
                    </Box>
                    <Typography sx={{ color: SUB2, fontSize: 10.4, mt: 0.2 }}>{formatSupportDate(report.createdAt)} · #{report.id}</Typography>
                  </Box>
                  <Chip size="small" label={supportStatusLabel(report.status)} sx={{ height: 23, fontWeight: 900, fontSize: 10.3, ...supportStatusSx(report.status) }} />
                </Box>
                {String(report.category ?? '').toUpperCase() === 'FEEDBACK' ? (
                  <SupportFeedbackBody report={report} />
                ) : (
                  <Typography sx={{ color: SUB, fontSize: 11.1, mt: 0.55, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{report.description}</Typography>
                )}
                {report.adminNote ? (
                  <Box sx={{ mt: 0.65, p: 0.8, borderRadius: 1.5, bgcolor: SOFT_BLUE, border: '1px solid #d6e6ef' }}>
                    <Typography sx={{ color: BLUE, fontSize: 10.6, fontWeight: 950 }}>운영 답변</Typography>
                    <Typography sx={{ color: TXT, fontSize: 10.9, mt: 0.2, whiteSpace: 'pre-wrap' }}>{report.adminNote}</Typography>
                  </Box>
                ) : null}
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {isAdmin ? (
        <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
          <SectionTitle title="빠른 피드백 요약" sub="최근 30일 신규 10초 피드백만 집계합니다. 이전 설문형 피드백은 별도 과거 데이터로 분리합니다." isMobile={isMobile} />
          {adminLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
          {feedbackSummary ? (() => {
            const allFeedback = supportSummaryNumber(feedbackSummary, 'totalCount');
            const total = supportSummaryNumber(feedbackSummary, 'quickCount');
            const legacy = supportSummaryNumber(feedbackSummary, 'legacyCount');
            const guest = supportSummaryNumber(feedbackSummary, 'quickGuestCount');
            const member = supportSummaryNumber(feedbackSummary, 'quickMemberCount');
            const gotIt = supportSummaryNumber(feedbackSummary, 'gotItCount');
            const unclear = supportSummaryNumber(feedbackSummary, 'unclearCount');
            const notFound = supportSummaryNumber(feedbackSummary, 'notFoundCount');
            const mainManual = supportSummaryNumber(feedbackSummary, 'mainManualCount');
            const mainExit = supportSummaryNumber(feedbackSummary, 'mainExitCount');
            const panelManual = supportSummaryNumber(feedbackSummary, 'panelManualCount');
            const panelClose = supportSummaryNumber(feedbackSummary, 'panelCloseCount');
            const reasonMissing = supportSummaryNumber(feedbackSummary, 'reasonMissingCount');

            const reasons: Array<[string, number]> = [
              ['판단 이해 어려움', supportSummaryNumber(feedbackSummary, 'reasonJudgmentUnclearCount')],
              ['근거 부족', supportSummaryNumber(feedbackSummary, 'reasonEvidenceWeakCount')],
              ['데이터 부족', supportSummaryNumber(feedbackSummary, 'reasonDataInsufficientCount')],
              ['정보 과다', supportSummaryNumber(feedbackSummary, 'reasonTooMuchInfoCount')],
              ['원하는 기능 없음', supportSummaryNumber(feedbackSummary, 'reasonMissingFeatureCount')],
              ['신뢰 어려움', supportSummaryNumber(feedbackSummary, 'reasonTrustLowCount')],
              ['기타', supportSummaryNumber(feedbackSummary, 'reasonOtherCount')],
              ['이유 미선택(과거)', reasonMissing],
            ];

            return (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, minmax(0, 1fr))' }, gap: 0.6 }}>
                  {[
                    ['빠른 피드백', `${total}건`],
                    ['비로그인', `${guest}건`],
                    ['로그인', `${member}건`],
                    ['답을 얻음', supportSummaryPct(gotIt, total)],
                    ['애매했음', supportSummaryPct(unclear, total)],
                    ['답을 못 얻음', supportSummaryPct(notFound, total)],
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ p: 0.8, borderRadius: 1.7, background: CARD, border: BORDER }}>
                      <Typography sx={{ color: SUB2, fontSize: 9.5, fontWeight: 850 }}>{label}</Typography>
                      <Typography sx={{ mt: 0.15, color: TXT, fontSize: 15, fontWeight: 950 }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 0.75, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1.3fr' }, gap: 0.65 }}>
                  <Box sx={{ p: 0.85, borderRadius: 1.7, background: '#f8fafc', border: BORDER }}>
                    <Typography sx={{ color: TXT, fontSize: 10.6, fontWeight: 950 }}>어디에서 들어왔나</Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.45 }}>
                      <Chip size="small" label={`메인 직접 ${mainManual}`} />
                      <Chip size="small" label={`메인 이탈 ${mainExit}`} />
                      <Chip size="small" label={`패널 직접 ${panelManual}`} />
                      <Chip size="small" label={`패널 닫기 ${panelClose}`} />
                    </Box>
                  </Box>

                  <Box sx={{ p: 0.85, borderRadius: 1.7, background: '#f8fafc', border: BORDER }}>
                    <Typography sx={{ color: TXT, fontSize: 10.6, fontWeight: 950 }}>애매하거나 답을 못 얻은 이유</Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.45 }}>
                      {reasons.map(([label, count]) => (
                        <Chip key={label} size="small" label={`${label} ${count}`} sx={count > 0 ? { fontWeight: 900, bgcolor: '#fff8e8', border: '1px solid #ead9a8' } : undefined} />
                      ))}
                    </Box>
                  </Box>
                </Box>

                {legacy > 0 ? (
                  <Typography sx={{ mt: 0.65, color: SUB2, fontSize: 10.3, lineHeight: 1.5 }}>
                    최근 30일 FEEDBACK 원본은 총 {allFeedback}건이며, 이 중 기존 설문형 {legacy}건은 위 신규 비율 계산에서 제외했습니다.
                  </Typography>
                ) : null}
              </>
            );
          })() : (
            <Typography sx={{ color: SUB, fontSize: 11.2, py: 0.7 }}>
              아직 집계할 빠른 피드백이 없습니다.
            </Typography>
          )}
        </Paper>
      ) : null}

      {isAdmin ? (
        <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
          <SectionTitle title="전체 오류·피드백" sub="운영자 전용 큐입니다. 사용자 피드백과 신규·확인 중 오류 제보를 함께 확인합니다." isMobile={isMobile} />
          {adminLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
          <Stack spacing={0.7}>
            {adminReports.map((report) => (
              <Paper key={report.id} elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 0.8, justifyContent: 'space-between' }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                      <Chip size="small" label={supportCategoryLabel(report.category)} sx={{ height: 22, fontSize: 9.5, fontWeight: 950, bgcolor: report.category === 'FEEDBACK' ? '#eef4ff' : '#f8fafc', color: report.category === 'FEEDBACK' ? '#245da8' : SUB, border: report.category === 'FEEDBACK' ? '1px solid #b9cdef' : BORDER }} />
                      <Typography sx={{ color: TXT, fontSize: 12.1, fontWeight: 950 }}>{String(report.category ?? '').toUpperCase() === 'FEEDBACK' ? supportFeedbackTitle(report) : report.title}</Typography>
                      <Chip size="small" label={supportStatusLabel(report.status)} sx={{ height: 22, fontWeight: 900, fontSize: 10, ...supportStatusSx(report.status) }} />
                      <Typography sx={{ color: SUB2, fontSize: 10.2 }}>#{report.id} · user {report.userId ?? 'anonymous'}</Typography>
                    </Box>
                    {String(report.category ?? '').toUpperCase() === 'FEEDBACK' ? (
                      <SupportFeedbackBody report={report} />
                    ) : (
                      <Typography sx={{ color: SUB, fontSize: 11, mt: 0.45, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{report.description}</Typography>
                    )}
                    <Typography sx={{ color: SUB2, fontSize: 10.2, mt: 0.45, wordBreak: 'break-all' }}>{report.pagePath || '-'} {report.hasScreenshot ? `· 첨부 ${report.screenshotName || '스크린샷'}` : ''}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'row', md: 'column' }} spacing={0.5} sx={{ minWidth: { md: 110 } }}>
                    <Button size="small" variant="outlined" onClick={() => void updateAdminStatus(report.id, 'IN_PROGRESS')} sx={{ fontWeight: 850, textTransform: 'none' }}>확인 중</Button>
                    <Button size="small" variant="outlined" onClick={() => void updateAdminStatus(report.id, 'RESOLVED')} sx={{ fontWeight: 850, textTransform: 'none', color: GOOD, borderColor: '#b9ddc4' }}>처리 완료</Button>
                    {report.hasScreenshot ? (
                      <Button size="small" variant="text" onClick={() => void openAdminScreenshot(report)} sx={{ fontWeight: 850, textTransform: 'none' }}>스크린샷</Button>
                    ) : null}
                  </Stack>
                </Box>
              </Paper>
            ))}
          </Stack>
        </Paper>
      ) : null}
    </Box>
  );
}

type TabKey =
  | 'overview'
  | 'portfolio'
  | 'alerts'
  | 'coverage'
  | 'billing'
  | 'account'
  | 'support'
  | 'adminUsers';

type InterestCoverageItem = {
  themeNumber: string;
  themeKey: string;
  themeName: string;
};

const MyPage: React.FC = () => {
  const dispatch = useDispatch();
  const smartNavigate = useSmartNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();

  const {
    profile,
    accountOpen,
    passwordEditOpen,
    withdrawOpen,
    emailChangeOpen,
    savingAccount,
    changingPassword,
    withdrawing,
    changingEmail,
    successMessage,
    errorMessage,
    socialLoadingProvider,
    openAccountSettings,
    closeAccountSettings,
    openPasswordEdit,
    closePasswordEdit,
    openWithdrawConfirm,
    closeWithdrawConfirm,
    openEmailChange,
    closeEmailChange,
    saveAccountSettings,
    changePassword,
    changeEmail,
    connectSocialAccount,
    disconnectSocialAccount,
    confirmWithdraw,
  } = useAccountSettings();

  const [me, setMe] = useState<UserProfileDto | null>(null);
  const muiTheme = useTheme();
  const mdDown = useMediaQuery(muiTheme.breakpoints.down('md'));
  const routeIsMobile = /^\/m(?:\/|$)/.test(location.pathname);
  const isMobile = routeIsMobile || mdDown;
  const { toasts, addToast } = useToasts();

  const { subscription: subscriptionRedux, error: myPageError } = useSelector((s: RootState) => s.myPage);


  const authRole = useSelector((s: RootState) => (s as any)?.auth?.user?.role ?? (s as any)?.auth?.role ?? null);
  const authUserEmail = useSelector((s: RootState) => (s as any)?.auth?.user?.email ?? (s as any)?.auth?.email ?? '');
  const userId = useSelector((s: RootState) => (s as any)?.auth?.user?.id ?? (s as any)?.auth?.userId ?? (s as any)?.auth?.id ?? undefined);
  const authAccessToken = useSelector(
    (s: RootState) =>
      (s as any)?.auth?.accessToken ??
      (s as any)?.auth?.access_token ??
      (s as any)?.auth?.token ??
      (s as any)?.auth?.jwt ??
      (s as any)?.auth?.user?.accessToken ??
      (s as any)?.auth?.user?.access_token ??
      '',
  );

  const resolvedUserId = useMemo(() => {
    const id = (me as any)?.id ?? userId;
    return id != null ? Number(id) : undefined;
  }, [me, userId]);

  const [statusUser, setStatusUser] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [userEmail, setUserEmail] = useState('');
  const [, setInterests] = useState<InterestCoverageItem[]>([]);
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  const [reloading, setReloading] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [sectorSurgeStatus, setSectorSurgeStatus] = useState<SectorSurgeAlertStatus | null>(null);
  const [sectorSurgePreview, setSectorSurgePreview] = useState<SectorSurgeAlertPreview | null>(null);
  const [sectorSurgeLoading, setSectorSurgeLoading] = useState(false);
  const [sectorSurgeAction, setSectorSurgeAction] = useState<string | null>(null);
  const [closeBetStatus, setCloseBetStatus] = useState<CloseBetAlertStatus | null>(null);
  const [, setCloseBetPreview] = useState<CloseBetAlertPreview | null>(null);
  const [, setCloseBetLoading] = useState(false);
  const [, setCloseBetAction] = useState<string | null>(null);
  const [telegramConnection, setTelegramConnection] = useState<TelegramConnectionState | null>(null);
  const [telegramConnectionOwnerId, setTelegramConnectionOwnerId] = useState<number | null>(null);
  const [telegramConnectUrl, setTelegramConnectUrl] = useState('');
  const [telegramLinkAction, setTelegramLinkAction] = useState<'connect' | 'waiting' | 'brief' | 'realtime' | 'disconnect' | null>(null);

  // Telegram 설정 버튼의 권위는 반드시 '현재 로그인 사용자'에 대해 서버가 반환한 connect/status 응답이다.
  // OAuth 계정 전환 직후 이전 사용자의 telegramConnection state가 잠깐 남더라도 linked=true로 재사용하지 않는다.
  // subscriptionRedux.telegramLinked는 보조/legacy 상태이므로 Telegram 설정 authority로 사용하지 않는다.
  const telegramConnectionLinked =
    resolvedUserId != null
    && telegramConnectionOwnerId === Number(resolvedUserId)
    && telegramConnection?.linked === true;
  const telegramPollTimerRef = useRef<number | null>(null);

  const telegramBotUsername = useMemo(() => {
    if (!telegramConnectUrl) return 'stockleekm_bot';
    try {
      const parsed = new URL(telegramConnectUrl);
      return parsed.pathname.split('/').filter(Boolean)[0] || 'stockleekm_bot';
    } catch {
      return 'stockleekm_bot';
    }
  }, [telegramConnectUrl]);

  const telegramStartCommand = useMemo(() => {
    if (!telegramConnectUrl) return '';
    try {
      const parsed = new URL(telegramConnectUrl);
      const token = String(parsed.searchParams.get('start') ?? '').trim();
      return token ? `/start ${token}` : '';
    } catch {
      return '';
    }
  }, [telegramConnectUrl]);
  const [portfolioText, setPortfolioText] = useState(() => {
    try {
      return localStorage.getItem('portfolioHoldingsText:v1') || '';
    } catch {
      return '';
    }
  });
  const [portfolioRiskMode, setPortfolioRiskMode] = useState<PortfolioRiskMode>(() => {
    try {
      const saved = localStorage.getItem('portfolioRiskMode:v1') as PortfolioRiskMode | null;
      return saved === 'conservative' || saved === 'aggressive' || saved === 'balanced' ? saved : 'balanced';
    } catch {
      return 'balanced';
    }
  });
  const [portfolioAutoManageEnabled, setPortfolioAutoManageEnabled] = useState(() => {
    try {
      return localStorage.getItem('portfolioAutoManageEnabled:v1') === '1';
    } catch {
      return false;
    }
  });
  const [portfolioStrategyPreview, setPortfolioStrategyPreview] = useState<PortfolioStrategyPreview | null>(null);
  const [portfolioAction, setPortfolioAction] = useState<string | null>(null);
  const [portfolioDeletingTicker, setPortfolioDeletingTicker] = useState<string | null>(null);
  const portfolioStrategyRequestSeqRef = useRef(0);
  const portfolioStrategyRunningRef = useRef(false);
  const portfolioLastAutoDiagnosisAtRef = useRef(0);
  const portfolioHoldingsLoadingRef = useRef(false);
  const portfolioHoldingsLoadKeyRef = useRef('');
  const portfolioHoldingsRequestSeqRef = useRef(0);
  const [, setPortfolioHoldingsLoading] = useState(false);
  const [, setPortfolioHoldingsLoaded] = useState(false);

  const hasKnownUser = useMemo(() => Boolean(userEmail || me?.email || authUserEmail), [authUserEmail, me?.email, userEmail]);

  const requireAuth = useCallback((forceOpen = false) => {
    localStorage.setItem('postLoginRedirect', `${location.pathname}${location.search}`);

    if (forceOpen || !hasKnownUser) {
      setStatusUser('error');
      setAuthOpen(true);
      return;
    }

    setAuthOpen(false);
    addToast({ kind: 'warn', title: '세션 확인 필요', msg: '일부 요청이 만료되었습니다. 새로고침 후 다시 시도해 주세요.' });
  }, [addToast, hasKnownUser, location.pathname, location.search]);

  const authenticatedRequestConfig = useMemo(() => {
    const token = normalizeBearerToken(authAccessToken || readStoredAccessToken());

    return {
      withCredentials: true,
      headers: token
        ? {
            Authorization: `Bearer ${token}`,
          }
        : undefined,
    };
  }, [authAccessToken]);

  const hasRequestAuthentication = useMemo(
    () => Boolean(normalizeBearerToken(authAccessToken || readStoredAccessToken())),
    [authAccessToken],
  );

  const isAdmin = useMemo(() => String(me?.role ?? authRole ?? '').toUpperCase() === 'ADMIN', [me?.role, authRole]);
  const isPro = !!subscriptionRedux?.active;
  const isCancelReserved = !!subscriptionRedux?.cancelAtPeriodEnd;
  const canStartPayment = !isPro || isCancelReserved;
  const canCancelSubscription = isPro && !isCancelReserved;

  const connectedProviders = useMemo(
    () => ({
      google: Boolean((profile as any)?.googleConnected ?? (me as any)?.googleConnected),
      kakao: Boolean((profile as any)?.kakaoConnected ?? (me as any)?.kakaoConnected),
      naver: Boolean((profile as any)?.naverConnected ?? (me as any)?.naverConnected),
    }),
    [profile, me],
  );

  const displayAccountEmail = String(
    userEmail || me?.email || profile?.email || '',
  ).trim();

  const displayAccountName = String(
    (profile as any)?.nickname ??
    (profile as any)?.displayName ??
    (profile as any)?.name ??
    (me as any)?.nickname ??
    (me as any)?.displayName ??
    (me as any)?.name ??
    '',
  ).trim() || displayAccountEmail || '내 계정';

  const visibleTabs: { key: TabKey; label: string; icon: React.ReactNode }[] = useMemo(() => {
    const base = [
      { key: 'overview' as TabKey, label: '홈', icon: <AutoGraphRoundedIcon sx={{ fontSize: 16 }} /> },
      { key: 'portfolio' as TabKey, label: '내 포트폴리오', icon: <AutoGraphRoundedIcon sx={{ fontSize: 16 }} /> },
      { key: 'alerts' as TabKey, label: '알림·모니터', icon: <CampaignRoundedIcon sx={{ fontSize: 16 }} /> },
      { key: 'account' as TabKey, label: '계정·보안', icon: <SettingsRoundedIcon sx={{ fontSize: 16 }} /> },
      { key: 'support' as TabKey, label: '오류 제보', icon: <BugReportRoundedIcon sx={{ fontSize: 16 }} /> },
    ];

    if (isAdmin) {
      base.push({ key: 'adminUsers' as TabKey, label: '운영', icon: <AdminPanelSettingsRoundedIcon sx={{ fontSize: 16 }} /> });
    }
    return base;
  }, [isAdmin]);

  const validateTab = useCallback(
    (v: any): TabKey => {
      const requested = String(v ?? '').trim() as TabKey;

      // 과거 링크/로컬 저장값과 호환: 분리 탭을 새 통합 탭으로 안전하게 흡수한다.
      const x: TabKey =
        requested === 'coverage'
          ? 'alerts'
          : requested === 'billing'
            ? 'account'
            : requested;

      return visibleTabs.some((t) => t.key === x) ? x : 'overview';
    },
    [visibleTabs],
  );

  const tab: TabKey = useMemo(
    () => validateTab(sp.get('tab') || localStorage.getItem('mypage:lastTab')),
    [sp, validateTab],
  );

  const trackMyPageEvent = useCallback(
    (eventName: string, params?: Record<string, any>) => {
      trackEvent(eventName, { screen_name: 'my_page', tab, is_mobile: isMobile, is_pro: Boolean(subscriptionRedux?.active), is_admin: isAdmin, ...params });
    },
    [tab, isMobile, subscriptionRedux?.active, isAdmin],
  );

  useEffect(() => {
    trackPageView('my_page', { tab, is_mobile: isMobile, is_admin: isAdmin });
  }, [tab, isMobile, isAdmin]);

  const handleChangeTab = useCallback(
    (nt: TabKey) => {
      trackEvent('my_page_tab_click', { screen_name: 'my_page', current_tab: tab, next_tab: nt, is_mobile: isMobile });
      const nx = new URLSearchParams(sp);
      nx.set('tab', nt);
      setSp(nx, { replace: true });
      localStorage.setItem('mypage:lastTab', nt);
    },
    [sp, setSp, tab, isMobile],
  );

  const handleLogin = useCallback((form: { email: string; password: string }) => dispatch(loginRequest(form)), [dispatch]);

  const handleSignup = useCallback(
    (form: any) => {
      if (form.password !== form.confirmPassword) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }

      dispatch(
        signupRequest({
          email: form.email,
          password: form.password,
          phone: form.phone ?? '',

          agreeTerms: form.termsAgree,
          agreePrivacy: form.privacyAgree,
          agreeDisclaimer: form.disclaimerAgree,
          ageOver14Agree: form.age14PlusAgree,

          marketingEmailAgree: form.marketingEmailAgree,
          marketingSmsAgree: form.marketingSmsAgree,

          consentVersions: {
            terms: form.consentVersions.terms,
            privacy: form.consentVersions.privacy,
            disclaimer: form.consentVersions.disclaimer,
            ageOver14: form.consentVersions.ageOver14,
            marketing: form.consentVersions.marketing,
          },

          turnstileToken: form.turnstileToken,
        }),
      );
    },
    [dispatch],
  );

  const loadUser = useCallback(async () => {
    setStatusUser('loading');
    try {
      const user = await getUserInfo();
      setMe(user ?? null);
      if (!user?.email) {
        setUserEmail('');
        setStatusUser('error');
        requireAuth(true);
        return;
      }
      setUserEmail(user.email);
      setAuthOpen(false);
      const nextUserId = (user as any)?.id ?? userId;
      if ((user as any).subscriptionStatus) dispatch(fetchSubscriptionStatusSuccess((user as any).subscriptionStatus as SubscriptionStatusDto));
      if (nextUserId != null) dispatch(fetchSubscriptionStatusRequest({ userId: Number(nextUserId) }));
      setStatusUser('success');
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      setStatusUser('error');
    }
  }, [dispatch, requireAuth, userId]);

  const loadBilling = useCallback(async () => {
    if (!BILLING_UI_ENABLED) {
      setPayments([]);
      return;
    }
    try {
      const paymentData: any = await getPaymentHistory();
      const arr: PaymentDto[] = Array.isArray(paymentData)
        ? paymentData
        : Array.isArray(paymentData?.payments)
          ? paymentData.payments
          : Array.isArray(paymentData?.list)
            ? paymentData.list
            : Array.isArray(paymentData?.data)
              ? paymentData.data
              : Array.isArray(paymentData?.result)
                ? paymentData.result
                : [];
      setPayments(arr);
    } catch (e: any) {
      if (isAxios401(e)) {
        setPayments([]);
        return;
      }
      setPayments([]);
    }
  }, []);

  const loadCoverage = useCallback(async () => {
    // 기존 테마 기반 관심상품은 제거되었고, 이 탭은 시장 알림 설정으로 전환되었다.
    // 과거 로컬 캐시가 남아 있어도 화면에 노출하지 않도록 초기화만 수행한다.
    setInterests([]);
    localStorage.removeItem('interestThemes:v2');
    localStorage.removeItem('interestThemes');
  }, []);

  const loadSectorSurgeStatus = useCallback(async () => {
    setSectorSurgeAction('load');
    setSectorSurgeLoading(true);
    try {
      const res = await api.get('/alerts/sector-surge/subscription');
      setSectorSurgeStatus((res?.data ?? null) as SectorSurgeAlertStatus | null);
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      setSectorSurgeStatus(null);
    } finally {
      setSectorSurgeLoading(false);
      setSectorSurgeAction(null);
    }
  }, [requireAuth]);

  const handlePreviewSectorSurge = useCallback(async () => {
    setSectorSurgeAction('preview');
    setSectorSurgeLoading(true);
    try {
      const res = await api.get('/alerts/sector-surge/preview');
      setSectorSurgePreview((res?.data ?? null) as SectorSurgeAlertPreview | null);
      addToast({ kind: 'ok', title: '섹터 등락 미리보기 갱신' });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '섹터 등락 미리보기 실패', msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.' });
    } finally {
      setSectorSurgeLoading(false);
      setSectorSurgeAction(null);
    }
  }, [addToast, requireAuth]);

  const handleSubscribeSectorSurge = useCallback(async () => {
    if (!subscriptionRedux?.telegramLinked) {
      addToast({
        kind: 'warn',
        title: '텔레그램 연결이 필요합니다.',
        msg: '계정/보안에서 텔레그램을 먼저 연결한 뒤 시장 급변 알림을 신청해 주세요.',
      });
      handleChangeTab('account');
      return;
    }

    setSectorSurgeAction('subscribe');
    setSectorSurgeLoading(true);
    try {
      const payload = {
        minSectorChangePct: sectorSurgeStatus?.minSectorChangePct ?? 1.0,
        topStockCount: sectorSurgeStatus?.topStockCount ?? 3,
        cooldownMinutes: sectorSurgeStatus?.cooldownMinutes ?? 30,
      };

      const res = await api.post('/alerts/sector-surge/subscribe', payload);
      setSectorSurgeStatus((res?.data ?? null) as SectorSurgeAlertStatus | null);
      addToast({
        kind: 'ok',
        title: '시장 급변 알림 신청 완료',
        msg: '연결된 텔레그램으로 신청한 Sector Surge 알림만 전송됩니다.',
      });
      void handlePreviewSectorSurge();
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({
        kind: 'error',
        title: '시장 급변 알림 신청 실패',
        msg: e?.response?.data?.message || e?.message || '텔레그램 연결 상태를 확인해 주세요.',
      });
    } finally {
      setSectorSurgeLoading(false);
      setSectorSurgeAction(null);
    }
  }, [
    addToast,
    handleChangeTab,
    handlePreviewSectorSurge,
    requireAuth,
    sectorSurgeStatus?.cooldownMinutes,
    sectorSurgeStatus?.minSectorChangePct,
    sectorSurgeStatus?.topStockCount,
    subscriptionRedux?.telegramLinked,
  ]);

  const handleUnsubscribeSectorSurge = useCallback(async () => {
    if (!window.confirm('실시간 섹터 등락 텔레그램 알림을 중단할까요?')) return;

    setSectorSurgeAction('unsubscribe');
    setSectorSurgeLoading(true);
    try {
      const res = await api.post('/alerts/sector-surge/unsubscribe');
      setSectorSurgeStatus((res?.data ?? null) as SectorSurgeAlertStatus | null);
      addToast({ kind: 'ok', title: '섹터 등락 알림 중단 완료' });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '섹터 등락 알림 중단 실패', msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.' });
    } finally {
      setSectorSurgeLoading(false);
      setSectorSurgeAction(null);
    }
  }, [addToast, requireAuth]);

  const loadCloseBetStatus = useCallback(async () => {
    setCloseBetAction('load');
    setCloseBetLoading(true);
    try {
      const res = await api.get('/alerts/close-bet/subscription');
      setCloseBetStatus((res?.data ?? null) as CloseBetAlertStatus | null);
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      setCloseBetStatus(null);
      addToast({ kind: 'error', title: '종가매매 알림 상태 조회 실패', msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.' });
    } finally {
      setCloseBetLoading(false);
      setCloseBetAction(null);
    }
  }, [addToast, requireAuth]);

  const loadAlertsAndSubs = useCallback(() => {
    dispatch(fetchAlertsRequestedRedux());
    dispatch(fetchSubsRequestedRedux());
  }, [dispatch]);

  const stopTelegramConnectionPolling = useCallback(() => {
    if (telegramPollTimerRef.current != null) {
      window.clearInterval(telegramPollTimerRef.current);
      telegramPollTimerRef.current = null;
    }
  }, []);

  const loadTelegramConnectionStatus = useCallback(async (silent = true) => {
    try {
      const res = await api.get<TelegramConnectionState>('/alerts/telegram/connect/status');
      const next = (res?.data ?? null) as TelegramConnectionState | null;
      setTelegramConnection(next);
      setTelegramConnectionOwnerId(resolvedUserId != null ? Number(resolvedUserId) : null);
      if (next?.linked) setTelegramConnectUrl('');
      return next;
    } catch (e: any) {
      if (isAxios401(e)) {
        if (!silent) requireAuth();
        return null;
      }
      if (!silent) {
        addToast({
          kind: 'error',
          title: 'Telegram 상태 조회 실패',
          msg: e?.response?.data?.message || e?.message || '잠시 후 다시 시도해 주세요.',
        });
      }
      return null;
    }
  }, [addToast, requireAuth, resolvedUserId]);

  const startTelegramConnectionPolling = useCallback(() => {
    stopTelegramConnectionPolling();
    const startedAt = Date.now();

    telegramPollTimerRef.current = window.setInterval(async () => {
      if (Date.now() - startedAt > 2 * 60_000) {
        stopTelegramConnectionPolling();
        setTelegramLinkAction(null);
        addToast({
          kind: 'warn',
          title: 'Telegram 연결 확인이 지연되고 있습니다',
          msg: 'Telegram 앱이 자동으로 열리지 않았다면 MyPage의 연결 도움말에서 Telegram Web 또는 연결 명령 복사를 이용해 주세요. 연결 코드는 발급 후 10분간 유효합니다.',
        });
        return;
      }

      const status = await loadTelegramConnectionStatus(true);
      if (!status?.linked) return;

      stopTelegramConnectionPolling();
      setTelegramLinkAction(null);
      setTelegramConnectUrl('');

      if (resolvedUserId != null) {
        dispatch(fetchSubscriptionStatusRequest({ userId: Number(resolvedUserId) }));
      }
      await Promise.allSettled([loadSectorSurgeStatus(), loadCloseBetStatus()]);
      loadAlertsAndSubs();

      addToast({
        kind: 'ok',
        title: 'Telegram 연결 완료',
        msg: status.morningBriefActive
          ? '오전 8시 장전 브리핑 수신까지 자동으로 활성화되었습니다.'
          : 'Telegram 연결이 완료되었습니다.',
      });
    }, 2_000);
  }, [
    addToast,
    dispatch,
    loadAlertsAndSubs,
    loadCloseBetStatus,
    loadSectorSurgeStatus,
    loadTelegramConnectionStatus,
    resolvedUserId,
    stopTelegramConnectionPolling,
  ]);

  useEffect(() => () => stopTelegramConnectionPolling(), [stopTelegramConnectionPolling]);

  const handleOpenTelegramWeb = useCallback(() => {
    window.open('https://web.telegram.org/', '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenTelegramDesktopHelp = useCallback(() => {
    window.open('https://desktop.telegram.org/', '_blank', 'noopener,noreferrer');
  }, []);

  const handleCopyTelegramStartCommand = useCallback(async () => {
    if (!telegramStartCommand) {
      addToast({
        kind: 'warn',
        title: '연결 명령을 다시 발급해 주세요',
        msg: 'Telegram 연결 버튼을 눌러 새 1회용 연결 코드를 먼저 발급해 주세요.',
      });
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(telegramStartCommand);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = telegramStartCommand;
        textarea.setAttribute('readonly', 'true');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) throw new Error('clipboard copy failed');
      }

      addToast({
        kind: 'ok',
        title: 'Telegram 연결 명령 복사 완료',
        msg: `Telegram Web에서 @${telegramBotUsername} 대화방을 연 뒤 복사한 명령을 한 번 전송해 주세요.`,
      });
    } catch {
      addToast({
        kind: 'error',
        title: '연결 명령 복사 실패',
        msg: '브라우저의 클립보드 권한을 허용한 뒤 다시 시도해 주세요.',
      });
    }
  }, [addToast, telegramBotUsername, telegramStartCommand]);

  const handleConnectTelegram = useCallback(async () => {
    setTelegramLinkAction('connect');
    try {
      const res = await api.post<TelegramConnectionState>('/alerts/telegram/connect', {
        subscribeMorningBrief: true,
      });
      const next = (res?.data ?? null) as TelegramConnectionState | null;
      setTelegramConnection(next);
      setTelegramConnectionOwnerId(resolvedUserId != null ? Number(resolvedUserId) : null);

      if (next?.linked) {
        setTelegramConnectUrl('');
        if (resolvedUserId != null) {
          dispatch(fetchSubscriptionStatusRequest({ userId: Number(resolvedUserId) }));
        }
        await Promise.allSettled([loadSectorSurgeStatus(), loadCloseBetStatus()]);
        loadAlertsAndSubs();
        addToast({
          kind: 'ok',
          title: '장전 브리핑 신청 완료',
          msg: next.message || 'Telegram 연결이 확인되어 오전 8시 장전 브리핑 수신을 활성화했습니다.',
        });
        setTelegramLinkAction(null);
        return;
      }

      const deepLink = String(next?.deepLink ?? '').trim();
      if (!deepLink) {
        throw new Error('Telegram 연결 링크를 발급받지 못했습니다.');
      }

      setTelegramConnectUrl(deepLink);
      window.open(deepLink, '_blank', 'noopener,noreferrer');
      setTelegramLinkAction('waiting');
      startTelegramConnectionPolling();

      addToast({
        kind: 'ok',
        title: 'Telegram에서 Start를 눌러 주세요',
        msg: 'Chat ID 입력은 필요 없습니다. 앱이 열리지 않으면 MyPage의 연결 도움말에서 Telegram Web 또는 연결 명령 복사를 이용할 수 있습니다.',
      });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      setTelegramLinkAction(null);
      addToast({
        kind: 'error',
        title: 'Telegram 연결 시작 실패',
        msg: e?.response?.data?.message || e?.message || 'Telegram 봇 설정과 webhook 상태를 확인해 주세요.',
      });
    }
  }, [
    addToast,
    dispatch,
    loadAlertsAndSubs,
    loadCloseBetStatus,
    loadSectorSurgeStatus,
    requireAuth,
    resolvedUserId,
    startTelegramConnectionPolling,
  ]);

  const handleSetMorningBriefActive = useCallback(async (active: boolean) => {
    // active=true는 화면 캐시를 신뢰하지 않고 항상 서버 authority를 다시 확인한다.
    // OAuth 계정 전환 직후 이전 사용자의 linked state가 남는 race를 차단한다.
    if (active) {
      const latest = await loadTelegramConnectionStatus(false);
      if (!latest?.linked) {
        addToast({
          kind: 'warn',
          title: 'Telegram 연결이 필요합니다.',
          msg: '현재 로그인 계정의 Telegram 연결을 먼저 완료한 뒤 장전 브리핑을 켜 주세요.',
        });
        return;
      }
    }

    setTelegramLinkAction('brief');
    try {
      const res = await api.post<TelegramConnectionState>('/alerts/telegram/morning-brief/subscription', { active });
      const next = (res?.data ?? null) as TelegramConnectionState | null;
      setTelegramConnection(next);
      setTelegramConnectionOwnerId(resolvedUserId != null ? Number(resolvedUserId) : null);
      addToast({
        kind: 'ok',
        title: active ? '장전 브리핑 수신 켜짐' : '장전 브리핑 수신 꺼짐',
        msg: next?.message || (active ? '매일 오전 8시 장전 브리핑을 받습니다.' : '장전 브리핑 발송을 중지했습니다.'),
      });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({
        kind: 'error',
        title: '장전 브리핑 설정 실패',
        msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.',
      });
    } finally {
      setTelegramLinkAction(null);
    }
  }, [addToast, loadTelegramConnectionStatus, requireAuth, resolvedUserId]);

  const handleSetTelegramRealtimeActive = useCallback(async (active: boolean) => {
    if (active) {
      const latest = await loadTelegramConnectionStatus(false);
      if (!latest?.linked) {
        addToast({
          kind: 'warn',
          title: 'Telegram 연결이 필요합니다.',
          msg: '현재 로그인 계정의 Telegram 연결을 먼저 완료한 뒤 실시간 상태 알림을 켜 주세요.',
        });
        return;
      }
    }

    setTelegramLinkAction('realtime');
    try {
      const res = await api.post<TelegramConnectionState>('/alerts/telegram/realtime/subscription', { active });
      const next = (res?.data ?? null) as TelegramConnectionState | null;
      setTelegramConnection(next);
      setTelegramConnectionOwnerId(resolvedUserId != null ? Number(resolvedUserId) : null);
      addToast({
        kind: 'ok',
        title: active ? '실시간 상태 알림 켜짐' : '실시간 상태 알림 꺼짐',
        msg: next?.message || (active
          ? 'Web Push와 동일한 실시간 상태 알림을 Telegram으로도 받습니다.'
          : '실시간 상태 알림의 Telegram 발송을 중지했습니다.'),
      });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({
        kind: 'error',
        title: '실시간 상태 알림 설정 실패',
        msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.',
      });
    } finally {
      setTelegramLinkAction(null);
    }
  }, [addToast, loadTelegramConnectionStatus, requireAuth, resolvedUserId]);

  const handleDisconnectTelegram = useCallback(async () => {
    if (!window.confirm('Telegram 연결을 해제할까요? 장전 브리핑, 실시간 상태 알림, 기존 Telegram 종목·시장 급변·Close Bet 알림이 비활성화됩니다.')) {
      return;
    }

    stopTelegramConnectionPolling();
    setTelegramLinkAction('disconnect');
    try {
      const res = await api.delete<TelegramConnectionState>('/alerts/telegram/connect');
      setTelegramConnection((res?.data ?? null) as TelegramConnectionState | null);
      setTelegramConnectionOwnerId(resolvedUserId != null ? Number(resolvedUserId) : null);
      setTelegramConnectUrl('');

      if (resolvedUserId != null) {
        dispatch(fetchSubscriptionStatusRequest({ userId: Number(resolvedUserId) }));
      }
      await Promise.allSettled([loadSectorSurgeStatus(), loadCloseBetStatus()]);
      loadAlertsAndSubs();

      addToast({
        kind: 'ok',
        title: 'Telegram 연결 해제 완료',
        msg: res?.data?.message || 'Telegram 발송 구독을 비활성화했습니다.',
      });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({
        kind: 'error',
        title: 'Telegram 연결 해제 실패',
        msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.',
      });
    } finally {
      setTelegramLinkAction(null);
    }
  }, [
    addToast,
    dispatch,
    loadAlertsAndSubs,
    loadCloseBetStatus,
    loadSectorSurgeStatus,
    requireAuth,
    resolvedUserId,
    stopTelegramConnectionPolling,
  ]);

  const handlePreviewCloseBet = useCallback(async () => {
    setCloseBetAction('preview');
    setCloseBetLoading(true);
    try {
      const res = await api.get('/alerts/close-bet/preview');
      setCloseBetPreview((res?.data ?? null) as CloseBetAlertPreview | null);
      addToast({ kind: 'ok', title: '종가매매 후보 조회 완료' });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '종가매매 후보 조회 실패', msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.' });
    } finally {
      setCloseBetLoading(false);
      setCloseBetAction(null);
    }
  }, [addToast, requireAuth]);

  useCallback(async () => {
    if (!subscriptionRedux?.telegramLinked) {
      addToast({
        kind: 'warn',
        title: '텔레그램 연결이 필요합니다.',
        msg: '계정/보안에서 텔레그램을 먼저 연결한 뒤 Close Bet 알림을 신청해 주세요.',
      });
      handleChangeTab('account');
      return;
    }

    setCloseBetAction('subscribe');
    setCloseBetLoading(true);
    try {
      const payload = {
        strategy: closeBetStatus?.strategy || 'NEXT_CLOSE',
        topStockCount: closeBetStatus?.topStockCount ?? 5,
        telegram: true,
        inApp: true,
      };
      const res = await api.post('/alerts/close-bet/subscribe', payload);
      setCloseBetStatus((res?.data ?? null) as CloseBetAlertStatus | null);
      addToast({ kind: 'ok', title: '종가매매 후보 알림 신청 완료', msg: '평일 장 마감 전 후보를 텔레그램으로 받을 수 있습니다.' });
      void handlePreviewCloseBet();
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '종가매매 알림 신청 실패', msg: e?.response?.data?.message || e?.message || '텔레그램 연결 상태를 확인해 주세요.' });
    } finally {
      setCloseBetLoading(false);
      setCloseBetAction(null);
    }
  }, [addToast, closeBetStatus?.strategy, closeBetStatus?.topStockCount, handleChangeTab, handlePreviewCloseBet, requireAuth, subscriptionRedux?.telegramLinked]);

  useCallback(async () => {
    if (!window.confirm('종가매매 후보 텔레그램 알림을 중단할까요?')) return;

    setCloseBetAction('unsubscribe');
    setCloseBetLoading(true);
    try {
      const res = await api.post('/alerts/close-bet/unsubscribe');
      setCloseBetStatus((res?.data ?? null) as CloseBetAlertStatus | null);
      addToast({ kind: 'ok', title: '종가매매 후보 알림 중단 완료' });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '종가매매 알림 중단 실패', msg: e?.response?.data?.message || e?.message || '다시 시도해 주세요.' });
    } finally {
      setCloseBetLoading(false);
      setCloseBetAction(null);
    }
  }, [addToast, requireAuth]);

  useCallback(async () => {
    if (!window.confirm('현재 대기 중인 종가매매 후보 알림을 텔레그램으로 발송할까요?')) return;

    setCloseBetAction('dispatch');
    setCloseBetLoading(true);
    try {
      const res = await api.post('/alerts/close-bet/dispatch-once');
      const sent = Number(res?.data?.sent ?? res?.data?.sentCount ?? res?.data?.sent_count ?? 0);
      addToast({
        kind: sent > 0 ? 'ok' : 'warn',
        title: sent > 0 ? '종가매매 알림 발송 완료' : '발송 대상 없음',
        msg: `발송 건수: ${Number.isFinite(sent) ? sent : 0}`,
      });
      await loadCloseBetStatus();
      await handlePreviewCloseBet();
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '종가매매 알림 발송 실패', msg: e?.response?.data?.message || e?.message || '백엔드 로그를 확인해 주세요.' });
    } finally {
      setCloseBetLoading(false);
      setCloseBetAction(null);
    }
  }, [addToast, handlePreviewCloseBet, loadCloseBetStatus, requireAuth]);

  const loadCoreData = useCallback(async () => {
    const coreTasks: Promise<unknown>[] = [loadUser(), loadBilling(), loadCoverage()];

    if (TELEGRAM_UI_ENABLED) {
      coreTasks.push(loadTelegramConnectionStatus(true), loadSectorSurgeStatus(), loadCloseBetStatus());
    }

    await Promise.allSettled(coreTasks);
    loadAlertsAndSubs();
  }, [
    loadUser,
    loadBilling,
    loadCoverage,
    loadTelegramConnectionStatus,
    loadSectorSurgeStatus,
    loadCloseBetStatus,
    loadAlertsAndSubs,
  ]);

  const refreshAll = useCallback(async () => {
    trackMyPageEvent('my_page_refresh_click', { source: 'header' });
    setReloading(true);
    try {
      await loadCoreData();
      if (resolvedUserId != null) dispatch(fetchSubscriptionStatusRequest({ userId: Number(resolvedUserId) }));
      addToast({ kind: 'ok', title: '새로고침 완료' });
    } catch {
      addToast({ kind: 'error', title: '새로고침 실패', msg: '네트워크 상태를 확인해 주세요.' });
    } finally {
      setReloading(false);
    }
  }, [addToast, dispatch, loadCoreData, resolvedUserId, trackMyPageEvent]);

  const handleReactivateSubscription = useCallback(async () => {
    if (resolvedUserId == null) {
      addToast({ kind: 'error', title: '사용자 정보가 없습니다.' });
      return;
    }
    try {
      await api.post('/billing/reactivate', null, { params: { userId: Number(resolvedUserId) } });
      addToast({ kind: 'ok', title: '해지 예약 취소 완료', msg: '구독이 계속 유지되도록 변경되었습니다.' });
      await refreshAll();
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '해지 예약 취소 실패', msg: e?.response?.data?.message || e?.message || '처리 중 오류가 발생했습니다.' });
    }
  }, [addToast, requireAuth, refreshAll, resolvedUserId]);

  const handleManageSubscription = useCallback(async () => {
    if (resolvedUserId == null) {
      addToast({ kind: 'error', title: '사용자 정보가 없습니다.' });
      return;
    }
    try {
      await api.post('/billing/cancel', null, { params: { userId: Number(resolvedUserId) } });
      addToast({ kind: 'ok', title: '해지 예약 완료', msg: '현재 결제 주기 종료 후 자동 종료됩니다.' });
      await refreshAll();
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'error', title: '해지 예약 실패', msg: e?.response?.data?.message || e?.message || '처리 중 오류가 발생했습니다.' });
    }
  }, [addToast, requireAuth, refreshAll, resolvedUserId]);

  const goSettings = useCallback(async () => {
    openAccountSettings();
  }, [openAccountSettings]);

  const portfolioHoldings = useMemo(() => parsePortfolioHoldings(portfolioText), [portfolioText]);
  const portfolioEffectiveRiskMode = useMemo<PortfolioRiskMode>(() => {
    const recommended = portfolioStrategyPreview?.recommendedRiskMode;
    return portfolioAutoManageEnabled && recommended ? recommended : portfolioRiskMode;
  }, [portfolioAutoManageEnabled, portfolioRiskMode, portfolioStrategyPreview?.recommendedRiskMode]);
  const portfolioRiskConfig = useMemo(() => getPortfolioRiskModeConfig(portfolioEffectiveRiskMode), [portfolioEffectiveRiskMode]);


  const loadPortfolioHoldings = useCallback(async () => {
    if (portfolioHoldingsLoadingRef.current) return;

    const requestSeq = ++portfolioHoldingsRequestSeqRef.current;
    portfolioHoldingsLoadingRef.current = true;
    setPortfolioHoldingsLoading(true);

    try {
      const res = await api.get('/portfolio/holdings', {
        ...authenticatedRequestConfig,
        timeout: 10_000,
        params: {
          // 사용자 식별은 쿼리 파라미터가 아니라 JWT/세션 인증 정보로 처리한다.
          _: Date.now(),
        },
        headers: {
          ...(authenticatedRequestConfig.headers ?? {}),
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (requestSeq !== portfolioHoldingsRequestSeqRef.current) return;

      const normalized = unwrapPortfolioHoldingsPayload(res?.data);
      const nextText = normalized.holdings.map(holdingToInputLine).join('\n');

      console.info('[MyPage portfolio holdings loaded]', {
        status: res?.status,
        count: normalized.holdings.length,
        raw: res?.data,
      });

      setPortfolioText(nextText);
      setPortfolioHoldingsLoaded(true);

      try {
        if (nextText) {
          window.localStorage.setItem('portfolioHoldingsText:v1', nextText);
        } else {
          window.localStorage.removeItem('portfolioHoldingsText:v1');
        }
      } catch {
        // Safari private mode 등 브라우저 저장소 실패는 서버 데이터 표시에 영향을 주지 않는다.
      }

      if (normalized.preferredRiskMode) {
        setPortfolioRiskMode(normalized.preferredRiskMode);
        try {
          window.localStorage.setItem('portfolioRiskMode:v1', normalized.preferredRiskMode);
        } catch {
          // ignore local cache failure
        }
      }

      if (normalized.autoManageEnabled != null) {
        setPortfolioAutoManageEnabled(normalized.autoManageEnabled);
        try {
          window.localStorage.setItem(
            'portfolioAutoManageEnabled:v1',
            normalized.autoManageEnabled ? '1' : '0',
          );
        } catch {
          // ignore local cache failure
        }
      }
    } catch (e: any) {
      if (requestSeq !== portfolioHoldingsRequestSeqRef.current) return;

      console.error('[MyPage portfolio holdings load failed]', {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });

      /*
       * 쿠키 인증이 살아 있어도 Redux 사용자 정보가 늦게 준비될 수 있다.
       * 실패 시 로드 키를 비워 다음 인증 상태 변경 때 다시 조회한다.
       */
      portfolioHoldingsLoadKeyRef.current = '';

      if (isAxios401(e)) {
        setPortfolioHoldingsLoaded(false);
        addToast({
          kind: 'warn',
          title: '로그인이 필요합니다',
          msg:
            e?.response?.data?.message ||
            '로그인 세션이 만료되었거나 인증 정보가 백엔드로 전달되지 않았습니다.',
        });
        requireAuth(true);
        return;
      }

      addToast({
        kind: 'warn',
        title: '보유 종목 조회 실패',
        msg:
          e?.response?.data?.message ||
          '서버 저장 목록을 불러오지 못해 현재 브라우저의 임시 목록을 유지합니다.',
      });
    } finally {
      if (requestSeq === portfolioHoldingsRequestSeqRef.current) {
        portfolioHoldingsLoadingRef.current = false;
        setPortfolioHoldingsLoading(false);
      }
    }
  }, [
    addToast,
    authenticatedRequestConfig,
    requireAuth,
  ]);

  useEffect(() => {
    /*
     * 모바일 Safari는 로그인 쿠키는 존재하지만 Redux/localStorage 토큰 정보가
     * 늦게 복원될 수 있다. 따라서 hasRequestAuthentication만으로 조회를 막지 않고
     * 마이페이지 진입 즉시 쿠키 포함 요청을 시도한다.
     *
     * 이후 사용자 ID·이메일·토큰 준비 상태가 달라지면 새로운 키로 한 번 더 조회한다.
     */
    const loadKey = [
      resolvedUserId ?? '',
      me?.email ?? '',
      authUserEmail ?? '',
      userEmail ?? '',
      hasRequestAuthentication ? 'auth' : 'cookie',
    ].join('|');

    if (portfolioHoldingsLoadKeyRef.current === loadKey) return;

    portfolioHoldingsLoadKeyRef.current = loadKey;
    void loadPortfolioHoldings();
  }, [
    authUserEmail,
    hasRequestAuthentication,
    loadPortfolioHoldings,
    me?.email,
    resolvedUserId,
    userEmail,
  ]);

  const handleChangePortfolioRiskMode = useCallback((mode: PortfolioRiskMode) => {
    setPortfolioRiskMode(mode);
    try {
      localStorage.setItem('portfolioRiskMode:v1', mode);
    } catch {
      // ignore local cache failure
    }
  }, []);

  const handleTogglePortfolioAutoManage = useCallback(() => {
    setPortfolioAutoManageEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('portfolioAutoManageEnabled:v1', next ? '1' : '0');
      } catch {
        // ignore local cache failure
      }
      addToast({
        kind: next ? 'ok' : 'warn',
        title: next ? '시장·업종 자동 조정 활성화' : '직접 선택 모드로 변경',
        msg: next ? '자동 진단 결과의 추천 성향을 알림 정책에 적용합니다.' : '사용자가 선택한 운용 성향만 적용합니다.',
      });
      return next;
    });
  }, [addToast]);

  const handleRefreshPortfolioStrategy = useCallback(async (
    options?: { silent?: boolean; source?: 'MANUAL' | 'AUTO_MARKET' },
  ) => {
    const silent = Boolean(options?.silent);
    const source = options?.source ?? 'MANUAL';

    if (!portfolioHoldings.length) {
      if (!silent) addToast({ kind: 'warn', title: '보유 종목을 먼저 입력하세요.' });
      return;
    }

    if (!hasRequestAuthentication) {
      if (!silent) requireAuth(true);
      return;
    }

    if (portfolioStrategyRunningRef.current) return;

    const requestSeq = ++portfolioStrategyRequestSeqRef.current;
    portfolioStrategyRunningRef.current = true;
    setPortfolioAction(source === 'AUTO_MARKET' ? 'strategy-auto' : 'strategy');

    const payload = {
      preferredRiskMode: portfolioRiskMode,
      preferredRiskModeCode: modeToApiValue(portfolioRiskMode),
      effectiveRiskMode: portfolioEffectiveRiskMode,
      effectiveRiskModeCode: modeToApiValue(portfolioEffectiveRiskMode),
      autoManageEnabled: portfolioAutoManageEnabled,
      holdings: portfolioHoldings,
      source: source === 'AUTO_MARKET'
        ? 'MYPAGE_PORTFOLIO_STRATEGY_AUTO_MARKET'
        : 'MYPAGE_PORTFOLIO_STRATEGY_PREVIEW',
    };

    try {
      const res = await api.post(
        '/portfolio/strategy/preview',
        payload,
        authenticatedRequestConfig,
      );

      if (requestSeq !== portfolioStrategyRequestSeqRef.current) return;

      const preview = normalizePortfolioStrategyPreview(res?.data, portfolioRiskMode);

      // 기존 결과를 먼저 비우지 않고 새 응답이 완성된 뒤 원자적으로 교체한다.
      setPortfolioStrategyPreview(preview);

      if (!silent) {
        addToast({
          kind: 'ok',
          title: '포트폴리오 자동 진단 완료',
          msg: `추천 성향: ${getPortfolioRiskModeConfig(
            preview.recommendedRiskMode ?? portfolioRiskMode,
          ).label}`,
        });
      }
    } catch (e: any) {
      if (requestSeq !== portfolioStrategyRequestSeqRef.current) return;

      if (isAxios401(e)) {
        if (!silent) {
          addToast({
            kind: 'warn',
            title: '로그인 인증이 만료되었습니다.',
            msg: '다시 로그인한 뒤 포트폴리오 자동 진단을 실행해 주세요.',
          });
          requireAuth(true);
        }
        return;
      }

      // 자동 갱신 실패 시 기존 진단을 유지한다.
      if (!silent && !portfolioStrategyPreview) {
        const fallback = buildLocalStrategyPreview(portfolioHoldings, portfolioRiskMode);
        setPortfolioStrategyPreview(fallback);
        addToast({
          kind: 'warn',
          title: '자동 진단 API 연결 대기',
          msg: '/portfolio/strategy/preview 연결 상태를 확인해 주세요.',
        });
      }
    } finally {
      if (requestSeq === portfolioStrategyRequestSeqRef.current) {
        portfolioStrategyRunningRef.current = false;
        setPortfolioAction(null);
      }
    }
  }, [addToast, authenticatedRequestConfig, hasRequestAuthentication, portfolioAutoManageEnabled, portfolioEffectiveRiskMode, portfolioHoldings, portfolioRiskMode, portfolioStrategyPreview, requireAuth]);

  useEffect(() => {
    if (!portfolioAutoManageEnabled) return;
    if (!portfolioHoldings.length) return;
    if (!hasRequestAuthentication) return;

    const runAutoDiagnosis = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isKrxRegularMarketTime()) return;

      const now = Date.now();
      if (
        now - portfolioLastAutoDiagnosisAtRef.current <
        PORTFOLIO_AUTO_DIAGNOSIS_INTERVAL_MS
      ) {
        return;
      }

      // 요청 시작 전에 시각을 기록해 effect 재실행이나 화면 상태 변경으로
      // 자동 진단이 연속 호출되는 것을 방지한다.
      portfolioLastAutoDiagnosisAtRef.current = now;

      void handleRefreshPortfolioStrategy({
        silent: true,
        source: 'AUTO_MARKET',
      });
    };

    // 자동 조정을 켠 상태로 장중 마이페이지에 진입하면 즉시 1회 실행한다.
    runAutoDiagnosis();

    const timer = window.setInterval(
      runAutoDiagnosis,
      PORTFOLIO_AUTO_DIAGNOSIS_INTERVAL_MS,
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') runAutoDiagnosis();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    handleRefreshPortfolioStrategy,
    hasRequestAuthentication,
    portfolioAutoManageEnabled,
    portfolioHoldings.length,
  ]);

  const handleApplyRecommendedPortfolioMode = useCallback(() => {
    const next = portfolioStrategyPreview?.recommendedRiskMode;
    if (!next) {
      addToast({ kind: 'warn', title: '추천 성향이 없습니다.', msg: '먼저 자동 진단을 실행하세요.' });
      return;
    }
    setPortfolioRiskMode(next);
    try {
      localStorage.setItem('portfolioRiskMode:v1', next);
    } catch {
      // ignore local cache failure
    }
    addToast({ kind: 'ok', title: '추천 성향 적용', msg: `${getPortfolioRiskModeConfig(next).label} 기준으로 포트폴리오 정책을 변경했습니다.` });
  }, [addToast, portfolioStrategyPreview?.recommendedRiskMode]);

  const handleAddPortfolioHolding = useCallback(
    (item: PortfolioHoldingItem) => {
      const ticker = normalizeTickerCode(item.ticker);
      if (!ticker) return;

      if (portfolioHoldings.some((h) => h.ticker === ticker)) {
        addToast({ kind: 'warn', title: '이미 추가된 종목입니다.', msg: `${item.tickerName || ticker} (${ticker})` });
        return;
      }

      const nextItem: PortfolioHoldingItem = {
        ticker,
        tickerName: item.tickerName || ticker,
        quantity: item.quantity ?? null,
        avgPrice: item.avgPrice ?? null,
      };
      const base = portfolioText.trim();
      const nextText = `${base ? `${base}
` : ''}${holdingToInputLine(nextItem)}`;
      setPortfolioText(nextText);
      try {
        localStorage.setItem('portfolioHoldingsText:v1', nextText);
      } catch {
        // ignore local cache failure
      }
      addToast({ kind: 'ok', title: '보유 종목 추가', msg: `${nextItem.tickerName} (${nextItem.ticker})` });
    },
    [addToast, portfolioHoldings, portfolioText],
  );

  const handleChangePortfolioHolding = useCallback(
    (
      tickerValue: string,
      patch: Pick<PortfolioHoldingItem, 'quantity' | 'avgPrice'>,
    ) => {
      const ticker = normalizeTickerCode(tickerValue);
      if (!ticker) return;

      const nextHoldings = portfolioHoldings.map((holding) =>
        holding.ticker === ticker
          ? {
              ...holding,
              quantity:
                patch.quantity !== undefined
                  ? patch.quantity
                  : holding.quantity ?? null,
              avgPrice:
                patch.avgPrice !== undefined
                  ? patch.avgPrice
                  : holding.avgPrice ?? null,
            }
          : holding,
      );

      const nextText = nextHoldings.map(holdingToInputLine).join('\n');
      setPortfolioText(nextText);

      try {
        if (nextText) {
          localStorage.setItem('portfolioHoldingsText:v1', nextText);
        } else {
          localStorage.removeItem('portfolioHoldingsText:v1');
        }
      } catch {
        // 브라우저 임시 저장 실패는 서버 저장 버튼 동작에 영향을 주지 않는다.
      }
    },
    [portfolioHoldings],
  );

  const handleDeletePortfolioHolding = useCallback(
    async (tickerValue: string) => {
      const ticker = normalizeTickerCode(tickerValue);
      if (!ticker || portfolioDeletingTicker) return;

      const target = portfolioHoldings.find((h) => h.ticker === ticker);
      const nextHoldings = portfolioHoldings.filter((h) => h.ticker !== ticker);
      const nextText = nextHoldings.map(holdingToInputLine).join('\n');

      setPortfolioDeletingTicker(ticker);
      setPortfolioText(nextText);
      try {
        localStorage.setItem('portfolioHoldingsText:v1', nextText);
      } catch {
        // ignore local cache failure
      }

      const payload = {
        preferredRiskMode: portfolioRiskMode,
        preferredRiskModeCode: modeToApiValue(portfolioRiskMode),
        effectiveRiskMode: portfolioEffectiveRiskMode,
        effectiveRiskModeCode: modeToApiValue(portfolioEffectiveRiskMode),
        autoManageEnabled: portfolioAutoManageEnabled,
        strategyPreview: portfolioStrategyPreview,
        holdings: nextHoldings,
      };

      try {
        await api.post('/portfolio/holdings/bulk', payload, authenticatedRequestConfig);
        addToast({
          kind: 'ok',
          title: '보유 종목 삭제 저장 완료',
          msg: `${target?.tickerName || ticker} (${ticker})를 제거하고 서버에 저장했습니다.`,
        });
      } catch (e: any) {
        if (isAxios401(e)) {
          requireAuth();
          return;
        }
        addToast({
          kind: 'warn',
          title: '화면에서는 삭제됨 · 서버 저장 실패',
          msg: e?.response?.data?.message || '새로고침 시 서버 DB 값이 다시 보일 수 있습니다. 백엔드 /portfolio/holdings/bulk 상태를 확인하세요.',
        });
      } finally {
        setPortfolioDeletingTicker(null);
      }
    },
    [
      addToast,
      authenticatedRequestConfig,
      portfolioAutoManageEnabled,
      portfolioDeletingTicker,
      portfolioEffectiveRiskMode,
      portfolioHoldings,
      portfolioRiskMode,
      portfolioStrategyPreview,
      requireAuth,
    ],
  );

  const handleSavePortfolio = useCallback(async () => {
    const payload = {
      preferredRiskMode: portfolioRiskMode,
      preferredRiskModeCode: modeToApiValue(portfolioRiskMode),
      effectiveRiskMode: portfolioEffectiveRiskMode,
      effectiveRiskModeCode: modeToApiValue(portfolioEffectiveRiskMode),
      autoManageEnabled: portfolioAutoManageEnabled,
      strategyPreview: portfolioStrategyPreview,
      holdings: portfolioHoldings,
    };

    setPortfolioAction('save');

    try {
      localStorage.setItem('portfolioHoldingsText:v1', portfolioText);
      localStorage.setItem('portfolioRiskMode:v1', portfolioRiskMode);
    } catch {
      // ignore local cache failure
    }

    try {
      const res = await api.post('/portfolio/holdings/bulk', payload, authenticatedRequestConfig);
      const normalized = unwrapPortfolioHoldingsPayload(res?.data);
      if (normalized.holdings.length || portfolioHoldings.length === 0) {
        const nextText = normalized.holdings.map(holdingToInputLine).join('\n');
        setPortfolioText(nextText);
        try {
          if (nextText) localStorage.setItem('portfolioHoldingsText:v1', nextText);
          else localStorage.removeItem('portfolioHoldingsText:v1');
        } catch {
          // ignore local cache failure
        }
      }
      await loadPortfolioHoldings();
      addToast({
        kind: 'ok',
        title: '보유 종목 저장 완료',
        msg: `${portfolioHoldings.length}개 종목을 저장하고 서버 데이터로 새로고침했습니다.`,
      });
    } catch (e: any) {
      if (isAxios401(e)) {
        requireAuth();
        return;
      }
      addToast({ kind: 'warn', title: '로컬 저장 완료', msg: '백엔드 포트폴리오 API가 연결되면 서버 저장까지 자동 적용됩니다.' });
    } finally {
      setPortfolioAction(null);
    }
  }, [
    addToast,
    authenticatedRequestConfig,
    loadPortfolioHoldings,
    portfolioAutoManageEnabled,
    portfolioEffectiveRiskMode,
    portfolioHoldings,
    portfolioRiskMode,
    portfolioStrategyPreview,
    portfolioText,
    requireAuth,
  ]);

  useEffect(() => {
    // 로그인 사용자 자체가 바뀌면 이전 계정의 Telegram UI state를 즉시 폐기한다.
    // 이후 loadCoreData/connect/status가 현재 userId 기준 authority를 다시 채운다.
    stopTelegramConnectionPolling();
    setTelegramConnection(null);
    setTelegramConnectionOwnerId(null);
    setTelegramConnectUrl('');
    setTelegramLinkAction(null);
  }, [resolvedUserId, stopTelegramConnectionPolling]);

  useEffect(() => {
    void loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    if (resolvedUserId != null) dispatch(fetchSubscriptionStatusRequest({ userId: Number(resolvedUserId) }));
  }, [dispatch, resolvedUserId]);

  useEffect(() => {
    if (myPageError) {
      addToast({ kind: 'error', title: '처리 실패', msg: myPageError });
      dispatch(clearMyPageError());
    }
  }, [addToast, dispatch, myPageError]);

  useEffect(() => {
    const cur = sp.get('tab');
    if (!cur || !visibleTabs.some((t) => t.key === (cur as any))) {
      const nx = new URLSearchParams(sp);
      nx.set('tab', validateTab(cur));
      setSp(nx, { replace: true });
    }
  }, [visibleTabs, sp, setSp, validateTab]);

  const loading = statusUser === 'loading' && !userEmail;
  const subscriptionDescription = useMemo(() => getSubscriptionDescription(subscriptionRedux, isPro, isCancelReserved), [subscriptionRedux, isPro, isCancelReserved]);

  const summaryStats = useMemo(
    () => ({
      portfolioCount: portfolioHoldings.length,
    }),
    [portfolioHoldings.length],
  );

  const todayTasks = useMemo(() => {
    const tasks: Array<{ title: string; desc: string; tab: TabKey; tone: 'warn' | 'info' | 'ok' }> = [];
    if (!portfolioHoldings.length) {
      tasks.push({ title: '보유 종목을 등록해 주세요', desc: '보유 종목을 등록하면 패널에서 종목별 변화 추적과 위험 판단을 바로 사용할 수 있습니다.', tab: 'portfolio', tone: 'warn' });
    }
    // Telegram/시장 급변 전송 기능은 연동 완료 전까지 오늘 할 일에서 제외한다.
    if (TELEGRAM_UI_ENABLED && !subscriptionRedux?.telegramLinked) {
      tasks.push({ title: '알림 받을 텔레그램을 연결해 주세요', desc: '시장 급변과 보유 종목 알림을 실제로 받으려면 수신 채널 연결이 필요합니다.', tab: 'account', tone: 'warn' });
    }
    if (TELEGRAM_UI_ENABLED && !sectorSurgeStatus?.active) {
      tasks.push({ title: '시장 모니터를 켜 주세요', desc: '상승·하락 업종과 연결 종목을 자동으로 감시할 수 있습니다.', tab: 'alerts', tone: 'info' });
    }
    if (!tasks.length) {
      tasks.push({ title: '필수 설정이 완료되었습니다', desc: '포트폴리오·알림·수신 채널이 모두 준비되었습니다.', tab: 'overview', tone: 'ok' });
    }
    return tasks.slice(0, 4);
  }, [
    portfolioHoldings.length,
    sectorSurgeStatus?.active,
    subscriptionRedux?.telegramLinked,
  ]);

  const paymentRows = useMemo(() => {
    return payments.slice(0, 8).map((p: any, idx) => ({
      id: p.id ?? p.paymentId ?? idx,
      date: p.approvedAt ?? p.createdAt ?? p.paidAt ?? p.date ?? '-',
      amount: formatAmount(p.amount ?? p.totalAmount ?? p.price),
      status: p.status ?? p.paymentStatus ?? '완료',
      product: p.orderName ?? p.productName ?? p.planName ?? 'PolicyIntel 구독',
    }));
  }, [payments]);

  const overviewCards = useMemo(
    () => [
      {
        title: '보유 종목',
        value: `${summaryStats.portfolioCount}개`,
        desc: `${portfolioRiskConfig.label} 기준으로 보유 종목을 관리합니다.`,
        actionLabel: '종목 관리',
        onClick: () => handleChangeTab('portfolio'),
        tone: summaryStats.portfolioCount > 0 ? ('ok' as const) : ('warn' as const),
      },
      {
        title: '종목 변화 추적',
        value: summaryStats.portfolioCount > 0 ? '바로 설정' : '대상 없음',
        desc: '패널에서는 종목별 추적을 즉시 켜고, 여기서는 상승·하락 기준과 수신 채널을 관리합니다.',
        actionLabel: '상세 관리',
        onClick: () => handleChangeTab('alerts'),
        tone: summaryStats.portfolioCount > 0 ? ('ok' as const) : ('default' as const),
      },
      ...(TELEGRAM_UI_ENABLED
        ? [{
            title: '시장 모니터',
            value: sectorSurgeStatus?.active ? '활성' : '대기',
            desc: '시장 급변과 주요 업종 흐름을 자동으로 감시합니다.',
            actionLabel: '모니터 설정',
            onClick: () => handleChangeTab('alerts'),
            tone: sectorSurgeStatus?.active ? ('ok' as const) : ('warn' as const),
          }]
        : []),
      ...(BILLING_UI_ENABLED
        ? [
            {
              title: '구독 상태',
              value: isPro ? 'PRO' : 'FREE',
              desc: isCancelReserved ? '해지 예약 상태입니다.' : isPro ? '프로 플랜이 활성화되어 있습니다.' : '무료 플랜입니다.',
              actionLabel: '결제 관리',
              onClick: () => handleChangeTab('account'),
              tone: isPro ? ('ok' as const) : ('warn' as const),
            },
          ]
        : []),
    ],
    [handleChangeTab, isCancelReserved, isPro, portfolioRiskConfig.label, sectorSurgeStatus?.active, summaryStats.portfolioCount],
  );

  const currentTabCopy = useMemo(() => {
    const copy: Record<TabKey, { eyebrow: string; title: string; desc: string }> = {
      overview: {
        eyebrow: '마이페이지',
        title: '내 투자 설정',
        desc: '오늘 필요한 포트폴리오·알림·시장 모니터 상태를 한눈에 확인합니다.',
      },
      portfolio: {
        eyebrow: '내 투자',
        title: '내 포트폴리오',
        desc: '보유 종목과 운용 기준을 관리합니다. 종목별 변화 추적은 패널에서 바로 실행할 수 있습니다.',
      },
      alerts: {
        eyebrow: '알림 센터',
        title: '알림 상세 관리',
        desc: '종목별 상승·하락 기준과 Web Push·Telegram 수신 채널, 시장 모니터를 관리합니다.',
      },
      coverage: {
        eyebrow: '알림 센터',
        title: '알림·시장 모니터',
        desc: '기존 시장 모니터 링크는 통합 알림 화면으로 연결됩니다.',
      },
      billing: {
        eyebrow: '계정',
        title: '계정·보안',
        desc: '기존 구독/결제 링크는 계정 화면으로 연결됩니다.',
      },
      account: {
        eyebrow: '계정',
        title: '계정·보안',
        desc: '로그인·보안·수신 연결과 계정 정보를 한곳에서 관리합니다.',
      },
      support: {
        eyebrow: '지원',
        title: '오류 제보',
        desc: '화면·데이터·로그인·포트폴리오·알림 오류를 접수하고 처리 상태를 확인합니다.',
      },
      adminUsers: {
        eyebrow: '운영',
        title: '사용자 · 인입 분석',
        desc: 'ADMIN을 제외한 실제 사용자 증가, 유입, 체류와 핵심 기능 행동을 확인합니다.',
      },
    };
    return copy[tab];
  }, [tab]);


  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 1.2, md: 2.2 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 1.1, md: 2 } }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: PANEL, border: BORDER }}>
            <Typography sx={{ color: TXT, fontWeight: 950 }}>마이페이지 데이터를 불러오는 중입니다...</Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: PAGE_BG,
        py: { xs: 0, md: 2.4 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 0, md: 2.2 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '216px minmax(0,1fr)' },
            gap: { xs: 0, lg: 1.5 },
            alignItems: 'start',
          }}
        >
          <Box
            component="aside"
            sx={{
              position: { lg: 'sticky' },
              top: { lg: 18 },
              minHeight: { lg: 'calc(100vh - 36px)' },
              borderRadius: { xs: 0, lg: 2.6 },
              bgcolor: { xs: '#fff', lg: '#071827' },
              color: { xs: TXT, lg: '#fff' },
              overflow: 'hidden',
              borderBottom: { xs: BORDER, lg: 'none' },
              boxShadow: { xs: 'none', lg: '0 18px 44px rgba(7,24,39,.14)' },
            }}
          >
            <Box
              sx={{
                p: { xs: 1.15, lg: 1.7 },
                background: {
                  xs: '#fff',
                  lg: 'radial-gradient(circle at 15% 0%, rgba(20,99,153,.42), transparent 38%), linear-gradient(145deg,#0b3a5b,#071827 72%)',
                },
              }}
            >
              <Stack direction="row" spacing={1.1} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: { xs: SOFT_BLUE, lg: 'rgba(255,255,255,.1)' },
                    color: { xs: BLUE, lg: '#fff' },
                    border: { xs: '1px solid #d7e6ef', lg: '1px solid rgba(255,255,255,.14)' },
                    flex: '0 0 auto',
                  }}
                >
                  <PersonRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ color: { xs: TXT, lg: '#fff' }, fontWeight: 900, fontSize: 13.5 }}
                    noWrap
                  >
                    {displayAccountName}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{ color: SUB2, fontSize: 10.2, mt: 0.2, maxWidth: 128 }}
                    title={displayAccountEmail || undefined}
                  >
                    {displayAccountEmail || '내 계정'}
                  </Typography>
                </Box>
                <IconButton
                  onClick={refreshAll}
                  disabled={reloading}
                  sx={{
                    ml: 'auto',
                    color: { xs: SUB, lg: 'rgba(255,255,255,.7)' },
                    border: { xs: BORDER, lg: '1px solid rgba(255,255,255,.12)' },
                    borderRadius: 1.8,
                    width: 34,
                    height: 34,
                  }}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={0.55} useFlexGap flexWrap="wrap" sx={{ mt: 1.35 }}>
                <Chip
                  size="small"
                  label={isPro ? 'PRO' : 'FREE'}
                  sx={{
                    height: 22,
                    color: { xs: isPro ? '#7a5400' : SUB, lg: isPro ? '#fde68a' : '#cbd5e1' },
                    bgcolor: { xs: isPro ? '#fff5d9' : '#f1f5f9', lg: isPro ? 'rgba(163,106,0,.22)' : 'rgba(255,255,255,.07)' },
                    border: { xs: isPro ? '1px solid #ead59b' : BORDER, lg: isPro ? '1px solid rgba(253,230,138,.3)' : '1px solid rgba(255,255,255,.1)' },
                    fontWeight: 950,
                    fontSize: 10.5,
                  }}
                />
                {TELEGRAM_UI_ENABLED ? (
                  <Chip
                    size="small"
                    label={subscriptionRedux?.telegramLinked ? '알림 연결됨' : '알림 연결 필요'}
                    sx={{
                      height: 22,
                      color: { xs: subscriptionRedux?.telegramLinked ? GOOD : AMBER, lg: subscriptionRedux?.telegramLinked ? '#86efac' : '#fcd34d' },
                      bgcolor: {
                        xs: subscriptionRedux?.telegramLinked ? SOFT_GOOD : SOFT_WARN,
                        lg: subscriptionRedux?.telegramLinked ? 'rgba(46,125,50,.2)' : 'rgba(163,106,0,.18)',
                      },
                      border: { xs: BORDER, lg: '1px solid rgba(255,255,255,.1)' },
                      fontWeight: 850,
                      fontSize: 10.2,
                    }}
                  />
                ) : null}
              </Stack>
            </Box>

            <Box sx={{ px: 1.1, py: 1.15, display: { xs: 'none', lg: 'block' } }}>
              <Typography
                sx={{
                  px: 1,
                  pb: 0.7,
                  color: 'rgba(255,255,255,.35)',
                  fontSize: 9.8,
                  fontWeight: 950,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                }}
              >
                메뉴
              </Typography>

              <Typography sx={{ px: 1, pb: 0.55, color: 'rgba(255,255,255,.28)', fontSize: 9.5, fontWeight: 950 }}>
                투자 관리
              </Typography>
              <Stack spacing={0.35}>
                {visibleTabs.filter((item) => ['overview', 'portfolio', 'alerts'].includes(item.key)).map((item) => {
                  const active = tab === item.key;
                  return (
                    <Button
                      key={item.key}
                      fullWidth
                      startIcon={item.icon}
                      onClick={() => handleChangeTab(item.key)}
                      sx={{
                        minHeight: 42,
                        justifyContent: 'flex-start',
                        px: 1.15,
                        borderRadius: 1.8,
                        color: active ? '#fff' : 'rgba(255,255,255,.58)',
                        bgcolor: active ? 'rgba(125,211,252,.13)' : 'transparent',
                        border: active ? '1px solid rgba(125,211,252,.22)' : '1px solid transparent',
                        boxShadow: active ? 'inset 3px 0 0 #7dd3fc' : 'none',
                        fontSize: 12,
                        fontWeight: active ? 950 : 800,
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: active
                            ? 'rgba(125,211,252,.17)'
                            : 'rgba(255,255,255,.055)',
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Stack>

              <Typography sx={{ px: 1, pt: 1.25, pb: 0.55, color: 'rgba(255,255,255,.28)', fontSize: 9.5, fontWeight: 950 }}>
                내 계정
              </Typography>
              <Stack spacing={0.35}>
                {visibleTabs.filter((item) => ['account', 'support', 'adminUsers'].includes(item.key)).map((item) => {
                  const active = tab === item.key;
                  return (
                    <Button
                      key={item.key}
                      fullWidth
                      startIcon={item.icon}
                      onClick={() => handleChangeTab(item.key)}
                      sx={{
                        minHeight: 42,
                        justifyContent: 'flex-start',
                        px: 1.15,
                        borderRadius: 1.8,
                        color: active ? '#fff' : 'rgba(255,255,255,.58)',
                        bgcolor: active ? 'rgba(125,211,252,.13)' : 'transparent',
                        border: active ? '1px solid rgba(125,211,252,.22)' : '1px solid transparent',
                        boxShadow: active ? 'inset 3px 0 0 #7dd3fc' : 'none',
                        fontSize: 12,
                        fontWeight: active ? 950 : 800,
                        textTransform: 'none',
                        '&:hover': { bgcolor: active ? 'rgba(125,211,252,.17)' : 'rgba(255,255,255,.055)' },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Stack>
            </Box>

            <Box
              sx={{
                display: { xs: 'none', lg: 'block' },
                mx: 1.1,
                mt: 0.2,
                p: 1.2,
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,.09)',
                bgcolor: 'rgba(255,255,255,.035)',
              }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,.38)', fontSize: 10, fontWeight: 900 }}>
                현재 상태
              </Typography>
              <Box sx={{ mt: 0.9, display: 'grid', gap: 0.7 }}>
                {[
                  ['보유 종목', `${summaryStats.portfolioCount}개`],
                  ['변화 추적 대상', `${summaryStats.portfolioCount}종목`],
                  ...(TELEGRAM_UI_ENABLED ? [['시장 모니터', sectorSurgeStatus?.active ? '켜짐' : '꺼짐']] : []),
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                  >
                    <Typography sx={{ color: 'rgba(255,255,255,.46)', fontSize: 10.8 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ color: '#fff', fontSize: 10.8, fontWeight: 900 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: { xs: 'none', lg: 'block' }, p: 1.1, mt: 0.4 }}>
              <Button
                fullWidth
                startIcon={<SettingsRoundedIcon />}
                onClick={goSettings}
                sx={{
                  justifyContent: 'flex-start',
                  minHeight: 40,
                  px: 1.15,
                  borderRadius: 1.8,
                  color: 'rgba(255,255,255,.58)',
                  fontSize: 11.5,
                  fontWeight: 850,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,.05)' },
                }}
              >
                계정 설정
              </Button>
            </Box>
          </Box>

          <Box component="main" sx={{ minWidth: 0, px: { xs: 1.1, md: 0 } }}>
            <Paper
              elevation={0}
              sx={{
                mt: { xs: 0.8, lg: 0 },
                p: { xs: 1.15, md: 1.4 },
                borderRadius: 3,
                background: '#fff',
                border: BORDER,
                boxShadow: '0 5px 18px rgba(15,23,42,.04)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) auto' },
                  gap: 1.4,
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography sx={{ color: SUB2, fontSize: 10.5, fontWeight: 900 }}>
                    {currentTabCopy.eyebrow}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      color: TXT,
                      fontSize: { xs: 20, md: 24 },
                      fontWeight: 950,
                      letterSpacing: '-.04em',
                    }}
                  >
                    {currentTabCopy.title}
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: SUB, fontSize: 11.8, lineHeight: 1.55 }}>
                    {currentTabCopy.desc}
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.7}>
                  {tab === 'overview' ? (
                    <Button
                      variant="contained"
                      startIcon={<AutoGraphRoundedIcon />}
                      onClick={() => handleChangeTab('portfolio')}
                      sx={{ minHeight: 40, px: 1.45, borderRadius: 2, bgcolor: BLUE, color: '#fff', fontWeight: 900, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#083e5a', boxShadow: 'none' } }}
                    >
                      보유 종목 관리
                    </Button>
                  ) : null}
                  {tab === 'portfolio' ? (
                    <Button
                      variant="outlined"
                      onClick={() => smartNavigate(`${routeIsMobile ? '/m' : ''}/realtime/stocks`)}
                      sx={{ minHeight: 40, px: 1.4, borderRadius: 2, color: TXT, borderColor: BORDER_STRONG, fontWeight: 900, textTransform: 'none' }}
                    >
                      실시간 종목 보기
                    </Button>
                  ) : null}
                  {tab === 'alerts' ? (
                    <Button
                      variant="outlined"
                      onClick={() => {
                        loadAlertsAndSubs();
                        if (TELEGRAM_UI_ENABLED) void loadSectorSurgeStatus();
                      }}
                      startIcon={<RefreshRoundedIcon />}
                      sx={{ minHeight: 40, px: 1.4, borderRadius: 2, color: TXT, borderColor: BORDER_STRONG, fontWeight: 900, textTransform: 'none' }}
                    >
                      알림·모니터 새로고침
                    </Button>
                  ) : null}
                  {tab === 'account' ? (
                    <Button variant="contained" startIcon={<SettingsRoundedIcon />} onClick={goSettings} sx={{ minHeight: 40, px: 1.45, borderRadius: 2, bgcolor: BLUE, color: '#fff', fontWeight: 900, textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#083e5a', boxShadow: 'none' } }}>
                      계정 편집
                    </Button>
                  ) : null}
                </Stack>
              </Box>

              {tab === 'overview' ? (
              <Box
                  sx={{
                  mt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2,minmax(0,1fr))',
                    md: 'repeat(4,minmax(0,1fr))',
                  },
                  border: BORDER,
                  borderRadius: 2.2,
                  overflow: 'hidden',
                  bgcolor: '#fff',
                }}
              >
                {[
                  ['보유 종목', `${summaryStats.portfolioCount}개`, summaryStats.portfolioCount > 0],
                  ['변화 추적', summaryStats.portfolioCount > 0 ? '설정 가능' : '대상 없음', summaryStats.portfolioCount > 0],
                  ...(TELEGRAM_UI_ENABLED
                    ? [['시장 모니터', sectorSurgeStatus?.active ? '켜짐' : '꺼짐', Boolean(sectorSurgeStatus?.active)]]
                    : []),
                  ['현재 플랜', isPro ? 'PRO' : 'FREE', isPro],
                ].map(([label, value, ready], index) => (
                  <Box
                    key={String(label)}
                    sx={{
                      p: 1.15,
                      borderLeft: {
                        xs: index % 2 === 0 ? 'none' : BORDER,
                        md: index === 0 ? 'none' : BORDER,
                      },
                      borderTop: {
                        xs: index < 2 ? 'none' : BORDER,
                        md: 'none',
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.7}>
                      <Typography sx={{ color: SUB2, fontSize: 10.5, fontWeight: 850 }}>
                        {label}
                      </Typography>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: ready ? GOOD : AMBER,
                          boxShadow: ready
                            ? '0 0 0 3px rgba(46,125,50,.1)'
                            : '0 0 0 3px rgba(163,106,0,.1)',
                        }}
                      />
                    </Stack>
                    <Typography sx={{ mt: 0.35, color: TXT, fontSize: 15, fontWeight: 950 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              ) : null}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mt: 1.1,
                p: 0.65,
                borderRadius: 2.6,
                background: '#fff',
                border: BORDER,
                display: { xs: 'block', lg: 'none' },
                overflowX: 'auto',
              }}
            >
              <Stack direction="row" spacing={0.55} sx={{ minWidth: 'max-content' }}>
                {visibleTabs.map((item) => {
                  const active = tab === item.key;
                  return (
                    <Button
                      key={item.key}
                      variant={active ? 'contained' : 'text'}
                      startIcon={item.icon}
                      onClick={() => handleChangeTab(item.key)}
                      sx={{
                        minHeight: 38,
                        px: 1.1,
                        borderRadius: 1.8,
                        bgcolor: active ? BLUE : 'transparent',
                        color: active ? '#fff' : SUB,
                        fontSize: 11.2,
                        fontWeight: 900,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': { bgcolor: active ? BLUE : CARD },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Stack>
            </Paper>

            <Box sx={{ mt: 1.1 }}>
        {tab === 'overview' ? (
          <Paper
            elevation={0}
            sx={{
              mb: 1.1,
              p: { xs: 1.25, md: 1.5 },
              borderRadius: 3,
              background: '#fff',
              color: TXT,
              border: BORDER,
              boxShadow: '0 8px 24px rgba(15,23,42,.05)',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ color: TXT, fontSize: 16, fontWeight: 950 }}>
                  오늘 할 일 {todayTasks.filter((task) => task.tone !== 'ok').length}개
                </Typography>
                <Typography sx={{ mt: 0.25, color: SUB, fontSize: 11.5 }}>
                  필요한 설정과 다음 행동만 우선 보여드립니다.
                </Typography>
              </Box>
              <Chip
                size="small"
                label={todayTasks.every((task) => task.tone === 'ok') ? '정상 운영' : '설정 확인 필요'}
                sx={{
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  color: todayTasks.every((task) => task.tone === 'ok') ? GOOD : AMBER,
                  bgcolor: todayTasks.every((task) => task.tone === 'ok') ? SOFT_GOOD : SOFT_WARN,
                  border: BORDER,
                  fontWeight: 900,
                }}
              />
            </Stack>

            <Box sx={{ mt: 1.1, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,minmax(0,1fr))' }, gap: 0.7 }}>
              {todayTasks.map((task, index) => (
                <Button
                  key={`${task.title}-${index}`}
                  onClick={() => handleChangeTab(task.tab)}
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    p: 1.05,
                    borderRadius: 2,
                    color: TXT,
                    bgcolor: '#f8fafc',
                    border: BORDER,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#f1f5f9' },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: task.tone === 'ok' ? GOOD : task.tone === 'warn' ? '#f59e0b' : '#38bdf8' }} />
                      <Typography sx={{ color: TXT, fontSize: 12.5, fontWeight: 950 }}>{task.title}</Typography>
                    </Stack>
                    <Typography sx={{ mt: 0.45, pl: 2, color: SUB, fontSize: 10.8, lineHeight: 1.5 }}>
                      {task.desc}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          </Paper>
        ) : null}

        {tab === 'overview' && (
          <>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.25, md: 1.5 },
                borderRadius: 3,
                background: PANEL,
                border: BORDER,
                boxShadow: '0 5px 18px rgba(15,23,42,.04)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box>
                  <Typography sx={{ color: TXT, fontSize: 16, fontWeight: 950 }}>빠른 설정</Typography>
                  <Typography sx={{ color: SUB2, fontSize: 11.5, mt: 0.25 }}>
                    필요한 항목만 확인하고 바로 설정할 수 있습니다.
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${[
                    summaryStats.portfolioCount > 0,
                    ...(TELEGRAM_UI_ENABLED
                      ? [Boolean(sectorSurgeStatus?.active), Boolean(subscriptionRedux?.telegramLinked)]
                      : []),
                  ].filter(Boolean).length}/${TELEGRAM_UI_ENABLED ? 3 : 1} 완료`}
                  sx={{ bgcolor: '#eef6ff', border: '1px solid #c8def2', color: BLUE, fontWeight: 950 }}
                />
              </Box>

              <Box sx={{ mt: 1.15, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
                {overviewCards.map((card) => <MetricCard key={card.title} title={card.title} value={card.value} desc={card.desc} actionLabel={card.actionLabel} onClick={card.onClick} tone={card.tone} />)}
              </Box>
            </Paper>

          </>
        )}

        {tab === 'portfolio' && (
          <Box sx={{ mt: 1.2 }}>
            <PortfolioAutomationPanel
              holdings={portfolioHoldings}
              riskMode={portfolioRiskMode}
              effectiveRiskMode={portfolioEffectiveRiskMode}
              autoManageEnabled={portfolioAutoManageEnabled}
              strategyPreview={portfolioStrategyPreview}
              action={portfolioAction}
              deletingTicker={portfolioDeletingTicker}
              isMobile={isMobile}
              onChangeRiskMode={handleChangePortfolioRiskMode}
              onToggleAutoManage={handleTogglePortfolioAutoManage}
              onAddHolding={handleAddPortfolioHolding}
              onChangeHolding={handleChangePortfolioHolding}
              onDeleteHolding={handleDeletePortfolioHolding}
              onSavePortfolio={handleSavePortfolio}
              onRefreshStrategy={handleRefreshPortfolioStrategy}
              onApplyRecommendedMode={handleApplyRecommendedPortfolioMode}
              onOpenRealtime={() => smartNavigate(`${routeIsMobile ? '/m' : ''}/realtime/stocks`)}
              onOpenMarketSettings={() => handleChangeTab('alerts')}
            />
          </Box>
        )}

        {tab === 'alerts' && (
          <Box sx={{ mt: 1.2, display: 'grid', gap: 1.1 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.05, md: 1.2 },
                borderRadius: 3,
                background: '#fff',
                border: BORDER,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' },
                  gap: 0.7,
                }}
              >
                {[
                  ['종목 변화 대상', `${summaryStats.portfolioCount}종목`],
                  ['시장 모니터', sectorSurgeStatus?.active ? '켜짐' : '꺼짐'],
                  ['수신 채널', telegramConnectionLinked ? 'Telegram 연결' : '연결 필요'],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ p: 0.9, borderRadius: 2, bgcolor: CARD, border: BORDER }}>
                    <Typography sx={{ color: SUB2, fontSize: 10.4, fontWeight: 850 }}>{label}</Typography>
                    <Typography sx={{ mt: 0.25, color: TXT, fontSize: 13, fontWeight: 950 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 1, md: 1.1 },
                borderRadius: 2.4,
                bgcolor: '#f5f9fd',
                border: '1px solid #dce8f2',
              }}
            >
              <Typography sx={{ color: BLUE, fontSize: 11.4, fontWeight: 950 }}>
                패널은 즉시 실행 · 마이페이지는 상세 관리
              </Typography>
              <Typography sx={{ mt: 0.25, color: SUB, fontSize: 10.8, lineHeight: 1.55 }}>
                종목별 변화 추적 ON/OFF는 패널에서 바로 처리합니다. 이 화면에서는 상승·하락 기준값과 Web Push·Telegram 수신 채널을 세부 조정합니다.
              </Typography>
            </Paper>

            <PortfolioThresholdAlertPanel authenticated={hasRequestAuthentication} />

            {TELEGRAM_UI_ENABLED ? (
              <MarketMonitoringSettingsPanel
                status={sectorSurgeStatus}
                preview={sectorSurgePreview}
                loading={sectorSurgeLoading}
                action={sectorSurgeAction}
                isMobile={isMobile}
                onRefresh={loadSectorSurgeStatus}
                onSubscribe={handleSubscribeSectorSurge}
                onUnsubscribe={handleUnsubscribeSectorSurge}
                onPreview={handlePreviewSectorSurge}
                onOpenRealtime={() => smartNavigate(`${routeIsMobile ? '/m' : ''}/realtime/stocks`)}
              />
            ) : null}
          </Box>
        )}

        {BILLING_UI_ENABLED && tab === 'account' && (
          <Box sx={{ mt: 1.2, display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr .9fr' }, gap: 1.1 }}>
            <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
              <SectionTitle title="현재 구독" sub="사용 중인 플랜과 다음 결제 상태를 확인합니다." isMobile={isMobile} />
              <Stack spacing={1}>
                <Paper elevation={0} sx={{ p: 1.1, borderRadius: 2.2, background: CARD, border: BORDER }}>
                  <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 900 }}>현재 플랜</Typography>
                  <Typography sx={{ color: TXT, fontSize: 18, fontWeight: 950, mt: 0.35 }}>{isPro ? '프로 플랜' : '무료 플랜'}</Typography>
                  <Typography sx={{ color: SUB, fontSize: 12, mt: 0.55, lineHeight: 1.6 }}>{subscriptionDescription}</Typography>
                </Paper>
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                  <Button variant="contained" disabled={!canStartPayment} onClick={() => { if (isPro && isCancelReserved) { void handleReactivateSubscription(); return; } setPayOpen(true); }} sx={{ borderRadius: 2, minHeight: 40, textTransform: 'none', fontWeight: 900, bgcolor: BLUE, color: '#fff', '&:hover': { bgcolor: BLUE }, '&.Mui-disabled': { bgcolor: 'rgba(148,163,184,.25)', color: '#94a3b8' } }}>{!isPro ? '결제 진행' : isCancelReserved ? '해지 예약 취소' : '결제 진행'}</Button>
                  {isPro ? <Button variant="outlined" color="warning" disabled={!canCancelSubscription} onClick={handleManageSubscription} sx={{ borderRadius: 2, minHeight: 40, textTransform: 'none', fontWeight: 900 }}>해지 예약</Button> : null}
                </Box>
                <BillingCheckoutPanel isPro={isPro && !isCancelReserved} onCheckout={() => { if (isPro && isCancelReserved) { void handleReactivateSubscription(); return; } if (isPro && !isCancelReserved) return; setPayOpen(true); }} />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
              <SectionTitle title="결제 내역" sub="최근 결제 기록을 확인합니다." isMobile={isMobile} />
              {!paymentRows.length ? <Typography sx={{ color: SUB, fontSize: 12.5 }}>결제 내역이 없습니다.</Typography> : <Box sx={{ display: 'grid', gap: 0.7 }}>{paymentRows.map((row) => <Paper key={row.id} elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}><Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr) auto auto', sm: '1.2fr 1fr auto' }, gap: 1, alignItems: 'center' }}><Box sx={{ minWidth: 0 }}><Typography sx={{ color: TXT, fontWeight: 900, fontSize: 12.5 }} noWrap>{row.product}</Typography><Typography sx={{ color: SUB2, fontSize: 11 }}>{formatDateTime(row.date)}</Typography></Box><Typography sx={{ color: SUB, fontSize: 12, fontWeight: 900 }}>{row.amount}</Typography><Chip size="small" label={row.status} sx={{ height: 22, bgcolor: '#ddf6df', border: '1px solid #b9e8bf', color: TXT, fontWeight: 900 }} /></Box></Paper>)}</Box>}
            </Paper>
          </Box>
        )}

        {tab === 'account' && (
          <Box sx={{ mt: 1.2, display: 'grid', gap: 1.1 }}>
            <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
              <SectionTitle
                title="계정 기본 정보"
                sub="로그인 정보와 현재 플랜을 확인하고, 세부 계정 편집은 팝업에서 진행합니다."
                isMobile={isMobile}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4,minmax(0,1fr))' }, gap: 0.8 }}>
                <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
                  <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 850 }}>로그인 이메일</Typography>
                  <Typography sx={{ color: TXT, fontSize: 12.8, fontWeight: 900, mt: 0.35, wordBreak: 'break-all' }}>
                    {displayAccountEmail || '-'}
                  </Typography>
                  <Typography sx={{ color: SUB, fontSize: 10.8, mt: 0.4, lineHeight: 1.45 }}>
                    계정 식별과 로그인 상태 확인에 사용됩니다.
                  </Typography>
                </Paper>

                <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
                  <Typography sx={{ color: SUB2, fontSize: 11, fontWeight: 850 }}>현재 플랜</Typography>
                  <Typography sx={{ color: TXT, fontSize: 12.8, fontWeight: 900, mt: 0.35 }}>
                    {!isPro ? '무료 플랜' : isCancelReserved ? '프로 · 해지 예약' : '프로 플랜'}
                  </Typography>
                  <Typography sx={{ color: SUB, fontSize: 10.8, mt: 0.4, lineHeight: 1.45 }}>
                    현재 이용 가능한 플랜 상태입니다. 구독/결제 기능은 준비가 완료되면 다시 안내합니다.
                  </Typography>
                </Paper>

              </Box>
            </Paper>

            {TELEGRAM_UI_ENABLED ? (
              <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
                <SectionTitle
                  title="알림 수신 채널"
                  sub="Telegram 계정을 한 번 연결하고, 장전 브리핑과 실시간 상태 알림 수신 여부를 설정합니다."
                  isMobile={isMobile}
                  right={
                    <Chip
                      size="small"
                      label={
                        telegramConnectionLinked
                          ? telegramConnection?.morningBriefActive || telegramConnection?.realtimeAlertsActive
                            ? '연결됨 · 알림 사용 중'
                            : '연결됨 · 선택 알림 꺼짐'
                          : telegramConnection?.pending
                            ? 'Start 대기 중'
                            : '연결 필요'
                      }
                      sx={{
                        height: 24,
                        bgcolor: telegramConnectionLinked ? SOFT_GOOD : SOFT_WARN,
                        color: telegramConnectionLinked ? GOOD : AMBER,
                        border: BORDER,
                        fontWeight: 950,
                      }}
                    />
                  }
                />

                <Box sx={{ p: 1.05, borderRadius: 2.2, bgcolor: '#f8fbfd', border: BORDER }}>
                  <Typography sx={{ color: TXT, fontSize: 12, fontWeight: 950 }}>연결 방식</Typography>
                  <Typography sx={{ color: SUB, fontSize: 11.4, mt: 0.35, lineHeight: 1.65, wordBreak: 'keep-all' }}>
                    아래 버튼을 누르면 PolicyIntel이 10분짜리 1회용 연결 토큰을 발급하고 Telegram을 엽니다. Telegram에서 Start를 한 번 누르면 Chat ID가 자동으로 연결됩니다. 사용자가 Chat ID를 직접 찾거나 입력할 필요가 없습니다.
                  </Typography>
                  <Typography sx={{ color: SUB, fontSize: 11.2, mt: 0.55, lineHeight: 1.6, wordBreak: 'keep-all' }}>
                    장전 브리핑은 연결 시 기본 신청됩니다. Web Push와 동일한 실시간 상태 알림은 별도 선택이며, 종목 알림·Sector Surge·Close Bet도 각각 사용자가 켠 항목만 발송됩니다.
                  </Typography>
                </Box>

                {telegramConnectionLinked ? (
                  <Box sx={{ mt: 1, display: 'grid', gap: 0.9 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: SOFT_GOOD, border: BORDER }}>
                      <Typography sx={{ color: TXT, fontSize: 12.8, fontWeight: 950 }}>Telegram 수신 계정 연결 완료</Typography>
                      <Typography sx={{ color: SUB, fontSize: 11.3, mt: 0.3, lineHeight: 1.55 }}>
                        {telegramConnection?.endpointMasked ? `연결 계정: ${telegramConnection.endpointMasked}` : 'Telegram 연결됨'}
                        <br />장전 브리핑: {telegramConnection?.morningBriefActive ? '수신 중' : '꺼짐'}
                        <br />실시간 상태 알림: {telegramConnection?.realtimeAlertsActive ? '수신 중' : '꺼짐'}
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8}>
                      <Button
                        variant={telegramConnection?.morningBriefActive ? 'outlined' : 'contained'}
                        onClick={() => handleSetMorningBriefActive(!Boolean(telegramConnection?.morningBriefActive))}
                        disabled={telegramLinkAction != null}
                        sx={{
                          borderRadius: 2,
                          minHeight: 40,
                          px: 1.6,
                          textTransform: 'none',
                          fontWeight: 950,
                          ...(telegramConnection?.morningBriefActive
                            ? { color: TXT, borderColor: BORDER_STRONG }
                            : { bgcolor: BLUE, color: '#fff', '&:hover': { bgcolor: BLUE } }),
                        }}
                      >
                        {telegramLinkAction === 'brief'
                          ? '변경 중…'
                          : telegramConnection?.morningBriefActive
                            ? '장전 브리핑 받지 않기'
                            : '오전 8시 장전 브리핑 받기'}
                      </Button>

                      <Button
                        variant={telegramConnection?.realtimeAlertsActive ? 'outlined' : 'contained'}
                        onClick={() => handleSetTelegramRealtimeActive(!Boolean(telegramConnection?.realtimeAlertsActive))}
                        disabled={telegramLinkAction != null}
                        sx={{
                          borderRadius: 2,
                          minHeight: 40,
                          px: 1.6,
                          textTransform: 'none',
                          fontWeight: 950,
                          ...(telegramConnection?.realtimeAlertsActive
                            ? { color: TXT, borderColor: BORDER_STRONG }
                            : { bgcolor: BLUE, color: '#fff', '&:hover': { bgcolor: BLUE } }),
                        }}
                      >
                        {telegramLinkAction === 'realtime'
                          ? '변경 중…'
                          : telegramConnection?.realtimeAlertsActive
                            ? '실시간 상태 알림 받지 않기'
                            : '실시간 상태 알림 받기'}
                      </Button>

                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleDisconnectTelegram}
                        disabled={telegramLinkAction != null}
                        sx={{ borderRadius: 2, minHeight: 40, textTransform: 'none', fontWeight: 900, flexShrink: 0 }}
                      >
                        {telegramLinkAction === 'disconnect' ? '해제 중…' : 'Telegram 연결 해제'}
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1, display: 'grid', gap: 0.85 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 0.7 }}>
                      {[
                        ['1', '연결 버튼 클릭'],
                        ['2', 'Telegram 열기'],
                        ['3', 'Start 한 번'],
                      ].map(([step, label]) => (
                        <Box key={step} sx={{ p: 0.9, borderRadius: 2, border: BORDER, bgcolor: CARD }}>
                          <Typography sx={{ color: BLUE, fontSize: 10.5, fontWeight: 950 }}>STEP {step}</Typography>
                          <Typography sx={{ color: TXT, fontSize: 11.6, fontWeight: 900, mt: 0.2 }}>{label}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleConnectTelegram}
                      disabled={telegramLinkAction != null}
                      sx={{ borderRadius: 2, minHeight: 42, px: 1.8, textTransform: 'none', fontWeight: 950, bgcolor: BLUE, color: '#fff', '&:hover': { bgcolor: BLUE } }}
                    >
                      {telegramLinkAction === 'connect'
                        ? '연결 링크 발급 중…'
                        : telegramLinkAction === 'waiting'
                          ? 'Telegram Start 대기 중…'
                          : 'Telegram으로 장전 브리핑 받기'}
                    </Button>

                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        border: BORDER,
                        bgcolor: telegramLinkAction === 'waiting' || telegramConnection?.pending ? SOFT_WARN : '#fbfcfd',
                      }}
                    >
                      <Typography sx={{ color: TXT, fontSize: 11.7, fontWeight: 950 }}>
                        START BOT을 눌러도 반응이 없나요?
                      </Typography>
                      <Typography sx={{ color: SUB, fontSize: 10.9, mt: 0.35, lineHeight: 1.55, wordBreak: 'keep-all' }}>
                        브라우저가 Telegram 앱을 열 수 없는 경우입니다. Telegram Desktop을 설치·실행해 로그인한 뒤 다시 시도하고,
                        브라우저에 외부 앱 열기 확인창이 표시되면 Telegram 열기를 허용해 주세요.
                      </Typography>
                      <Typography sx={{ color: SUB, fontSize: 10.9, mt: 0.35, lineHeight: 1.55, wordBreak: 'keep-all' }}>
                        앱을 사용하지 않거나 계속 열리지 않으면 Telegram Web에서 @{telegramBotUsername}을 검색한 뒤
                        {telegramStartCommand ? ' 아래의 「연결 명령 복사」로 복사한 명령을 대화방에 한 번 전송하면 됩니다.' : ' 연결 버튼으로 1회용 코드를 발급한 뒤 연결 명령을 복사해 전송하면 됩니다.'}
                      </Typography>

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.65} sx={{ mt: 0.75 }}>
                        <Button
                          variant="outlined"
                          onClick={handleOpenTelegramDesktopHelp}
                          sx={{ borderRadius: 1.8, minHeight: 34, textTransform: 'none', fontWeight: 850, color: TXT, borderColor: BORDER_STRONG }}
                        >
                          Telegram Desktop 설치
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleOpenTelegramWeb}
                          sx={{ borderRadius: 1.8, minHeight: 34, textTransform: 'none', fontWeight: 850, color: TXT, borderColor: BORDER_STRONG }}
                        >
                          Telegram Web 열기
                        </Button>
                        {telegramStartCommand ? (
                          <Button
                            variant="outlined"
                            onClick={() => void handleCopyTelegramStartCommand()}
                            sx={{ borderRadius: 1.8, minHeight: 34, textTransform: 'none', fontWeight: 850, color: BLUE, borderColor: BORDER_STRONG }}
                          >
                            연결 명령 복사
                          </Button>
                        ) : null}
                      </Stack>

                      {telegramStartCommand ? (
                        <Typography sx={{ color: SUB2, fontSize: 10.2, mt: 0.55, lineHeight: 1.45 }}>
                          연결 명령에는 10분간 유효한 1회용 코드가 포함됩니다. 다른 사람에게 공유하지 마세요.
                        </Typography>
                      ) : null}
                    </Box>

                    {(telegramConnectUrl || telegramConnection?.pending) ? (
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.7}>
                        {telegramConnectUrl ? (
                          <Button
                            variant="outlined"
                            onClick={() => window.open(telegramConnectUrl, '_blank', 'noopener,noreferrer')}
                            sx={{ borderRadius: 2, minHeight: 38, textTransform: 'none', fontWeight: 900, color: TXT, borderColor: BORDER_STRONG }}
                          >
                            Telegram 다시 열기
                          </Button>
                        ) : null}
                        <Button
                          variant="outlined"
                          onClick={() => void loadTelegramConnectionStatus(false)}
                          sx={{ borderRadius: 2, minHeight: 38, textTransform: 'none', fontWeight: 900, color: TXT, borderColor: BORDER_STRONG }}
                        >
                          연결 상태 확인
                        </Button>
                      </Stack>
                    ) : null}

                    <Typography sx={{ color: SUB2, fontSize: 10.8, lineHeight: 1.55 }}>
                      연결 토큰은 10분 후 만료되고 한 번 사용되면 즉시 폐기됩니다. 연결이 완료된 뒤에는 매일 새로운 토큰을 발급하지 않습니다.
                    </Typography>
                  </Box>
                )}
              </Paper>
            ) : null}

            <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
              <SectionTitle
                title="연결된 소셜 계정"
                sub="연결 상태를 확인하고 필요한 경우 계정 편집에서 연결하거나 해제할 수 있습니다."
                isMobile={isMobile}
                right={
                  <Button
                    variant="outlined"
                    onClick={goSettings}
                    sx={{
                      borderRadius: 2,
                      minHeight: 36,
                      px: 1.25,
                      color: TXT,
                      borderColor: BORDER_STRONG,
                      fontWeight: 900,
                      textTransform: 'none',
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    연결 관리
                  </Button>
                }
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,minmax(0,1fr))' }, gap: 0.8 }}>
                <SocialProviderStatusCard label="NAVER" connected={connectedProviders.naver} accent="#03c75a" />
                <SocialProviderStatusCard label="Google" connected={connectedProviders.google} accent="#2563eb" />
                <SocialProviderStatusCard label="Kakao" connected={connectedProviders.kakao} accent="#8a6500" />
              </Box>

              <Box sx={{ mt: 0.9, p: 1, borderRadius: 2, bgcolor: '#f8fbfd', border: BORDER }}>
                <Typography sx={{ color: TXT, fontSize: 11.8, fontWeight: 900 }}>소셜 로그인 안내</Typography>
                <Typography sx={{ color: SUB, fontSize: 11.2, mt: 0.35, lineHeight: 1.6, wordBreak: 'keep-all' }}>
                  소셜 로그인은 회원 식별과 로그인 인증을 위해 사용됩니다. 소셜 계정으로 가입한 경우 PolicyIntel용 비밀번호를 새로 만들지 않아도 되며, 연결 상태는 언제든 계정 편집에서 확인할 수 있습니다.
                </Typography>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
              <SectionTitle
                title="개인정보 및 수신 설정"
                sub="로그인에 필요한 정보와 선택 수신 설정을 구분해 관리합니다."
                isMobile={isMobile}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.8 }}>
                <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
                  <Typography sx={{ color: SUB2, fontSize: 10.8, fontWeight: 900 }}>계정 정보</Typography>
                  <Typography sx={{ color: TXT, fontSize: 12.3, fontWeight: 900, mt: 0.35 }}>이메일</Typography>
                  <Typography sx={{ color: SUB, fontSize: 11.2, mt: 0.35, lineHeight: 1.55 }}>
                    회원 식별, 로그인 인증, 계정 관리와 중요한 서비스 안내를 위해 사용합니다.
                  </Typography>
                </Paper>

                <Paper elevation={0} sx={{ p: 1, borderRadius: 2, background: CARD, border: BORDER }}>
                  <Typography sx={{ color: SUB2, fontSize: 10.8, fontWeight: 900 }}>선택 수신</Typography>
                  <Typography sx={{ color: TXT, fontSize: 12.3, fontWeight: 900, mt: 0.35 }}>마케팅 알림</Typography>
                  <Typography sx={{ color: SUB, fontSize: 11.2, mt: 0.35, lineHeight: 1.55 }}>
                    이메일·SMS 마케팅 수신 동의는 선택 사항이며 계정 편집에서 별도로 변경할 수 있습니다.
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mt: 0.9, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SettingsRoundedIcon />}
                  onClick={goSettings}
                  sx={{
                    minHeight: 38,
                    px: 1.4,
                    borderRadius: 2,
                    bgcolor: BLUE,
                    color: '#fff',
                    fontWeight: 900,
                    textTransform: 'none',
                    boxShadow: 'none',
                    width: isMobile ? '100%' : 'auto',
                    '&:hover': { bgcolor: '#083e5a', boxShadow: 'none' },
                  }}
                >
                  계정 편집 열기
                </Button>
              </Box>
            </Paper>
          </Box>
        )}

        {tab === 'support' && (
          <SupportReportPanel
            isMobile={isMobile}
            isAdmin={isAdmin}
            holdings={portfolioHoldings}
            addToast={addToast}
          />
        )}

        {tab === 'adminUsers' && (
          <Paper elevation={0} sx={{ mt: 1.2, p: { xs: 1.1, md: 1.35 }, borderRadius: 3, background: PANEL, border: BORDER }}>
            <SectionTitle
              title="운영자 도구"
              sub="ADMIN을 제외한 실제 사용자 인입·성장·행동과 기존 사용자 관리를 한 화면에서 확인합니다."
              isMobile={isMobile}
            />
            <AdminAnalyticsPanel />
            <AdminProductValidationPanel />
            <Box sx={{ mt: 1.2 }}>
              <SectionTitle title="사용자 관리" sub="계정 상태와 사용자 정보를 조회합니다." isMobile={isMobile} />
              <AdminUsersGrid />
            </Box>
          </Paper>
        )}
            </Box>
          </Box>
        </Box>
      </Container>

      {BILLING_UI_ENABLED ? (
        <BillingPayDialog open={payOpen} onClose={() => setPayOpen(false)} userId={resolvedUserId} />
      ) : null}

      <div className="toast-root">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`} role="status" aria-live="polite">
            <div className="title">{t.title}</div>
            {t.msg ? <div className="msg">{t.msg}</div> : null}
          </div>
        ))}
      </div>

      <AccountSettingsModal
        open={accountOpen}
        onClose={closeAccountSettings}
        userEmail={(profile?.email ?? userEmail ?? me?.email) || ''}
        userPhone={(profile as any)?.phone ?? (me as any)?.phone ?? ''}
        marketingEmailAgree={Boolean((profile as any)?.marketingEmailAgree ?? (me as any)?.marketingEmailAgree)}
        marketingSmsAgree={Boolean((profile as any)?.marketingSmsAgree ?? (me as any)?.marketingSmsAgree)}
        connectedProviders={connectedProviders}
        loading={savingAccount}
        error={errorMessage}
        successMessage={successMessage}
        onSave={saveAccountSettings}
        onOpenPasswordChange={openPasswordEdit}
        onWithdraw={openWithdrawConfirm}
        onOpenEmailChange={openEmailChange}
        onConnectSocial={connectSocialAccount}
        onDisconnectSocial={disconnectSocialAccount}
        socialLoadingProvider={socialLoadingProvider}
      />

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onLogin={handleLogin} onSignup={handleSignup} />

      <AuthModal open={passwordEditOpen} onClose={closePasswordEdit} mode="password-edit" userEmail={(profile?.email ?? userEmail ?? me?.email) || ''} loading={changingPassword} onPasswordUpdate={changePassword} />

      <WithdrawConfirmDialog open={withdrawOpen} onClose={closeWithdrawConfirm} loading={withdrawing} userEmail={(profile?.email ?? userEmail ?? me?.email) || ''} onConfirm={confirmWithdraw} />

      <EmailChangeDialog open={emailChangeOpen} onClose={closeEmailChange} currentEmail={(profile?.email ?? userEmail ?? me?.email) || ''} loading={changingEmail} successMessage={successMessage} errorMessage={errorMessage} onSubmit={changeEmail} />
    </Box>
  );
};

export default MyPage;
